import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core';
import { z } from 'zod';
import * as coercions from '@/common/utils/coercions';

export type OneTimePassword = Partial<{
    id: Id;
    receiver: string;
    expires_at: DateTime;
    refreshable_at: DateTime;
    created_at: DateTime;
    updated_at: DateTime;
    attempts: number;
    max_attempts: number;
}>;
export type Id = number;
export type DateTime = coercions.date;
export type Morphable = {
    id: Id;
    object: Morphs;
};
export type Morphs =
    | 'user'
    | 'student'
    | 'agency'
    | 'campus'
    | 'company'
    | 'company_branch'
    | 'attended_institution'
    | 'language_test'
    | 'program'
    | 'document_requirement'
    | 'application'
    | 'agreement'
    | 'media_storage'
    | 'payment_attempt'
    | 'payment_item'
    | 'attempt_record'
    | 'association_request'
    | 'student_resume'
    | 'settings'
    | 'package'
    | 'offer_letter'
    | 'signature'
    | 'task'
    | 'comment'
    | 'service_template'
    | 'service'
    | 'smart_event'
    | 'family_member'
    | 'advanced_posts'
    | 'company_payout_batch'
    | 'company_payout'
    | 'service_step'
    | 'packaged_program'
    | 'payment_plan'
    | 'applied_program'
    | 'manual_payment_log';
export type JwtClaims = {
    sub: number;
    act: AccountType;
    name: string;
    iat: number;
    exp: number;
    nbf?: number | undefined;
    scopes: Array<'onboarded'>;
};
export type AccountType = 'student' | 'staff' | 'school' | 'agency';
export type OtpStatus = {
    receiver: string;
    expires_at: DateTime;
    refreshable_at: DateTime;
    created_at: DateTime;
    updated_at: DateTime;
    attempts: number;
    max_attempts?: number | undefined;
};
export type Language = {
    alpha3b: string;
    alpha2: LanguageCodeIso2;
    name: string;
};
export type LanguageCodeIso2 = string;
export type Currency = {
    code: CurrencyCode;
    name: string;
    minor_unit: number;
    payments_enabled: boolean;
};
export type CurrencyCode = string;
export type City = {
    name: string;
    country_code: CountryCodeIso2;
    country_name: string;
    principal_subdivision_code: PrincipalSubdivisionCode;
    principal_subdivision_name: string;
    lat: NullableDecimal;
    lng: NullableDecimal;
};
export type CountryCodeIso2 = string;
export type PrincipalSubdivisionCode = string;
export type NullableDecimal = coercions.nullableDecimal;
export type PrincipalSubdivision = {
    subdivision_code: PrincipalSubdivisionCode;
    name: string;
    country_code: CountryCodeIso2;
    country_name: string;
    lat: NullableDecimal;
    lng: NullableDecimal;
};
export type Country = {
    name: string;
    iso3?: CountryCodeIso3 | undefined;
    iso2: CountryCodeIso2;
    phone_code: string | null;
    emoji: string | null;
    currency_code: CurrencyCode | null;
    currency_symbol: string | null;
    currency_name: string | null;
    principal_subdivisions?: Array<PrincipalSubdivision> | undefined;
};
export type CountryCodeIso3 = string;
export type PostalCode = {
    id: Id;
    postcode: string;
    name: string;
    lat: NullableDecimal;
    lng: NullableDecimal;
    principal_subdivision_code: PrincipalSubdivisionCode;
    country_code: CountryCodeIso2;
};
export type BasicAddress = {
    street_address: string;
    city: string;
    state: string;
    postal_code: string;
    country: CountryCodeIso2;
};
export type UserPublic = {
    id: Id;
    company_id: Id;
    company_branch_id: Id;
    status: AccountStatus;
    account_type: AccountType;
    company_reference_id?: (string | null) | undefined;
    is_online?: boolean | undefined;
    avatar_url: string | null;
    given_name: string;
    family_name: string;
    additional_name?: (string | null) | undefined;
    honorific_prefix?: (string | null) | undefined;
    honorific_suffix?: (string | null) | undefined;
    full_name: string;
    nickname?: (string | null) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
};
export type AccountStatus = 'onboarding' | 'rejected' | 'active' | 'suspended';
export type Notification = {
    id: string;
    deletable: boolean;
    tag: string | null;
    priority: number;
    created_at: DateTime;
    read_at?: NullableDateTime | undefined;
    updated_at?: DateTime | undefined;
    notifiable_id: Id;
    notifiable_type: Morphs;
    data: {
        title: string;
        message: string;
        code?: (string | null) | undefined;
        application_id?: NullableId | undefined;
        agency_id?: NullableId | undefined;
        campus_id?: NullableId | undefined;
        student_id?: NullableId | undefined;
        user_id?: NullableId | undefined;
        program_id?: NullableId | undefined;
        package_id?: NullableId | undefined;
        association_request_id?: NullableId | undefined;
        agreement_id?: NullableId | undefined;
        agreement_sender_company_id?: NullableId | undefined;
        agreement_receiver_company_id?: NullableId | undefined;
    };
};
export type NullableDateTime = coercions.nullableDate;
export type NullableId = number | null;
export type User = UserPublic & {
    media_storage_id?: NullableId | undefined;
    gender?: (Gender | null) | undefined;
    password_changed_at?: NullableDateTime | undefined;
    parent_user_id?: NullableId | undefined;
    mara_number?: (string | null) | undefined;
    assists_user_id?: NullableId | undefined;
    finished_onboarding?: boolean | undefined;
    date_of_birth?: NullableDate | undefined;
    email: string;
    email_verified_at?: NullableDateTime | undefined;
    phone_primary?: (string | null) | undefined;
    phone_primary_verified_at?: NullableDateTime | undefined;
    phone_secondary?: (string | null) | undefined;
    phone_secondary_verified_at?: NullableDateTime | undefined;
    phone_alternate?: (string | null) | undefined;
    phone_alternate_name?: (string | null) | undefined;
    addresses?: Array<Address> | undefined;
    media?: Array<Media> | undefined;
    applications_count?: number | undefined;
    role?: Role | undefined;
    media_storage?: MediaStorage | undefined;
};
export type Gender = 'male' | 'female' | 'other';
export type NullableDate = coercions.nullableDate;
export type Address = AddressBody & {
    id: Id;
    formatted_address?: string | undefined;
    addressable_id: Id;
    addressable_type: Morphs;
};
export type AddressBody = {
    remarks?: (string | null) | undefined;
    address_type?: (null | ('residence' | 'work' | 'business' | 'postal_box' | 'other')) | undefined;
    address_purpose?: (null | ('permanent' | 'postal' | 'billing' | 'shipping' | 'invoicing' | 'other')) | undefined;
    default?: boolean | undefined;
    line_one: string;
    line_two?: (string | null) | undefined;
    line_three?: (string | null) | undefined;
    city: string;
    principal_subdivision: string;
    postal_code: string;
    country: CountryCodeIso2;
    lat?: (string | number | null) | undefined;
    lng?: (string | number | null) | undefined;
    gmaps_place_id?: (string | null) | undefined;
    media?: Array<Media> | undefined;
};
export type MediaCustomProperties = Partial<{
    document_type_id: number | null;
    description: string;
}> | null;
export type Role = {
    id: Id;
    name: string;
    label: string;
    rank: number;
    deletable: boolean;
    modifiable: boolean;
    permissions: Array<Permission>;
};
export type Permission = {
    id: Id;
    name: string;
    code: string | null;
    description: string | null;
    category: string;
    company_types: Array<AccountType>;
};
export type MediaStorage = {
    id: Id;
    parent_media_storage_id: NullableId;
    storage_type: MediaStorageType;
    owner_id: Id;
    owner_type: Morphs;
    created_at: DateTime;
    updated_at: DateTime;
    deleted_at: NullableDateTime;
    media?: Array<Media> | undefined;
    media_collections?: Array<MediaCollection> | undefined;
};
export type MediaStorageType = 'student_documents' | 'application_documents' | 'settings';
export type MediaCollection = {
    available: boolean;
    display_name: string | null;
    collection_name: string;
    description: string | null;
    min_media_count: number;
    max_media_count: number | null;
    keeps_latest: boolean;
    allowed_mime_types: null | Array<string>;
    max_media_size_bytes: number | null;
    max_collection_size_bytes: number | null;
    can_delete_media: boolean;
    dimensions: Partial<{
        width: number | null;
        height: number | null;
        min_height: number | null;
        min_width: number | null;
        max_height: number | null;
        max_width: number | null;
    }> | null;
};
export type TusMediaConversion = {
    tus_file_id: string;
    name?: (string | null) | undefined;
    custom_properties?: (null | MediaCustomProperties) | undefined;
    media_collection?: (string | null) | undefined;
};
export type MediaLink = {
    id: Id;
    linkable_id: Id;
    linkable_type: Morphs;
    media_id: Id;
    is_locked?: boolean | undefined;
    lock_reason?: (string | null) | undefined;
    unlinked_at?: NullableDateTime | undefined;
    media?: Media | undefined;
    created_at: DateTime;
    updated_at: DateTime;
};
export type Media = {
    id: Id;
    model_id?: Id | undefined;
    model_type?: Morphs | undefined;
    collection_name: string;
    name: string;
    mime_type?: (string | null) | undefined;
    original_url: string;
    preview_url?: (string | null) | undefined;
    size: number | null;
    custom_properties?: MediaCustomProperties | undefined;
    order?: (number | null) | undefined;
    media_links?: Array<MediaLink> | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    uploaded_by?:
        | (null | Partial<{
              id: Id;
              first_name: string;
              last_name: string;
          }>)
        | undefined;
};
export type OnboardingProgress = {
    conversation_id: NullableId;
    address: Address | null;
    company: Company;
    user: User;
    agreements: Array<Agreement>;
    dependent: (null | Agency | Campus) | Array<null | Agency | Campus>;
};
export type CompanyMetadata = Partial<{
    references: null | Array<WorkReference>;
}>;
export type WorkReference = {
    provider: string;
    name: string;
    email: string;
    phone: string;
};
export type CompanyBranchStatus = 'active' | 'hidden' | 'disabled';
export type EmailCategoryConfig = {
    accounts: string | null;
    platform: string | null;
    misc: string | null;
    admissions: string | null;
};
export type ExternalPresence = Partial<{
    website_url: string | null;
    contact_phone: string | null;
    contact_email: string | null;
    whatsapp_business_id: string | null;
    zoom_id: string | null;
    facebook_page_name: string | null;
    instagram_handle: string | null;
    twitter_handle: string | null;
    linkedin_url: string | null;
    youtube_channel_name: string | null;
}>;
export type AgencyFactoids = Partial<{
    students_recruited: number | null;
    primary_country: string | null;
}>;
export type CampusFactoids = Partial<{
    disciplines: string | null;
    features: null | Array<
        | 'post_graduation_work_permit'
        | 'work_while_studying'
        | 'conditional_offer_letter'
        | 'no_co_op_or_internship_participation'
        | 'accommodations'
    >;
    education_levels: null | Array<
        | 'grade 11'
        | 'grade 12'
        | '3_year_bachelors_degree'
        | '4_year_bachelors_degree'
        | 'postgraduate_certificate_/_diploma'
        | 'masters_degree'
        | 'doctoral_degree_(phd)'
        | 'micro-credentials'
    >;
    founded: number | null;
    total_students: number | null;
    international_students: number | null;
}>;
export type PartnerPublic = {
    id: Id;
    type: AccountType;
    name: string;
    address: string | null;
    logo_url: string | null;
    website_url: string | null;
};
export type SignupReferral = {
    id: Id;
    company_branch_id: Id;
    user_id: NullableId;
    enabled: boolean;
    referral_url?: string | undefined;
    download_url?: string | undefined;
    branch_name?: string | undefined;
    platform?: (string | null) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
};
export type Contact = {
    name: string;
    email: string;
    phone: string | null;
};
export type Agreement = {
    id: Id;
    name?: string | undefined;
    can_be_rejected: boolean;
    agreement_type: AgreementType;
    valid_upto: NullableDate;
    sender_terminated?: boolean | undefined;
    receiver_terminated?: boolean | undefined;
    sender_company_id: Id;
    receiver_company_id: Id;
    contents: string | null;
    commission_percentage: Decimal;
    status: AgreementStatus;
    status_changed_at?: NullableDateTime | undefined;
    meta?:
        | (Partial<{
              pdf_error: string | null;
          }> | null)
        | undefined;
    offer_days?: (number | null) | undefined;
    offer_percentage?: (string | number | null) | undefined;
    offer_name?: (string | null) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    sender_company?: Company | undefined;
    receiver_company?: Company | undefined;
    media?: Array<Media> | undefined;
    signatures?: Array<Signature> | undefined;
    program_commissions?: Array<ProgramCommission> | undefined;
    agreement_modifications?: Array<AgreementModification> | undefined;
};
export type AgreementType = 'commission' | 'site_usage' | 'service_advice' | 'service_visa' | 'service_appeal';
export type Decimal = z.input<typeof Decimal>;
export type AgreementStatus = 'created' | 'signed' | 'terminated' | 'archived';
export type Signature = {
    id: Id;
    company_id: Id;
    company_branch_id?: NullableId | undefined;
    signable_id: Id;
    signable_type: Morphs;
    company_name?: (string | null) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    signed_at?: NullableDateTime | undefined;
    signee_name?: (string | null) | undefined;
    signee_ip?: (string | null) | undefined;
    signee_email?: (string | null) | undefined;
    signee_id?: NullableId | undefined;
    meta?:
        | (Partial<{
              signee_agree_texts: Array<string>;
          }> | null)
        | undefined;
    terms?: (null | Array<string>) | undefined;
    media?: Array<Media> | undefined;
};
export type ProgramCommission = {
    id: Id;
    program_id: Id;
    agreement_id: Id;
    commission_percentage: Decimal;
    created_at: DateTime;
    updated_at: DateTime;
    program?: Program | undefined;
};
export type FeesSchedule = {
    duration_weeks: number;
    schedule_type: FeesScheduleType;
    regular_interval_width: number | null;
    regular_interval_unit: null | RegularIntervalUnit;
    number_of_payments: number | null;
};
export type FeesScheduleType = 'regular_intervals' | 'fixed_number_of_payments' | 'interval_with_number_of_payments';
export type RegularIntervalUnit = 'days' | 'weeks' | 'months' | 'years';
export type FeesStructure = {
    application_fees: Decimal;
    material_fees: Decimal;
    tuition_fees: Decimal;
    commission_bonus: Decimal;
    discount: Decimal;
    advance_fees: Decimal;
    fees_currency: CurrencyCode;
};
export type Month = number;
export type MethodOfDelivery =
    | 'face_to_face'
    | 'instructor_led'
    | 'virtual_classroom'
    | 'self_paced_online'
    | 'blended_learning';
export type ProgramLevel = {
    id: Id;
    title: string;
    enabled: boolean;
    created_at: DateTime;
    updated_at: DateTime;
};
export type AgreementModification = {
    id: Id;
    created_at: DateTime;
    updated_at: DateTime;
    agreement_id: Id;
    reason: string;
    start: DateTime;
    end: DateTime;
    commission_percentage: Decimal;
    invalidation_reason?: (string | null) | undefined;
    invalidated?: boolean | undefined;
};
export type SocialComment = SocialReactable & {
    id: Id;
    social_post_id?: Id | undefined;
    social_user_id?: Id | undefined;
    content?: string | undefined;
    created_at: DateTime;
    updated_at: DateTime;
};
export type SocialReactable = {
    reactions: {};
    my_reaction?: (string | null) | undefined;
};
export type ConversationContact = {
    user_id: Id;
    name: string;
    branch: MinimalCompanyBranch | null;
    relevance: number | null;
    avatar_url: string;
};
export type MinimalCompanyBranch = {
    id: Id;
    company_id: Id;
    company_type: AccountType;
    parent_branch_id: NullableId;
    status: CompanyBranchStatus;
    name: string;
    display_name?: string | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    company: MinimalCompany | null;
};
export type MinimalCompany = {
    id: Id;
    display_name: string;
    reference_id: string | null;
    company_type: AccountType;
    created_at: DateTime;
    updated_at: DateTime;
};
export type ConversationParticipant = {
    user_id: Id;
    name: string | null;
    company_id: Id;
    company_name?: string | undefined;
    company_branch_id: Id;
    company_type: AccountType;
    is_admin?: boolean | undefined;
    avatar_url: string;
    conversation_id: Id;
    created_at: DateTime;
    updated_at: DateTime;
    deleted_at?: NullableDateTime | undefined;
    notifications_enabled?: boolean | undefined;
    read_upto_id: NullableId;
    received_upto_id: NullableId;
    is_active?: boolean | undefined;
    removed_at?: NullableDateTime | undefined;
};
export type ConversationAttachment = {
    id: number;
    conversation_message_id: number;
    display_name: string;
    content_type: string;
    size_bytes?: number | undefined;
    download_url?: (string | null) | undefined;
    placeholder_hash: string | null;
    created_at: DateTime;
    updated_at: DateTime;
};
export type ConversationMessage = {
    id: number;
    conversation_id: number;
    user_id: number | null;
    system: boolean;
    message_body: string | null;
    message_special?:
        | (Partial<{
              document_requirement_id: number | null;
              document_requirement_instructions: string | null;
              event_code: string | null;
          }> | null)
        | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    deleted_at?: NullableDateTime | undefined;
    attachments?: Array<ConversationAttachment> | undefined;
    user?: (null | UserPublic) | undefined;
    conversation?: (null | Conversation) | undefined;
};
export type ConversationType = 'support' | 'partner' | 'application' | 'service' | 'inter_company' | 'intra_company';
export type ConversationMeta = Partial<{
    private: boolean;
    student_id: NullableId;
    application_id: NullableId;
}>;
export type MinimalStudent = {
    id: Id;
    user_id: Id;
    reference_id: string | null;
    company_id: Id;
    agency_branch_id: Id;
    full_name?: (string | null) | undefined;
    full_reference: string;
    created_at: DateTime;
    updated_at: DateTime;
    avatar_url?: (string | null) | undefined;
    agency_branch?: MinimalCompanyBranch | undefined;
};
export type MinimalApplication = {
    id: Id;
    reference_id?: (string | null) | undefined;
    updated_at: DateTime;
    created_at: DateTime;
    student_id: Id;
    media_storage_id: NullableId;
    application_type?: ('course' | 'visa') | undefined;
    applicable_id?: NullableId | undefined;
    applicable_type?: (string | null) | undefined;
    visa_type?: (('subclass_500' | 'subclass_485' | 'subclass_482' | null) | null) | undefined;
    target_date?: (string | null) | undefined;
    school_company_id?: NullableId | undefined;
    agency_company_id: Id;
    staff_company_id: Id;
    school_branch_id?: NullableId | undefined;
    agency_branch_id: Id;
    agency_conversation_id: NullableId;
    student_conversation_id: NullableId;
    agency_staff_agreement_id?: NullableId | undefined;
    school_staff_agreement_id?: NullableId | undefined;
    agency_school_agreement_id?: NullableId | undefined;
    intake_year?: (number | null) | undefined;
    intake_month?: (number | null) | undefined;
    status: string;
    agency_branch?: (MinimalCompanyBranch | null) | undefined;
    school_branch?: (MinimalCompanyBranch | null) | undefined;
    student?: (MinimalStudent | null) | undefined;
};
export type ConversationTopics = Partial<{
    student_id: NullableId;
    student_name: string | null;
    student_reference: string | null;
    application_id: NullableId;
    application_reference: string | null;
    application_name: string | null;
}>;
export type Conversation = {
    id: Id;
    media_storage_id: NullableId;
    title: string;
    conversation_type: ConversationType;
    thumbnail_url?: (string | null) | undefined;
    meta?: (null | ConversationMeta) | undefined;
    closed_at?: NullableDateTime | undefined;
    deleted_at?: NullableDateTime | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    participants?: Array<ConversationParticipant> | undefined;
    recent_messages?:
        | {
              has_more: boolean;
              messages: Array<ConversationMessage>;
          }
        | undefined;
    my_participant?: (null | ConversationParticipant) | undefined;
    latest_message_id?: NullableId | undefined;
    company_branches?: Array<MinimalCompanyBranch> | undefined;
    companies?: Array<MinimalCompany> | undefined;
    student?: (null | MinimalStudent) | undefined;
    application?: (null | MinimalApplication) | undefined;
};
export type BankDetail = {
    id: Id;
    account_name: string;
    account_number: string;
    account_bsb: string;
    payment_plus_id?: (string | null) | undefined;
    verified_at?: NullableDateTime | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    deleted_at?: NullableDateTime | undefined;
};
export type Handler = {
    id: Id;
    user_id: Id;
    handleable_id: Id;
    handleable_type: Morphs;
    deleted_at?: (string | null) | undefined;
    data?: ({} | null) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    user?: UserPublic | undefined;
    company?: Company | undefined;
};
export type Company = {
    id: Id;
    reference_id?: (string | null) | undefined;
    company_type: AccountType;
    superadmin_id?: Id | undefined;
    meta?: CompanyMetadata | undefined;
    display_name: string;
    is_onboarded?: boolean | undefined;
    assessment_level?: (number | null) | undefined;
    business_identification: string | null;
    business_identification_verification_status?: ('unverified' | 'verified' | 'failed') | undefined;
    business_identification_verification_remarks?: (string | null) | undefined;
    business_name?: (string | null) | undefined;
    trading_name?: string | undefined;
    legal_name?: (string | null) | undefined;
    business_type_code?: (string | null) | undefined;
    business_active?: boolean | undefined;
    gst_registered_from?: NullableDateTime | undefined;
    gst_registered_to?: NullableDateTime | undefined;
    address_principal_subdivision?: (string | null) | undefined;
    address_postal_code?: (string | null) | undefined;
    company_logo_url?: (string | null) | undefined;
    cricos_number: string | null;
    rto_number: string | null;
    cricos_number_verified_at?: (string | null) | undefined;
    rto_number_verified_at?: (string | null) | undefined;
    school_type: ('college' | 'university') | null;
    stripe_account_id?: (string | null) | undefined;
    stripe_create_failed_reason?: (string | null) | undefined;
    stripe_details_submitted?: boolean | undefined;
    stripe_charges_enabled?: boolean | undefined;
    stripe_tos_accepted?: boolean | undefined;
    staff_referral_id?: (string | null) | undefined;
    accepting_association_requests?: boolean | undefined;
    bank_details_locked_since?: NullableDateTime | undefined;
    applications_count?: (number | null) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    company_branches?: Array<CompanyBranch> | undefined;
    campuses?: Array<Campus> | undefined;
    agencies?: Array<Agency> | undefined;
    addresses?: Array<Address> | undefined;
    media?: Array<Media> | undefined;
    media_collections?: Array<MediaCollection> | undefined;
    root_address?: (null | Address) | undefined;
    contact?: (null | Contact) | undefined;
    handlers?: Array<Handler> | undefined;
};
export type CompanyBranch = {
    id: Id;
    company_id: Id;
    company_type: AccountType;
    parent_branch_id: NullableId;
    status: CompanyBranchStatus;
    status_locked: boolean;
    name: string;
    email_category_config?: EmailCategoryConfig | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    is_authorized?: boolean | undefined;
    display_name?: (string | null) | undefined;
    company?: Company | undefined;
    addresses?: Array<Address> | undefined;
    managed_companies?: Array<Company> | undefined;
    parent_branch?: CompanyBranch | undefined;
    agency?: (null | Agency) | undefined;
    campus?: (null | Campus) | undefined;
};
export type CompanyUser = {
    user_id: Id;
    company_id: Id;
    company_branch_id: Id;
    parent_user_id: NullableId;
    designation: string | null;
    created_at: DateTime;
    updated_at: DateTime;
    permissions: string | null;
    deleted_at: NullableDateTime;
    user?: (User | UserPublic) | undefined;
    parent_user?: (User | UserPublic) | undefined;
};
export type TransactionTODO = {
    id: Id;
};
export type Student = {
    id: Id;
    object: Morphs;
    user_id: Id;
    reference_id?: (string | null) | undefined;
    company_id: Id;
    media_storage_id: Id;
    agency_branch_id: Id;
    full_name?: (string | null) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    avatar_url?: (string | null) | undefined;
    is_online?: boolean | undefined;
    user?: UserPublic | undefined;
    addresses?: Array<Address> | undefined;
    media_storage?: MediaStorage | undefined;
    personal_details?: StudentPersonalDetails | undefined;
    applications_count?: number | undefined;
    handlers?: Array<Handler> | undefined;
    agency_branch?: MinimalCompanyBranch | undefined;
    referral_platform?: (string | null) | undefined;
};
export type StudentPersonalDetails = {
    id: Id;
    documentable_id: Id;
    documentable_type: Morphs;
    updated_at: DateTime;
    created_at: DateTime;
    required: Array<string>;
    given_name?: (string | null) | undefined;
    additional_name?: (string | null) | undefined;
    family_name?: (string | null) | undefined;
    date_of_birth?: NullableDate | undefined;
    country_of_citizenship?: (CountryCodeIso2 | null) | undefined;
    passport_id?: (string | null) | undefined;
    passport_expiry_date?: NullableDate | undefined;
    passport_issue_date?: NullableDate | undefined;
    first_language?: (LanguageCodeIso2 | null) | undefined;
    country_of_birth?: (CountryCodeIso2 | null) | undefined;
    place_of_birth?: (string | null) | undefined;
    marital_status?: (MaritalStatus | null) | undefined;
    gender?: (Gender | null) | undefined;
    recognition_of_prior_learning?: (boolean | null) | undefined;
    visa_type?: (string | null) | undefined;
    visa_grant_number?: (string | null) | undefined;
    contact_email?: (string | null) | undefined;
    contact_phone?: (string | null) | undefined;
    phone_emergency?: (string | null) | undefined;
    phone_emergency_name?: (string | null) | undefined;
    phone_emergency_relationship?: (string | null) | undefined;
};
export type MaritalStatus = 'de_facto' | 'divorced' | 'engaged' | 'married' | 'never_married' | 'separated' | 'widowed';
export type FamilyMember = {
    id: Id;
    object: Morphs;
    student_id: Id;
    relationship: FamilyRelationship;
    relationship_label: string | null;
    created_at: DateTime;
    updated_at: DateTime;
    personal_details?: StudentPersonalDetails | undefined;
};
export type FamilyRelationship = 'spouse' | 'child' | 'parent' | 'sibling' | 'guardian' | 'other';
export type FamilyMemberSyncRequest = {
    family_member_ids: Array<Id>;
};
export type FamilyMemberCreateRequest = {
    relationship: FamilyRelationship;
    given_name: string;
    family_name: string;
    additional_name?: (string | null) | undefined;
    date_of_birth?: NullableDate | undefined;
    country_of_citizenship?: (CountryCodeIso2 | null) | undefined;
    country_of_birth?: (CountryCodeIso2 | null) | undefined;
    place_of_birth?: (string | null) | undefined;
    passport_id?: (string | null) | undefined;
    passport_expiry_date?: NullableDate | undefined;
    passport_issue_date?: NullableDate | undefined;
    contact_email?: (string | null) | undefined;
    contact_phone?: (string | null) | undefined;
    gender?: (Gender | null) | undefined;
    marital_status?: (MaritalStatus | null) | undefined;
};
export type AttendedInstitution = {
    id: Id;
    documentable_id: Id;
    documentable_type: Morphs;
    hidden?: boolean | undefined;
    institution_name: string;
    institution_address?: (string | null) | undefined;
    institution_country: CountryCodeIso2;
    institution_principal_subdivision: PrincipalSubdivisionCode;
    course_name: string;
    course_started_on: DateUtc;
    course_ended_on?: NullableDate | undefined;
    areas_of_study?: (string | null) | undefined;
    graduation_status: 'currently_enrolled' | 'completed' | 'failed';
    is_highest_qualification: boolean;
    language_of_instruction?: (LanguageCodeIso2 | null) | undefined;
    qualification_earned?: (string | null) | undefined;
    remarks?: (string | null) | undefined;
    qualification_level_id?: NullableId | undefined;
    education_level?:
        | (
              | (
                    | 'high_school'
                    | 'diploma'
                    | 'graduate_diploma'
                    | 'bachelors'
                    | 'masters'
                    | 'post_graduate'
                    | 'doctorate'
                    | 'other'
                )
              | null
          )
        | undefined;
    grading_scheme?: (('gpa' | 'marks' | 'other') | null) | undefined;
    grade_average?: (string | null) | undefined;
    transcript_url?: (string | null) | undefined;
    marksheet_url?: (string | null) | undefined;
    certificate_url?: (string | null) | undefined;
    media_links?: Array<MediaLink> | undefined;
    updated_at: DateTime;
    created_at: DateTime;
};
export type DateUtc = coercions.date;
export type LanguageTest = {
    id: Id;
    documentable_id: Id;
    documentable_type: Morphs;
    hidden?: boolean | undefined;
    test_type: string;
    test_unique_id?: (string | null) | undefined;
    other_test_name?: (string | null) | undefined;
    test_taken_on: NullableDate;
    test_valid_until?: NullableDate | undefined;
    reading_score?: (string | null) | undefined;
    listening_score?: (string | null) | undefined;
    writing_score?: (string | null) | undefined;
    speaking_score?: (string | null) | undefined;
    overall_score: string;
    created_at?: DateTime | undefined;
    updated_at?: DateTime | undefined;
    exam_result_url?: (string | null) | undefined;
    media_links?: Array<MediaLink> | undefined;
};
export type EmploymentRecord = {
    id: Id;
    documentable_id: Id;
    documentable_type: Morphs;
    hidden?: boolean | undefined;
    employer_name: string;
    employer_address?: (string | null) | undefined;
    employer_contact?: (string | null) | undefined;
    employment_position: string;
    employment_started_on: DateUtc;
    employment_ended_on?: NullableDate | undefined;
    responsibilities: null | Array<string>;
    remarks?: (string | null) | undefined;
    media?: Array<Media> | undefined;
    updated_at?: DateTime | undefined;
    created_at?: DateTime | undefined;
};
export type AgreementTemplate = {
    id: Id;
    created_at: DateTime;
    updated_at: DateTime;
    internal_name: string;
    name: string;
    enabled: boolean;
    contents: string;
    automatically_assign: boolean;
    target: AgreementTarget;
    validity_days: number | null;
    agreement_type: AgreementType;
    commission_percentage: Decimal;
    offer_days?: (number | null) | undefined;
    offer_percentage?: (string | number | null) | undefined;
    offer_name?: (string | null) | undefined;
};
export type AgreementTarget = 'agency' | 'college';
export type AgentApplication = {
    business_identification: string;
    trading_name: string;
    address: string;
    contact_name: string;
    contact_phone: string;
    contact_email: string;
    references: Array<WorkReference>;
};
export type AssociationRequest = {
    id: Id;
    created_at: DateTime;
    updated_at: DateTime;
    sender_company_id: Id;
    receiver_company_id: Id;
    initiator_company_id: Id;
    flow_type: 'agency_to_school';
    requires_agent_application?: boolean | undefined;
    agreement_id?: NullableId | undefined;
    renewal_agreement_id?: NullableId | undefined;
    status?: AssociationRequestStatus | undefined;
    agent_application?: (AgentApplication | null) | undefined;
    sender_terms?: (Array<string> | null) | undefined;
    receiver_company?: Company | undefined;
    sender_company?: Company | undefined;
    agreement?: (Agreement | null) | undefined;
    renewal_agreement?: (Agreement | null) | undefined;
};
export type AssociationRequestStatus = 'interested' | 'pending' | 'accepted' | 'rejected' | 'cancelled' | 'expired';
export type DocumentDraft = {
    id: number;
    name: string;
    description?: (string | null) | undefined;
    template_html: string;
    company_id: number;
    created_at: string;
    updated_at: string;
    deleted_at?: (string | null) | undefined;
    fields?: Array<DocumentDraftField> | undefined;
    company?: Company | undefined;
};
export type DocumentDraftField = {
    id: number;
    field_label: string;
    field_value?: (string | null) | undefined;
    binding?: (string | null) | undefined;
    document_draft_id: number;
    created_at: string;
    updated_at: string;
};
export type Task = {
    id: Id;
    object: Morphs;
    reference_id: string;
    title: string;
    description?: (string | null) | undefined;
    status: TaskStatus;
    type: TaskType;
    start_date?: NullableDateTime | undefined;
    deadline?: NullableDateTime | undefined;
    reschedule_reason?: (string | null) | undefined;
    completed_at?: NullableDateTime | undefined;
    student_id?: NullableId | undefined;
    taskable_id?: (number | null) | undefined;
    taskable_type?: (null | Morphs) | undefined;
    taskable_count?: number | undefined;
    parent_task_id?: NullableId | undefined;
    subtask_count?: number | undefined;
    company_branch_id: number;
    company_id: number;
    user_id?: number | undefined;
    created_at?: DateTime | undefined;
    updated_at?: DateTime | undefined;
    user?: (UserPublic | null) | undefined;
    handlers?: Array<Handler> | undefined;
    media?: Array<Media> | undefined;
    student?: (MinimalStudent | null) | undefined;
};
export type TaskStatus = 'todo' | 'doing' | 'done';
export type TaskType = 'task' | 'meeting';
export type DiscountLog = {
    id: Id;
    discountable_type: Morphs;
    discountable_id: Id;
    user_id?: NullableId | undefined;
    previous_discount?: (string | null) | undefined;
    new_discount: string;
    reason?: (string | null) | undefined;
    user?:
        | (null | Partial<{
              id: Id;
              given_name: string;
              family_name: string;
          }>)
        | undefined;
    created_at: DateTime;
    updated_at: DateTime;
};
export type ServiceTemplate = {
    id: Id;
    name: string;
    description?: (string | null) | undefined;
    kind?: ('general' | 'visa') | undefined;
    steps?: Array<ServiceTemplateStep> | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    deleted_at?: NullableDateTime | undefined;
};
export type ServiceTemplateStep = {
    id: Id;
    service_template_id: Id;
    name: string;
    description?: (string | null) | undefined;
    config: ServiceStepConfig;
    order: number;
    created_at: DateTime;
    updated_at: DateTime;
};
export type ServiceStepConfig =
    | FormStepConfig
    | UploadStepConfig
    | WaitingStepConfig
    | PaymentStepConfig
    | DocumentSignStepConfig
    | QuotationStepConfig
    | ProfileGateStepConfig
    | AgreementStepConfig
    | ResultStepConfig;
export type FormStepConfig = {
    type: 'srv_form';
    fields: Array<FormStepConfigField>;
};
export type FormStepConfigField = {
    name: string;
    value?: (string | null) | undefined;
    type: FormFieldType;
    required?: boolean | undefined;
    order: number;
    options?: Array<string> | undefined;
};
export type FormFieldType = 'text' | 'email' | 'tel' | 'number' | 'select' | 'textarea';
export type UploadStepConfig = {
    type: 'srv_upload';
    uploads: Array<UploadStepConfigItem>;
};
export type UploadStepConfigItem = {
    title: string;
    description?: (string | null) | undefined;
    required: boolean;
    order: number;
    document_schema_id?: (number | null) | undefined;
};
export type WaitingStepConfig = {
    type: 'srv_waiting';
    deadline_needed?: boolean | undefined;
    deadline_in_days?: number | undefined;
    message_field_needed?: boolean | undefined;
    message?: (string | null) | undefined;
    waiting_type?: string | undefined;
    lock_next_steps?: boolean | undefined;
};
export type PaymentStepConfig = {
    type: 'srv_payment';
    payment_amount: Decimal;
    currency: CurrencyCode;
    deadline_needed?: boolean | undefined;
    deadline_in_days?: number | undefined;
    payment_message_needed?: boolean | undefined;
    payment_message?: (string | null) | undefined;
    pay_later_enabled?: boolean | undefined;
    payment_type?: (('stripe' | 'bank_transfer' | 'cash') | null) | undefined;
    gst_percentage?: (number | null) | undefined;
    gst_inclusive?: boolean | undefined;
};
export type DocumentSignStepConfig = {
    type: 'srv_document_sign';
    documents: Array<DocumentSignStepItem>;
    deadline_needed?: boolean | undefined;
    deadline_in_days?: number | undefined;
};
export type DocumentSignStepItem = {
    id: number | null;
    media_id?: (number | null) | undefined;
    signable?: boolean | undefined;
    name?: (string | null) | undefined;
    description?: (string | null) | undefined;
    order: number;
};
export type QuotationStepConfig = {
    type: 'srv_quotation';
    documents: Array<DocumentSignStepItem>;
    deadline_needed?: boolean | undefined;
    deadline_in_days?: number | undefined;
};
export type ProfileGateStepConfig = {
    type: 'srv_profile_gate';
    lock_next_steps?: boolean | undefined;
};
export type AgreementStepConfig = {
    type: 'srv_agreement';
    agreements: Array<AgreementStepItem>;
    block_until_all_signed?: boolean | undefined;
    message?: (string | null) | undefined;
};
export type AgreementStepItem = {
    id: number;
    name: string;
    description?: (string | null) | undefined;
    content?: (string | null) | undefined;
    required?: boolean | undefined;
    order: number;
};
export type ResultStepConfig = {
    type: 'srv_result';
    outcome?: (('approved' | 'refused' | 'pending' | null) | null) | undefined;
    decision_date?: (string | null) | undefined;
    expiry_date?: (string | null) | undefined;
    refusal_reason?: (string | null) | undefined;
    next_steps_deadline?: (string | null) | undefined;
};
export type Service = {
    id: Id;
    reference_id: string;
    object: Morphs;
    student_id: Id;
    student_company_id: Id;
    agency_company_id: Id;
    agency_branch_id?: Id | undefined;
    service_template_id?: NullableId | undefined;
    media_storage_id: Id;
    conversation_id: Id;
    name: string;
    description?: (string | null) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    student?: MinimalStudent | undefined;
    media_storage?: MediaStorage | undefined;
    student_company?: MinimalCompany | undefined;
    agency_company?: MinimalCompany | undefined;
    agency_branch?: MinimalCompanyBranch | undefined;
    steps?: Array<ServiceStep> | undefined;
};
export type ServiceStep = {
    id: Id;
    service_id: Id;
    service_template_step_id?: (number | null) | undefined;
    name: string;
    description?: (string | null) | undefined;
    config: ServiceStepConfig;
    state: Record<string, any>;
    order: number;
    status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
    started_at?: (string | null) | undefined;
    completed_at?: (string | null) | undefined;
    result?: ({} | null) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    signatures?: Array<Signature> | undefined;
    media_links?: Array<ServiceStepMediaLink> | undefined;
};
export type ServiceStepMediaLink = {
    id: Id;
    media?:
        | (null | Partial<{
              id: Id;
              name: string;
              file_name: string;
              mime_type: string | null;
              size: number;
              url: string;
          }>)
        | undefined;
    created_at?: (string | null) | undefined;
};
export type ServiceCreate = {
    student_id: Id;
    student_company_id: Id;
    service_template_id: Id;
    name: string;
    description?: (string | null) | undefined;
};
export type ServiceCreateFromTemplate = {
    student_id: Id;
    name: string;
    description?: (string | null) | undefined;
    steps?: Array<ServiceStepConfigUpdate> | undefined;
};
export type ServiceStepConfigUpdate = {
    config: ServiceStepConfig;
};
export type ServiceStepCreate = {
    service_id: Id;
    service_template_step_id?: (number | null) | undefined;
    name: string;
    description?: (string | null) | undefined;
    config: ServiceStepConfig;
    order?: number | undefined;
    status?: ('pending' | 'in_progress' | 'completed' | 'cancelled') | undefined;
};
export type ServiceStepUpdate = Partial<{
    name: string;
    description: string | null;
    config: ServiceStepConfig;
    state: Record<string, any>;
    order: number;
    status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
    started_at: string | null;
    completed_at: string | null;
    result: {} | null;
}>;
export type ServiceProgress = {
    progress_percentage: number;
    is_completed: boolean;
    total_steps: number;
    completed_steps: number;
    steps: Array<ServiceStepSummary>;
};
export type ServiceStepSummary = {
    id: Id;
    name: string;
    type: string;
    status: 'created' | 'pending' | 'in_progress' | 'pending_approval' | 'completed' | 'skipped' | 'cancelled';
    order: number;
    can_complete: boolean;
    warnings: Array<string>;
    started_at: string | null;
    completed_at: string | null;
};
export type Comment = {
    id: number;
    body: string;
    commentable_id: number;
    commentable_type: string;
    user_id: number;
    edit_history: Array<EditHistory>;
    last_edited_at?: NullableDateTime | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    media?: Array<Media> | undefined;
    user?: (UserPublic | null) | undefined;
};
export type EditHistory = {
    body: string;
    changed_at: DateTime;
    created_at: DateTime;
    changed_by?: NullableId | undefined;
};
export type AppliedProgram = FeesStructure &
    FeesSchedule & {
        id: Id;
        created_at: DateTime;
        updated_at: DateTime;
        name: string;
        order: number;
        is_offered?: (boolean | null) | undefined;
        is_accepted?: (boolean | null) | undefined;
        is_enrolled?: (boolean | null) | undefined;
        program_id: Id;
        application_id: Id;
        payment_plan_id: NullableId;
        program?: Program | undefined;
        payment_plan?: PaymentPlan | undefined;
    };
export type PaymentPlan = FeesSchedule &
    FeesStructure & {
        id: Id;
        program_id?: NullableId | undefined;
        application_id: Id;
        start_date?: NullableDate | undefined;
        end_date?: NullableDate | undefined;
        cancelled_at?: NullableDateTime | undefined;
        description: string;
        created_at: DateTime;
        updated_at: DateTime;
        payment_items?: Array<PaymentItem> | undefined;
        payment_attempts?: Array<PaymentAttempt> | undefined;
    };
export type PaymentItem = {
    id: Id;
    payment_plan_id: Id;
    index: number;
    remarks: string;
    due_amount: Decimal;
    due_on: DateUtc;
    gst_percentage: NullableDecimal;
    created_at: DateTime;
    updated_at: DateTime;
    can?:
        | Partial<{
              attempt: boolean;
          }>
        | undefined;
    payment_attempts?: Array<PaymentAttempt> | undefined;
    invoice_urls?: Array<string> | undefined;
};
export type PaymentAttempt = {
    id: Id;
    payment_plan_id: Id;
    status: PaymentAttemptStatus;
    remarks: string;
    amount: Decimal;
    fulfilled_at: NullableDateTime;
    available_at: NullableDateTime;
    distributed_at: NullableDateTime;
    refunded_at: NullableDateTime;
    stripe_payment_intent_id?: (string | null) | undefined;
    client_secret?: (string | null) | undefined;
    linked_amount?: Decimal | undefined;
    last_polled_at?: NullableDateTime | undefined;
    is_automatic?: boolean | undefined;
    requires_user_input?: boolean | undefined;
    initiated_by_user_id?: NullableId | undefined;
    can?:
        | Partial<{
              cancel: boolean;
              pay: boolean;
          }>
        | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    invoice_urls?: Array<string> | undefined;
};
export type PaymentAttemptStatus = 'pending' | 'fulfilled' | 'available' | 'distributed' | 'refunded' | 'cancelled';
export type OfferLetter = {
    id: Id;
    application_id: Id;
    status: OfferLetterStatus;
    valid_upto: DateTime;
    created_at: DateTime;
    updated_at: DateTime;
    media?: Array<Media> | undefined;
    signatures?: Array<Signature> | undefined;
};
export type OfferLetterStatus = 'pending' | 'signed' | 'revoked' | 'rejected';
export type Application = {
    id: Id;
    reference_id?: (string | null) | undefined;
    object: Morphs;
    name?: (string | null) | undefined;
    updated_at: DateTime;
    created_at: DateTime;
    student_id: Id;
    media_storage_id: NullableId;
    application_type?: ('course' | 'visa') | undefined;
    applicable_id?: NullableId | undefined;
    applicable_type?: (string | null) | undefined;
    visa_type?: (('subclass_500' | 'subclass_485' | 'subclass_482' | null) | null) | undefined;
    target_date?: (string | null) | undefined;
    school_company_id?: NullableId | undefined;
    agency_company_id: Id;
    staff_company_id: Id;
    school_branch_id?: NullableId | undefined;
    agency_branch_id: Id;
    agency_conversation_id: NullableId;
    student_conversation_id: NullableId;
    agency_staff_agreement_id?: NullableId | undefined;
    school_staff_agreement_id?: NullableId | undefined;
    agency_school_agreement_id?: NullableId | undefined;
    intake_year?: (number | null) | undefined;
    intake_month?: (number | null) | undefined;
    status: string;
    status_reason?: (string | null) | undefined;
    student?: Student | undefined;
    offer_letters?: Array<OfferLetter> | undefined;
    package?: (Package | null) | undefined;
    handlers?: Array<Handler> | undefined;
    applied_programs?: Array<AppliedProgram> | undefined;
    media_storage?: (MediaStorage | null) | undefined;
    document_requirements?: Array<DocumentRequirement> | undefined;
    payment_plans?: Array<PaymentPlan> | undefined;
    campus?: Campus | undefined;
    agency?: Agency | undefined;
    personal_details?: (StudentPersonalDetails | null) | undefined;
    diverged?: boolean | undefined;
    can?:
        | Partial<{
              view: string | boolean;
              send_offer_letter: string | boolean;
              attach_acceptance_letter: string | boolean;
              sign_offer_letter: string | boolean;
              attach_coe: string | boolean;
              submit: string | boolean;
              in_review: string | boolean;
              reject: string | boolean;
              withdraw: string | boolean;
          }>
        | undefined;
    notes_moved_from_student?: boolean | undefined;
};
export type DocumentRequirement = {
    id: Id;
    application_id: Id;
    required?: boolean | undefined;
    document_type_id: number | null;
    denied_at?: NullableDateTime | undefined;
    denied_reason?: (string | null) | undefined;
    last_submitted_at?: NullableDateTime | undefined;
    approved_at?: NullableDateTime | undefined;
    title: string;
    instructions?: (string | null) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    media_links: Array<MediaLink>;
};
export type QualificationLevel = {
    id: Id;
    title: string;
    description: string;
    enabled: boolean;
    created_at: DateTime;
    updated_at: DateTime;
};
export type DocumentType = {
    id: Id;
    title: string;
    instructions: string | null;
    required: boolean;
    auto_add?: boolean | undefined;
    enabled: boolean;
    qualification_level_id: NullableId;
    created_at: DateTime;
    updated_at: DateTime;
    qualification_level?: (QualificationLevel | null) | undefined;
};
export type ApplicationEvent = {
    id: Id;
    application_id: Id;
    created_at: DateTime;
    updated_at: DateTime;
    caused_by: string | null;
    code: string;
    title: string;
    description: string;
    data?:
        | (Partial<{
              document_requirement_id: Id;
              application_status: ApplicationStatus;
              subject: string;
              communication_id: Id;
          }> | null)
        | undefined;
    changes?: ({} | null) | undefined;
};
export type ApplicationStatus =
    | 'draft'
    | 'submitted'
    | 'in_review'
    | 'offered'
    | 'accepted'
    | 'confirmed'
    | 'rejected'
    | 'withdrawn'
    | 'canceled'
    | 'completed';
export type Communication = {
    id: Id;
    student_id: Id;
    loggable_id: number;
    loggable_type: Morphs;
    channel: 'visa' | 'course';
    subject: string;
    html_body: string;
    text_body?: (string | null) | undefined;
    recipients: Partial<{
        to: Array<string>;
        cc: Array<string>;
    }> | null;
    sent_at: DateTime;
    created_at: DateTime;
    updated_at: DateTime;
};
export type SummarizedPaymentPlan = FeesSchedule &
    FeesStructure & {
        id: Id;
        program_id: NullableId;
        application_id: Id;
        application_reference?: string | undefined;
        student_reference?: string | undefined;
        agency?: MinimalCompany | undefined;
        school?: MinimalCompany | undefined;
        start_date: NullableDate;
        end_date?: NullableDate | undefined;
        cancelled_at?: NullableDateTime | undefined;
        description: string;
        created_at: DateTime;
        updated_at: DateTime;
        next_due: NullableDate;
        total_amount: Decimal;
        paid_amount: Decimal;
        other_amounts: Array<{
            label: string;
            company_id: Id;
            company_type?: AccountType | undefined;
            total_amount: Decimal;
            received_amount: Decimal;
        }>;
    };
export type MockPaymentPlanConfig = {
    program_id?: NullableId | undefined;
    currency: CurrencyCode;
    application_fees?: Decimal | undefined;
    tuition_fees?: Decimal | undefined;
    material_fees?: Decimal | undefined;
    commission_bonus?: Decimal | undefined;
    start_date?: DateUtc | undefined;
    agency_platform_fees?: Decimal | undefined;
    school_platform_fees?: Decimal | undefined;
    agency_commission?: Decimal | undefined;
    number_of_payments?: number | undefined;
    interval_days?: number | undefined;
};
export type MockPaymentItem = {
    currency: CurrencyCode;
    due_on: DateUtc;
    due_amount: Decimal;
    due_explanation: string;
    agency_amount?: Decimal | undefined;
    agency_explanation?: string | undefined;
    school_amount?: Decimal | undefined;
    school_explanation?: string | undefined;
    platform_amount?: Decimal | undefined;
    platform_explanation?: string | undefined;
};
export type AttemptRecord = {
    id: Id;
    payment_attempt_id: Id;
    company_id?: NullableId | undefined;
    company_branch_id?: NullableId | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    total_amount: string;
    expected_amount: string;
    remarks?: string | undefined;
    status: AttemptRecordStatus;
    paid_at?: NullableDate | undefined;
    stripe_transfer_id?: (string | null) | undefined;
    invoice_urls?: Array<string> | undefined;
};
export type AttemptRecordStatus =
    | 'pending'
    | 'fulfilled'
    | 'available_soon'
    | 'available'
    | 'payout_initiated'
    | 'paid_out'
    | 'refunded'
    | 'cancelled';
export type PaymentRecord = {
    id: Id;
    company_id: Id;
    company_branch_id: NullableId;
    application_id: Id;
    payment_item_id: Id;
    invoice_id?: NullableId | undefined;
    student_name: string;
    application_name: string;
    agency_name: string;
    school_name: string;
    expected_remarks: string;
    total_remarks: string;
    expected_amount: string;
    total_amount: string;
    expected_around: NullableDate;
    created_at: DateTime;
    updated_at: DateTime;
    attempt_records?: Array<AttemptRecord> | undefined;
};
export type WebView = DefinedWebView & {
    updated_at: DateTime;
    updated_by_id: NullableId;
    updated_by?: (UserPublic | null) | undefined;
    requirements?: WebViewRequirements | undefined;
    contents: Array<{
        type: 'text' | 'html' | 'link';
        key: string;
        value: string;
    }>;
    images: Array<{
        alt: string | null;
        key: string;
        src: string;
    }>;
};
export type DefinedWebView = {
    id: string;
    name: string;
    description: string;
    available: boolean;
};
export type WebViewRequirements = {
    id: string;
    contents: Array<{
        title: string;
        type: 'text' | 'html' | 'link';
        group: string;
        key: string;
        required: boolean;
        min_length: number | null;
        max_length: number | null;
    }>;
    images: Array<{
        title: string;
        group: string;
        key: string;
        required: boolean;
        constraints: {
            min_width: number | null;
            min_height: number | null;
            max_width: number | null;
            max_height: number | null;
            width: number | null;
            height: number | null;
            allowed_mime_types: Array<string> | null;
            max_size_bytes: number | null;
            min_size_bytes: number | null;
            aspect_ratio: number | null;
        } | null;
    }>;
};
export type PublicPage = {
    id: Id;
    visible?: boolean | undefined;
    name: string;
    display_name?: string | undefined;
    distance?: NullableDecimal | undefined;
    company_type: AccountType;
    company_id: Id;
    company_branch_id: Id;
    about: string;
    external: ExternalPresence;
    agency_factoids?: AgencyFactoids | undefined;
    campus_factoids?: CampusFactoids | undefined;
    media?: Array<Media> | undefined;
    media_collections?: Array<MediaCollection> | undefined;
    partnered?: boolean | undefined;
    partners?: Array<PartnerPublic> | undefined;
    address?: (Address | null) | undefined;
    company_branch?: (CompanyBranch | null) | undefined;
    logo_url?: (string | null) | undefined;
    branches?:
        | (null | Array<
              Partial<{
                  id: Id;
                  name: string;
                  address: Address | null;
              }>
          >)
        | undefined;
};
export type UpdatePublicPage = Partial<{
    about: string;
    external: ExternalPresence;
    agency_factoids: AgencyFactoids;
    campus_factoids: CampusFactoids;
}>;
export type CreatePublicPage = {
    name: string;
    program_ids?: Array<Id> | undefined;
    package_ids?: Array<Id> | undefined;
    about?: string | undefined;
    external?: ExternalPresence | undefined;
    agency_factoids?: AgencyFactoids | undefined;
    campus_factoids?: CampusFactoids | undefined;
    address: AddressBody;
};
export type Campus = PublicPage & {
    assessment_level?: (number | null) | undefined;
    company_name?: string | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    can?:
        | Partial<{
              update: boolean | string;
          }>
        | undefined;
};
export type Program = FeesSchedule &
    FeesStructure & {
        id: Id;
        program_level_id: Id;
        company_id: Id;
        enabled: boolean;
        created_at: DateTime;
        updated_at: DateTime;
        name: string;
        distance?: NullableDecimal | undefined;
        about: string;
        requirements: string;
        national_course_code?: (string | null) | undefined;
        cricos_course_code?: (string | null) | undefined;
        intakes: Array<Month>;
        method_of_delivery: MethodOfDelivery;
        partnered?: boolean | undefined;
        campuses?: Array<Campus> | undefined;
        media?: Array<Media> | undefined;
        media_collections?: Array<MediaCollection> | undefined;
        program_level?: (null | ProgramLevel) | undefined;
        packaged_programs?: Array<PackagedProgram> | undefined;
        assessment_level?: (number | null) | undefined;
        company_name?: string | undefined;
        logo_url?: (string | null) | undefined;
        applications_count?: (number | null) | undefined;
    };
export type PackagedProgram = FeesSchedule &
    FeesStructure & {
        id: Id;
        program_id: Id;
        package_id: Id;
        name: string;
        order: number;
        created_at: DateTime;
        updated_at: DateTime;
        program?: (null | Program) | undefined;
        package?: (null | Package) | undefined;
    };
export type Package = {
    id: Id;
    company_id: Id;
    created_at: DateTime;
    updated_at: DateTime;
    enabled: boolean;
    name: string;
    tuition_fees?: (number | null) | undefined;
    duration_weeks?: (number | null) | undefined;
    material_fees?: (number | null) | undefined;
    application_fees?: (number | null) | undefined;
    logo_url?: (string | null) | undefined;
    partnered?: boolean | undefined;
    company_name?: string | undefined;
    assessment_level?: (number | null) | undefined;
    packaged_programs?: Array<PackagedProgram> | undefined;
    campuses?: Array<Campus> | undefined;
};
export type PlatformReferral = {
    id: Id;
    created_at: DateTime;
    updated_at: DateTime;
    enabled: boolean;
    company_id: Id;
    referral_code: string;
    offer_days: number | null;
    offer_percentage: Decimal;
};
export type Agency = PublicPage & {
    mara_number?: (string | null) | undefined;
    signup_referral?: (null | SignupReferral) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    can?:
        | Partial<{
              update: boolean | string;
          }>
        | undefined;
};
export type CompanyPayoutBatch = {
    id: Id;
    reference_id: string;
    currency: string;
    payout_source?: (AccountType | null) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    processing_file_url?: (string | null) | undefined;
    payouts?: Array<CompanyPayout> | undefined;
    bank_detail?: (null | BankDetail) | undefined;
};
export type CompanyPayout = {
    id: Id;
    reference_id: string;
    company_id: Id;
    company_name?: (null | string) | undefined;
    amount: string;
    fee?: (string | null) | undefined;
    currency: string;
    status: CompanyPayoutStatus;
    status_description?: (string | null) | undefined;
    status_changed_at?: DateTime | undefined;
    arrival_date: NullableDate;
    created_at: DateTime;
    updated_at: DateTime;
    details_file_url?: (string | null) | undefined;
};
export type CompanyPayoutStatus = 'created' | 'cancelled' | 'in_transit' | 'paid' | 'failed';
export type PayoutPrediction = {
    company_id: Id;
    company_name: string;
    company_type: AccountType;
    amount: string | null;
    currency: string | null;
    is_valid?: boolean | undefined;
    errors: Array<string>;
};
export type EventRegistrationConfig = Partial<{
    enabled: boolean;
    more_info: string | null;
    starts_at: NullableDateTime;
    ends_at: NullableDateTime;
    email_required: boolean;
    phone_required: boolean;
}>;
export type AdvancedPostMeta = Partial<{
    event_registration: null | EventRegistrationConfig;
}>;
export type EventRegistrant = {
    id: Id;
    advanced_post_id: Id;
    user_id: Id;
    name?: string | undefined;
    email?: (string | null) | undefined;
    phone?: (string | null) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
};
export type AdvancedPost = {
    id: Id;
    user_id: NullableId;
    postable_id: Id;
    postable_type: Morphs;
    postable_name?: string | undefined;
    post_type: 'news' | 'event' | 'article';
    title: string;
    content: string;
    summary?: (string | null) | undefined;
    event_start_at?: NullableDateTime | undefined;
    event_end_at?: NullableDateTime | undefined;
    event_location?: (string | null) | undefined;
    article_authors?: (string | null) | undefined;
    company_branch_ids?: (null | Array<Id>) | undefined;
    company_branches?: Array<CompanyBranch> | undefined;
    created_at?: DateTime | undefined;
    updated_at?: DateTime | undefined;
    meta?: (null | AdvancedPostMeta) | undefined;
    media?: Array<Media> | undefined;
    event_registrants?: Array<EventRegistrant> | undefined;
};
export type PrettySearchItem = {
    id: Id;
    type: Morphs;
    name: string;
    description: string | null;
    image_url: string | null;
    stats?: ({} | null) | undefined;
    addresses?: Array<Address> | undefined;
    related?:
        | Array<{
              id: Id;
              type: Morphs;
              name: string;
          }>
        | undefined;
};
export type MinimalSearchItem = {
    id: Id;
    type: MinimalSearchTypes;
    name: string;
    description: string | null;
    image_url: string | null;
    parents: Array<{
        id: Id;
        type: Morphs;
        name: string;
    }>;
    descriptors: {} | null;
};
export type MinimalSearchTypes =
    | 'program'
    | 'school'
    | 'agency'
    | 'campus'
    | 'agency_branch'
    | 'student'
    | 'user'
    | 'company';
export type StudentResume = {
    id: Id;
    user_id: Id;
    name?: (string | null) | undefined;
    address?: (string | null) | undefined;
    email?: (string | null) | undefined;
    phone?: (string | null) | undefined;
    career_objective?: (string | null) | undefined;
    key_skills?: Array<string> | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    resume_url: string | null;
};
export type UserContext = {
    app_env?: (string | null) | undefined;
    user: User;
    students: Array<Student>;
    role: Role;
    company: Company;
    authorized_branches: Array<CompanyBranch>;
    agencies: Array<Agency>;
    campuses: Array<Campus>;
    settings: Settings;
    managed_company_ids: Array<Id>;
    enums: ApplicationEnums;
    defaults: Partial<{
        country: Country | null;
        currency: Currency | null;
    }>;
};
export type Settings = Partial<{
    show_business_in_agency_search: boolean;
    show_business_in_school_search: boolean;
    default_currency: CurrencyCode;
    default_country: CountryCodeIso2 | null;
    payment_attempt_minimum_amount: Decimal;
    partner_request_retry_interval: number;
    platform_info: PlatformInfo;
    application_hard_expiry_days: number;
    application_expiry_warn_days: number;
    agreement_expiry_warn_days: number;
    payment_plan_ending_warn_days: number;
    payment_due_warn_days: number;
    payment_overdue_repeat_days: number;
    application_stale_days: number;
    autopay_upcoming_payments_days: number;
    becs_transaction_limit: number;
    dynamic: Partial<{
        auto_submit_applications: boolean;
        allow_agency_direct_add_student: boolean;
        invite_student_cooldown_hours: number;
        platform_referral_offer_percentage: NullableDecimal;
        platform_referral_offer_days: number | null;
        max_discount_percentage: NullableDecimal;
        signature: null | {
            name: string;
            email: string;
        };
        external_links: Array<{
            name: string;
            url: string;
        }>;
        support_email: string | null;
        email_regards: string | null;
        email_copyright: string | null;
        auto_invoice_upcoming_payments_days: number | null;
        email_exceptions: Array<string>;
    }>;
    media_storage: MediaStorage;
    trust_bank_detail: null | BankDetail;
    platform_bank_detail: null | BankDetail;
}>;
export type PlatformInfo = {
    name: string | null;
    email: string | null;
    phone: string | null;
    toll_free_phone: string | null;
    address: string | null;
};
export type ApplicationEnums = {
    language_test_types: Array<{
        label: string;
        value: string;
        enabled: boolean;
    }>;
};
export type EmailTemplate = {
    id: Id;
    routable?: boolean | undefined;
    code: string;
    category?: EmailCategory | undefined;
    title: string;
    description: string;
    has_related_entities: boolean;
    root_branch_only: boolean;
    subject?: (string | null) | undefined;
    content?: (string | null) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
};
export type EmailCategory = 'accounts' | 'platform' | 'misc' | 'admissions' | 'user';
export type EmailRecipient = {
    id: Id;
    company_id: Id;
    company_branch_id: Id;
    email_template_id: Id;
    recipients: Array<string>;
    send_to_related: boolean;
    created_at: DateTime;
    updated_at: DateTime;
    email_template?: (null | EmailTemplate) | undefined;
};
export type PaymentMethodReference = {
    id: Id;
    provider: string;
    reference_id: string;
    reference_object: null | StripePaymentMethod;
    last_error_message: string | null;
    is_primary: boolean;
    is_active: boolean;
    created_at: DateTime;
    updated_at: DateTime;
};
export type StripePaymentMethod = {
    id: string;
    type: string;
    created?: number | undefined;
    au_becs_debit?:
        | Partial<{
              bsb_number: null | string;
              last4: null | string;
              fingerprint: null | string;
          }>
        | undefined;
    card?:
        | Partial<{
              brand: string;
              last4: string;
              fingerprint: null | string;
              exp_month: number;
              exp_year: number;
              display_brand: null | string;
              funding: string;
          }>
        | undefined;
};
export type SmartEvent = {
    id: number;
    eventable_id: number;
    eventable_type: Morphs;
    task_id?: NullableId | undefined;
    user_id?: NullableId | undefined;
    title?: (string | null) | undefined;
    description?: (string | null) | undefined;
    type: SmartEventType;
    event_data?: (null | SmartEventData) | undefined;
    created_at: DateTime;
    updated_at: DateTime;
    task?: (null | Task) | undefined;
    user?: (null | UserPublic) | undefined;
    media_links?: Array<MediaLink> | undefined;
};
export type SmartEventType =
    | 'note'
    | 'task'
    | 'meeting'
    | 'activity'
    | 'upload'
    | 'document_reupload'
    | 'payment'
    | 'email';
export type SmartEventData =
    | NoteEventData
    | ActivityEventData
    | InvoiceEventData
    | DocumentReuploadEventData
    | EmailEventData;
export type NoteEventData = {
    type: 'se_note';
    body: string;
    history?: Array<EditHistory> | undefined;
};
export type ActivityEventData = {
    type: 'se_activity';
    old: Record<string, unknown> | null;
    new: Record<string, unknown> | null;
};
export type InvoiceEventData = {
    type: 'se_invoice';
    invoice_id: number;
    invoice_url: string;
    total_amount: string;
    currency: string;
    due_date?: (string | null) | undefined;
    line_items_count: number;
    payment_plan_id?: (number | null) | undefined;
    receipt_url?: (string | null) | undefined;
};
export type DocumentReuploadEventData = {
    type: 'se_document_reupload';
    document_reupload_request_id: number;
    document_title: string;
    status: 'pending_approval' | 'accepted' | 'rejected';
    reason?: (string | null) | undefined;
    rejection_reason?: (string | null) | undefined;
    previous_media_id?: (number | null) | undefined;
    new_media_id?: (number | null) | undefined;
};
export type EmailEventData = {
    type: 'se_email';
    subject: string;
    communication_id: Id;
    invoice_id?: Id | undefined;
    invoice_url?: (string | null) | undefined;
};

export const CommonError = z.object({ code: z.string().optional(), message: z.string() });
export const Id = z.number();
export const DateTime = coercions.date();
export const ProgramLevel = z.object({
    id: Id.int(),
    title: z.string(),
    enabled: z.boolean(),
    created_at: DateTime,
    updated_at: DateTime,
});
export const title = z.string();
export const enabled = z.boolean();
export const createProgramLevel_Body = z.object({ title: title, enabled: enabled });
export const ValidationError = z.object({ message: z.string(), errors: z.record(z.array(z.string())) });
export const updateProgramLevel_Body = z.object({ title: title, enabled: enabled }).partial();
export const NullableId = z.union([z.number(), z.null()]);
export const QualificationLevel = z.object({
    id: Id.int(),
    title: z.string(),
    description: z.string(),
    enabled: z.boolean(),
    created_at: DateTime,
    updated_at: DateTime,
});
export const DocumentType = z.object({
    id: Id.int(),
    title: z.string(),
    instructions: z.union([z.string(), z.null()]),
    required: z.boolean(),
    auto_add: z.boolean().optional(),
    enabled: z.boolean(),
    qualification_level_id: NullableId,
    created_at: DateTime,
    updated_at: DateTime,
    qualification_level: z.union([QualificationLevel, z.null()]).optional(),
});
export const instructions = z.union([z.string(), z.null()]);
export const required = z.boolean();
export const auto_add = z.boolean();
export const qualification_level = z.union([QualificationLevel, z.null()]);
export const createDocumentType_Body = z.object({
    title: title,
    instructions: instructions,
    required: required,
    auto_add: auto_add.optional(),
    enabled: enabled,
    qualification_level_id: NullableId,
    qualification_level: qualification_level.optional(),
});
export const updateDocumentType_Body = z
    .object({
        title: title,
        instructions: instructions,
        required: required,
        auto_add: auto_add,
        enabled: enabled,
        qualification_level_id: NullableId,
        qualification_level: qualification_level,
    })
    .partial();
export const filter_search_ = z.union([coercions.any().optional(), z.array(coercions.any().optional())]).optional();
export const filter_company_branch_id_ = z
    .union([z.string(), z.array(z.string()), z.number(), z.array(z.number())])
    .nullish();
export const PaginatedResponse = z.object({
    current_page: z.number().int(),
    per_page: z.number().int(),
    from: z.union([z.number(), z.null()]).optional(),
    to: z.union([z.number(), z.null()]).optional(),
    next_page_url: z.union([z.string(), z.null()]).optional(),
    prev_page_url: z.union([z.string(), z.null()]).optional(),
    total: z.number().int().optional(),
});
export const FormFieldType = z.enum(['text', 'email', 'tel', 'number', 'select', 'textarea']);
export const FormStepConfigField = z.object({
    name: z.string(),
    value: z.union([z.string(), z.null()]).optional(),
    type: FormFieldType,
    required: z.boolean().optional(),
    order: z.number().int(),
    options: z.array(z.string()).optional(),
});
export const FormStepConfig = z.object({ type: z.literal('srv_form'), fields: z.array(FormStepConfigField) });
export const UploadStepConfigItem = z.object({
    title: z.string(),
    description: z.union([z.string(), z.null()]).optional(),
    required: z.boolean(),
    order: z.number().int(),
    document_schema_id: z.union([z.number(), z.null()]).optional(),
});
export const UploadStepConfig = z.object({ type: z.literal('srv_upload'), uploads: z.array(UploadStepConfigItem) });
export const WaitingStepConfig = z.object({
    type: z.literal('srv_waiting'),
    deadline_needed: z.boolean().optional(),
    deadline_in_days: z.number().int().optional(),
    message_field_needed: z.boolean().optional(),
    message: z.union([z.string(), z.null()]).optional(),
    waiting_type: z.string().optional(),
    lock_next_steps: z.boolean().optional(),
});
export const Decimal = coercions.decimal();
export const CurrencyCode = z.string();
export const PaymentStepConfig = z.object({
    type: z.literal('srv_payment'),
    payment_amount: Decimal,
    currency: CurrencyCode.min(3).max(3),
    deadline_needed: z.boolean().optional(),
    deadline_in_days: z.number().int().optional(),
    payment_message_needed: z.boolean().optional(),
    payment_message: z.union([z.string(), z.null()]).optional(),
    pay_later_enabled: z.boolean().optional(),
    payment_type: z.union([z.enum(['stripe', 'bank_transfer', 'cash']), z.null()]).optional(),
    gst_percentage: z.union([z.number(), z.null()]).optional(),
    gst_inclusive: z.boolean().optional().default(true),
});
export const DocumentSignStepItem = z.object({
    id: z.union([z.number(), z.null()]),
    media_id: z.union([z.number(), z.null()]).optional(),
    signable: z.boolean().optional().default(true),
    name: z.union([z.string(), z.null()]).optional(),
    description: z.union([z.string(), z.null()]).optional(),
    order: z.number().int(),
});
export const DocumentSignStepConfig = z.object({
    type: z.literal('srv_document_sign'),
    documents: z.array(DocumentSignStepItem),
    deadline_needed: z.boolean().optional(),
    deadline_in_days: z.number().int().optional(),
});
export const QuotationStepConfig = z.object({
    type: z.literal('srv_quotation'),
    documents: z.array(DocumentSignStepItem),
    deadline_needed: z.boolean().optional(),
    deadline_in_days: z.number().int().optional(),
});
export const ProfileGateStepConfig = z.object({
    type: z.literal('srv_profile_gate'),
    lock_next_steps: z.boolean().optional(),
});
export const AgreementStepItem = z.object({
    id: z.number().int(),
    name: z.string(),
    description: z.union([z.string(), z.null()]).optional(),
    content: z.union([z.string(), z.null()]).optional(),
    required: z.boolean().optional(),
    order: z.number().int(),
});
export const AgreementStepConfig = z.object({
    type: z.literal('srv_agreement'),
    agreements: z.array(AgreementStepItem),
    block_until_all_signed: z.boolean().optional(),
    message: z.union([z.string(), z.null()]).optional(),
});
export const ResultStepConfig = z.object({
    type: z.literal('srv_result'),
    outcome: z.union([z.enum(['approved', 'refused', 'pending', null]), z.null()]).optional(),
    decision_date: z.union([z.string(), z.null()]).optional(),
    expiry_date: z.union([z.string(), z.null()]).optional(),
    refusal_reason: z.union([z.string(), z.null()]).optional(),
    next_steps_deadline: z.union([z.string(), z.null()]).optional(),
});
export const ServiceStepConfig = z.discriminatedUnion('type', [
    FormStepConfig,
    UploadStepConfig,
    WaitingStepConfig,
    PaymentStepConfig,
    DocumentSignStepConfig,
    QuotationStepConfig,
    ProfileGateStepConfig,
    AgreementStepConfig,
    ResultStepConfig,
]);
export const ServiceTemplateStep = z.object({
    id: Id.int(),
    service_template_id: Id.int(),
    name: z.string().max(255),
    description: z.union([z.string(), z.null()]).optional(),
    config: ServiceStepConfig,
    order: z.number().int(),
    created_at: DateTime,
    updated_at: DateTime,
});
export const NullableDateTime = coercions.nullableDate();
export const ServiceTemplate = z.object({
    id: Id.int(),
    name: z.string().max(255),
    description: z.union([z.string(), z.null()]).optional(),
    kind: z.enum(['general', 'visa']).optional().default('general'),
    steps: z.array(ServiceTemplateStep).optional(),
    created_at: DateTime,
    updated_at: DateTime,
    deleted_at: NullableDateTime.optional(),
});
export const createServiceTemplate_Body = z.object({
    name: z.string().max(255),
    description: z.union([z.string(), z.null()]).optional(),
    duplicate_template_id: z.number().int().optional(),
    company_branch_id: z.number().int(),
    kind: z.enum(['general', 'visa']).optional(),
});
export const updateServiceTemplate_Body = z
    .object({
        name: z.string().max(255),
        description: z.union([z.string(), z.null()]),
        kind: z.enum(['general', 'visa']),
    })
    .partial();
export const name = z.string();
export const description = z.union([z.string(), z.null()]);
export const config = ServiceStepConfig;
export const createServiceTemplateStep_Body = z
    .object({ name: name.max(255), description: description, config: config })
    .partial();
export const reorderServiceTemplateSteps_Body = z.object({ order: z.array(z.number().int()) }).partial();
export const ProfilePreCheckResult = z.object({
    complete: z.boolean(),
    missing: z.array(z.object({ key: z.string(), label: z.string(), profile_deep_link: z.string() })),
});
export const updateServiceTemplateStep_Body = z
    .object({ name: name.max(255), description: description, config: config, updated_at: DateTime })
    .partial();
export const Morphs = z.enum([
    'user',
    'student',
    'agency',
    'campus',
    'company',
    'company_branch',
    'attended_institution',
    'language_test',
    'program',
    'document_requirement',
    'application',
    'agreement',
    'media_storage',
    'payment_attempt',
    'payment_item',
    'attempt_record',
    'association_request',
    'student_resume',
    'settings',
    'package',
    'offer_letter',
    'signature',
    'task',
    'comment',
    'service_template',
    'service',
    'smart_event',
    'family_member',
    'advanced_posts',
    'company_payout_batch',
    'company_payout',
    'service_step',
    'packaged_program',
    'payment_plan',
    'applied_program',
    'manual_payment_log',
]);
export const AccountType = z.enum(['student', 'staff', 'school', 'agency']);
export const CompanyBranchStatus = z.enum(['active', 'hidden', 'disabled']);
export const MinimalCompany = z.object({
    id: Id.int(),
    display_name: z.string(),
    reference_id: z.union([z.string(), z.null()]),
    company_type: AccountType,
    created_at: DateTime,
    updated_at: DateTime,
});
export const MinimalCompanyBranch = z.object({
    id: Id.int(),
    company_id: Id.int(),
    company_type: AccountType,
    parent_branch_id: NullableId,
    status: CompanyBranchStatus,
    name: z.string(),
    display_name: z.string().optional(),
    created_at: DateTime,
    updated_at: DateTime,
    company: z.union([MinimalCompany, z.null()]),
});
export const MinimalStudent = z.object({
    id: Id.int(),
    user_id: Id.int(),
    reference_id: z.union([z.string(), z.null()]),
    company_id: Id.int(),
    agency_branch_id: Id.int(),
    full_name: z.union([z.string(), z.null()]).optional(),
    full_reference: z.string(),
    created_at: DateTime,
    updated_at: DateTime,
    avatar_url: z.union([z.string(), z.null()]).optional(),
    agency_branch: MinimalCompanyBranch.optional(),
});
export const MediaStorageType = z.enum(['student_documents', 'application_documents', 'settings']);
export const MediaCustomProperties = z.union([
    z.object({ document_type_id: z.union([z.number(), z.null()]), description: z.string().max(1000) }).partial(),
    z.null(),
]);
export const MediaLink = z.lazy(() =>
    z.object({
        id: Id.int(),
        linkable_id: Id.int(),
        linkable_type: Morphs,
        media_id: Id.int(),
        is_locked: z.boolean().optional(),
        lock_reason: z.union([z.string(), z.null()]).optional(),
        unlinked_at: NullableDateTime.optional(),
        media: Media.optional(),
        created_at: DateTime,
        updated_at: DateTime,
    })
);
export const Media = z.lazy(() =>
    z.object({
        id: Id.int(),
        model_id: Id.int().optional(),
        model_type: Morphs.optional(),
        collection_name: z.string(),
        name: z.string(),
        mime_type: z.union([z.string(), z.null()]).optional(),
        original_url: z.string(),
        preview_url: z.union([z.string(), z.null()]).optional(),
        size: z.union([z.number(), z.null()]),
        custom_properties: MediaCustomProperties.optional(),
        order: z.union([z.number(), z.null()]).optional(),
        media_links: z.array(MediaLink).optional(),
        created_at: DateTime,
        updated_at: DateTime,
        uploaded_by: z
            .union([z.null(), z.object({ id: Id.int(), first_name: z.string(), last_name: z.string() }).partial()])
            .optional(),
    })
);
export const MediaCollection = z.object({
    available: z.boolean(),
    display_name: z.union([z.string(), z.null()]),
    collection_name: z.string(),
    description: z.union([z.string(), z.null()]),
    min_media_count: z.number().int(),
    max_media_count: z.union([z.number(), z.null()]),
    keeps_latest: z.boolean(),
    allowed_mime_types: z.union([z.null(), z.array(z.string())]),
    max_media_size_bytes: z.union([z.number(), z.null()]),
    max_collection_size_bytes: z.union([z.number(), z.null()]),
    can_delete_media: z.boolean(),
    dimensions: z.union([
        z
            .object({
                width: z.union([z.number(), z.null()]),
                height: z.union([z.number(), z.null()]),
                min_height: z.union([z.number(), z.null()]),
                min_width: z.union([z.number(), z.null()]),
                max_height: z.union([z.number(), z.null()]),
                max_width: z.union([z.number(), z.null()]),
            })
            .partial(),
        z.null(),
    ]),
});
export const MediaStorage = z.object({
    id: Id.int(),
    parent_media_storage_id: NullableId,
    storage_type: MediaStorageType,
    owner_id: Id.int(),
    owner_type: Morphs,
    created_at: DateTime,
    updated_at: DateTime,
    deleted_at: NullableDateTime,
    media: z.array(Media).optional(),
    media_collections: z.array(MediaCollection).optional(),
});
export const Signature = z.object({
    id: Id.int(),
    company_id: Id.int(),
    company_branch_id: NullableId.optional(),
    signable_id: Id.int(),
    signable_type: Morphs,
    company_name: z.union([z.string(), z.null()]).optional(),
    created_at: DateTime,
    updated_at: DateTime,
    signed_at: NullableDateTime.optional(),
    signee_name: z.union([z.string(), z.null()]).optional(),
    signee_ip: z.union([z.string(), z.null()]).optional(),
    signee_email: z.union([z.string(), z.null()]).optional(),
    signee_id: NullableId.optional(),
    meta: z.union([z.object({ signee_agree_texts: z.array(z.string()) }).partial(), z.null()]).optional(),
    terms: z.union([z.null(), z.array(z.string())]).optional(),
    media: z.array(Media).optional(),
});
export const ServiceStepMediaLink = z.object({
    id: Id.int(),
    media: z
        .union([
            z.null(),
            z
                .object({
                    id: Id.int(),
                    name: z.string(),
                    file_name: z.string(),
                    mime_type: z.union([z.string(), z.null()]),
                    size: z.number().int(),
                    url: z.string(),
                })
                .partial(),
        ])
        .optional(),
    created_at: z.union([z.string(), z.null()]).optional(),
});
export const ServiceStep = z.object({
    id: Id.int(),
    service_id: Id.int(),
    service_template_step_id: z.union([z.number(), z.null()]).optional(),
    name: z.string().max(255),
    description: z.union([z.string(), z.null()]).optional(),
    config: ServiceStepConfig,
    state: z.record(z.any()),
    order: z.number().int(),
    status: z.enum(['pending', 'in_progress', 'completed', 'cancelled']),
    started_at: z.union([z.string(), z.null()]).optional(),
    completed_at: z.union([z.string(), z.null()]).optional(),
    result: z.union([z.object({}).partial(), z.null()]).optional(),
    created_at: DateTime,
    updated_at: DateTime,
    signatures: z.array(Signature).optional(),
    media_links: z.array(ServiceStepMediaLink).optional(),
});
export const Service = z.object({
    id: Id.int(),
    reference_id: z.string(),
    object: Morphs,
    student_id: Id.int(),
    student_company_id: Id.int(),
    agency_company_id: Id.int(),
    agency_branch_id: Id.int().optional(),
    service_template_id: NullableId.optional(),
    media_storage_id: Id.int(),
    conversation_id: Id.int(),
    name: z.string().max(255),
    description: z.union([z.string(), z.null()]).optional(),
    created_at: DateTime,
    updated_at: DateTime,
    student: MinimalStudent.optional(),
    media_storage: MediaStorage.optional(),
    student_company: MinimalCompany.optional(),
    agency_company: MinimalCompany.optional(),
    agency_branch: MinimalCompanyBranch.optional(),
    steps: z.array(ServiceStep).optional(),
});
export const ServiceCreate = z.object({
    student_id: Id.int(),
    student_company_id: Id.int(),
    service_template_id: Id.int(),
    name: z.string().max(255),
    description: z.union([z.string(), z.null()]).optional(),
});
export const ServiceStepConfigUpdate = z.object({ config: ServiceStepConfig });
export const ServiceCreateFromTemplate = z.object({
    student_id: Id.int(),
    name: z.string().max(255),
    description: z.union([z.string(), z.null()]).optional(),
    steps: z.array(ServiceStepConfigUpdate).optional(),
});
export const ServiceStats = z.object({
    total_services: z.number().int(),
    pending_services: z.number().int(),
    in_progress_services: z.number().int(),
    completed_services: z.number().int(),
});
export const ServiceUpdate = z
    .object({ name: z.string().max(255), description: z.union([z.string(), z.null()]) })
    .partial();
export const ServiceStepSummary = z.object({
    id: Id.int(),
    name: z.string(),
    type: z.string(),
    status: z.enum(['created', 'pending', 'in_progress', 'pending_approval', 'completed', 'skipped', 'cancelled']),
    order: z.number().int(),
    can_complete: z.boolean(),
    warnings: z.array(z.string()),
    started_at: z.union([z.string(), z.null()]),
    completed_at: z.union([z.string(), z.null()]),
});
export const ServiceProgress = z.object({
    progress_percentage: z.number(),
    is_completed: z.boolean(),
    total_steps: z.number().int(),
    completed_steps: z.number().int(),
    steps: z.array(ServiceStepSummary),
});
export const createGeneralInvoice_Body = z.object({
    items: z
        .array(
            z.object({
                description: z.string().max(500),
                amount: z.number().gte(0.01),
                gst_percentage: z.number().gte(0).lte(100),
            })
        )
        .min(1),
    due_date: z.string(),
    currency: z.string().min(3).max(3).optional(),
    note: z.string().max(2000).nullish(),
});
export const GeneralInvoiceCreatedResponse = z.object({
    invoice_id: z.number().int(),
    invoice_url: z.string(),
    smart_event_id: z.number().int(),
    payment_plan_id: z.number().int(),
    total_amount: z.string(),
    currency: z.string(),
});
export const ServiceStepCreate = z.object({
    service_id: Id.int(),
    service_template_step_id: z.union([z.number(), z.null()]).optional(),
    name: z.string().max(255),
    description: z.union([z.string(), z.null()]).optional(),
    config: ServiceStepConfig,
    order: z.number().int().optional(),
    status: z.enum(['pending', 'in_progress', 'completed', 'cancelled']).optional(),
});
export const ServiceStepUpdate = z
    .object({
        name: z.string().max(255),
        description: z.union([z.string(), z.null()]),
        config: ServiceStepConfig,
        state: z.record(z.any()),
        order: z.number().int(),
        status: z.enum(['pending', 'in_progress', 'completed', 'cancelled']),
        started_at: z.union([z.string(), z.null()]),
        completed_at: z.union([z.string(), z.null()]),
        result: z.union([z.object({}).partial(), z.null()]),
    })
    .partial();
export const ServiceStepStateUpdate = z.object({
    state: z.object({}).partial(),
    validate_completion: z.boolean().optional().default(false),
});
export const ServiceStepValidationStatus = z.object({
    can_complete: z.boolean(),
    validation_errors: z.object({}).partial(),
    step_status: z.enum(['pending', 'in_progress', 'completed', 'cancelled']),
});
export const ServiceStepComplete = z.object({ result: z.union([z.object({}).partial(), z.null()]) }).partial();
export const PaymentAttemptStatus = z.enum([
    'pending',
    'fulfilled',
    'available',
    'distributed',
    'refunded',
    'cancelled',
]);
export const PaymentAttempt = z.object({
    id: Id.int(),
    payment_plan_id: Id.int(),
    status: PaymentAttemptStatus,
    remarks: z.string(),
    amount: Decimal,
    fulfilled_at: NullableDateTime,
    available_at: NullableDateTime,
    distributed_at: NullableDateTime,
    refunded_at: NullableDateTime,
    stripe_payment_intent_id: z.union([z.string(), z.null()]).optional(),
    client_secret: z.union([z.string(), z.null()]).optional(),
    linked_amount: Decimal.optional(),
    last_polled_at: NullableDateTime.optional(),
    is_automatic: z.boolean().optional(),
    requires_user_input: z.boolean().optional(),
    initiated_by_user_id: NullableId.optional(),
    can: z.object({ cancel: z.boolean(), pay: z.boolean() }).partial().optional(),
    created_at: DateTime,
    updated_at: DateTime,
    invoice_urls: z.array(z.string()).optional(),
});
export const logServiceStepManualPayment_Body = z.object({
    amount: z.number().gte(0.01),
    payment_method: z.string().max(255),
    payment_reference: z.union([z.string(), z.null()]).optional(),
    notes: z.union([z.string(), z.null()]).optional(),
});
export const reuseStepDocument_Body = z.object({
    document_schema_id: Id.int(),
    source_step_id: Id.int(),
    upload_index: z.number().int().gte(0),
});
export const updateServiceStepConfig_Body = z.object({
    payment_amount: z.number().gte(0),
    currency: z.string().min(3).max(3).optional(),
    pay_later_enabled: z.boolean().optional(),
    deadline_needed: z.boolean().optional(),
    deadline_in_days: z.number().int().gte(1).optional(),
    payment_message_needed: z.boolean().optional(),
    payment_message: z.union([z.string(), z.null()]).optional(),
});
export const QuotationDocumentPreview = z.object({
    document_id: z.number().int(),
    name: z.string(),
    resolved: z.record(z.string()),
    manual: z.array(z.string()),
    missing: z.array(z.object({ key: z.string(), label: z.string() })),
});
export const sendQuotation_Body = z.object({ manual_values: z.record(z.record(z.string())) }).partial();
export const createQualificationLevel_Body = z.object({ title: title, description: description, enabled: enabled });
export const updateQualificationLevel_Body = z
    .object({ title: title, description: description, enabled: enabled })
    .partial();
export const parent_id = z.union([z.number(), z.null()]).optional();
export const MinimalSearchTypes = z.enum([
    'program',
    'school',
    'agency',
    'campus',
    'agency_branch',
    'student',
    'user',
    'company',
]);
export const MinimalSearchItem = z.object({
    id: Id.int(),
    type: MinimalSearchTypes,
    name: z.string(),
    description: z.union([z.string(), z.null()]),
    image_url: z.union([z.string(), z.null()]),
    parents: z.array(z.object({ id: Id.int(), type: Morphs, name: z.string() })),
    descriptors: z.union([z.object({}).partial().passthrough(), z.null()]),
});
export const CountryCodeIso2 = z.string();
export const AddressBody = z.object({
    remarks: z.union([z.string(), z.null()]).optional(),
    address_type: z.union([z.null(), z.enum(['residence', 'work', 'business', 'postal_box', 'other'])]).optional(),
    address_purpose: z
        .union([z.null(), z.enum(['permanent', 'postal', 'billing', 'shipping', 'invoicing', 'other'])])
        .optional(),
    default: z.boolean().optional(),
    line_one: z.string().min(1).max(500),
    line_two: z.union([z.string(), z.null()]).optional(),
    line_three: z.union([z.string(), z.null()]).optional(),
    city: z.string(),
    principal_subdivision: z.string(),
    postal_code: z.string(),
    country: CountryCodeIso2.min(2).max(2),
    lat: z.union([z.string(), z.number(), z.null()]).optional(),
    lng: z.union([z.string(), z.number(), z.null()]).optional(),
    gmaps_place_id: z.union([z.string(), z.null()]).optional(),
    media: z.array(Media).optional(),
});
export const Address = AddressBody.and(
    z.object({
        id: Id.int(),
        formatted_address: z.string().optional(),
        addressable_id: Id.int(),
        addressable_type: Morphs,
    })
);
export const PrettySearchItem = z.object({
    id: Id.int(),
    type: Morphs,
    name: z.string(),
    description: z.union([z.string(), z.null()]),
    image_url: z.union([z.string(), z.null()]),
    stats: z.union([z.object({}).partial().passthrough(), z.null()]).optional(),
    addresses: z.array(Address).optional(),
    related: z.array(z.object({ id: Id.int(), type: Morphs, name: z.string() })).optional(),
});
export const filter_assessment_level_gt_ = z.union([z.unknown(), z.string(), z.number()]).optional();
export const filter_assessment_level_between_ = z
    .array(z.union([z.string(), z.number()]))
    .min(2)
    .max(2)
    .optional();
export const lat = z.union([z.string(), z.number()]).optional();
export const NullableDecimal = coercions.nullableDecimal();
export const ExternalPresence = z
    .object({
        website_url: z.union([z.string(), z.null()]),
        contact_phone: z.union([z.string(), z.null()]),
        contact_email: z.union([z.string(), z.null()]),
        whatsapp_business_id: z.union([z.string(), z.null()]),
        zoom_id: z.union([z.string(), z.null()]),
        facebook_page_name: z.union([z.string(), z.null()]),
        instagram_handle: z.union([z.string(), z.null()]),
        twitter_handle: z.union([z.string(), z.null()]),
        linkedin_url: z.union([z.string(), z.null()]),
        youtube_channel_name: z.union([z.string(), z.null()]),
    })
    .partial();
export const AgencyFactoids = z
    .object({ students_recruited: z.union([z.number(), z.null()]), primary_country: z.union([z.string(), z.null()]) })
    .partial();
export const CampusFactoids = z
    .object({
        disciplines: z.union([z.string(), z.null()]),
        features: z.union([
            z.null(),
            z.array(
                z.enum([
                    'post_graduation_work_permit',
                    'work_while_studying',
                    'conditional_offer_letter',
                    'no_co_op_or_internship_participation',
                    'accommodations',
                ])
            ),
        ]),
        education_levels: z.union([
            z.null(),
            z.array(
                z.enum([
                    'grade 11',
                    'grade 12',
                    '3_year_bachelors_degree',
                    '4_year_bachelors_degree',
                    'postgraduate_certificate_/_diploma',
                    'masters_degree',
                    'doctoral_degree_(phd)',
                    'micro-credentials',
                ])
            ),
        ]),
        founded: z.union([z.number(), z.null()]),
        total_students: z.union([z.number(), z.null()]),
        international_students: z.union([z.number(), z.null()]),
    })
    .partial();
export const PartnerPublic = z.object({
    id: Id.int(),
    type: AccountType,
    name: z.string(),
    address: z.union([z.string(), z.null()]),
    logo_url: z.union([z.string(), z.null()]),
    website_url: z.union([z.string(), z.null()]),
});
export const EmailCategoryConfig = z.object({
    accounts: z.union([z.string(), z.null()]),
    platform: z.union([z.string(), z.null()]),
    misc: z.union([z.string(), z.null()]),
    admissions: z.union([z.string(), z.null()]),
});
export const WorkReference = z.object({ provider: z.string(), name: z.string(), email: z.string(), phone: z.string() });
export const CompanyMetadata = z.object({ references: z.union([z.null(), z.array(WorkReference)]) }).partial();
export const SignupReferral = z.object({
    id: Id.int(),
    company_branch_id: Id.int(),
    user_id: NullableId,
    enabled: z.boolean(),
    referral_url: z.string().optional(),
    download_url: z.string().optional(),
    branch_name: z.string().optional(),
    platform: z.union([z.string(), z.null()]).optional(),
    created_at: DateTime,
    updated_at: DateTime,
});
export const Agency = z.lazy(() =>
    PublicPage.and(
        z.object({
            mara_number: z.string().max(20).nullish(),
            signup_referral: z.union([z.null(), SignupReferral]).optional(),
            created_at: DateTime,
            updated_at: DateTime,
            can: z
                .object({ update: z.union([z.boolean(), z.string()]) })
                .partial()
                .optional(),
        })
    )
);
export const Contact = z.object({
    name: z.string(),
    email: z.string().email(),
    phone: z.union([z.string(), z.null()]),
});
export const AccountStatus = z.enum(['onboarding', 'rejected', 'active', 'suspended']);
export const UserPublic = z.object({
    id: Id.int(),
    company_id: Id.int(),
    company_branch_id: Id.int(),
    status: AccountStatus,
    account_type: AccountType,
    company_reference_id: z.union([z.string(), z.null()]).optional(),
    is_online: z.boolean().optional(),
    avatar_url: z.union([z.string(), z.null()]),
    given_name: z.string().max(100),
    family_name: z.string().max(100),
    additional_name: z.union([z.string(), z.null()]).optional(),
    honorific_prefix: z.union([z.string(), z.null()]).optional(),
    honorific_suffix: z.union([z.string(), z.null()]).optional(),
    full_name: z.string(),
    nickname: z.union([z.string(), z.null()]).optional(),
    created_at: DateTime,
    updated_at: DateTime,
});
export const Handler = z.lazy(() =>
    z.object({
        id: Id.int(),
        user_id: Id.int(),
        handleable_id: Id.int(),
        handleable_type: Morphs,
        deleted_at: z.union([z.string(), z.null()]).optional(),
        data: z.union([z.object({}).partial(), z.null()]).optional(),
        created_at: DateTime,
        updated_at: DateTime,
        user: UserPublic.optional(),
        company: Company.optional(),
    })
);
export const Company = z.lazy(() =>
    z.object({
        id: Id.int(),
        reference_id: z.union([z.string(), z.null()]).optional(),
        company_type: AccountType,
        superadmin_id: Id.int().optional(),
        meta: CompanyMetadata.optional(),
        display_name: z.string(),
        is_onboarded: z.boolean().optional(),
        assessment_level: z.union([z.number(), z.null()]).optional(),
        business_identification: z.union([z.string(), z.null()]),
        business_identification_verification_status: z.enum(['unverified', 'verified', 'failed']).optional(),
        business_identification_verification_remarks: z.union([z.string(), z.null()]).optional(),
        business_name: z.union([z.string(), z.null()]).optional(),
        trading_name: z.string().optional(),
        legal_name: z.union([z.string(), z.null()]).optional(),
        business_type_code: z.union([z.string(), z.null()]).optional(),
        business_active: z.boolean().optional(),
        gst_registered_from: NullableDateTime.optional(),
        gst_registered_to: NullableDateTime.optional(),
        address_principal_subdivision: z.union([z.string(), z.null()]).optional(),
        address_postal_code: z.union([z.string(), z.null()]).optional(),
        company_logo_url: z.union([z.string(), z.null()]).optional(),
        cricos_number: z.union([z.string(), z.null()]),
        rto_number: z.union([z.string(), z.null()]),
        cricos_number_verified_at: z.union([z.string(), z.null()]).optional(),
        rto_number_verified_at: z.union([z.string(), z.null()]).optional(),
        school_type: z.union([z.enum(['college', 'university']), z.null()]),
        stripe_account_id: z.union([z.string(), z.null()]).optional(),
        stripe_create_failed_reason: z.union([z.string(), z.null()]).optional(),
        stripe_details_submitted: z.boolean().optional(),
        stripe_charges_enabled: z.boolean().optional(),
        stripe_tos_accepted: z.boolean().optional(),
        staff_referral_id: z.union([z.string(), z.null()]).optional(),
        accepting_association_requests: z.boolean().optional(),
        bank_details_locked_since: NullableDateTime.optional(),
        applications_count: z.union([z.number(), z.null()]).optional(),
        created_at: DateTime,
        updated_at: DateTime,
        company_branches: z.array(CompanyBranch).optional(),
        campuses: z.array(Campus).optional(),
        agencies: z.array(Agency).optional(),
        addresses: z.array(Address).optional(),
        media: z.array(Media).optional(),
        media_collections: z.array(MediaCollection).optional(),
        root_address: z.union([z.null(), Address]).optional(),
        contact: z.union([z.null(), Contact]).optional(),
        handlers: z.array(Handler).optional(),
    })
);
export const CompanyBranch = z.lazy(() =>
    z.object({
        id: Id.int(),
        company_id: Id.int(),
        company_type: AccountType,
        parent_branch_id: NullableId,
        status: CompanyBranchStatus,
        status_locked: z.boolean(),
        name: z.string(),
        email_category_config: EmailCategoryConfig.optional(),
        created_at: DateTime,
        updated_at: DateTime,
        is_authorized: z.boolean().optional(),
        display_name: z.union([z.string(), z.null()]).optional(),
        company: Company.optional(),
        addresses: z.array(Address).optional(),
        managed_companies: z.array(Company).optional(),
        parent_branch: CompanyBranch.optional(),
        agency: z.union([z.null(), Agency]).optional(),
        campus: z.union([z.null(), Campus]).optional(),
    })
);
export const PublicPage = z.lazy(() =>
    z.object({
        id: Id.int(),
        visible: z.boolean().optional(),
        name: z.string(),
        display_name: z.string().optional(),
        distance: NullableDecimal.optional(),
        company_type: AccountType,
        company_id: Id.int(),
        company_branch_id: Id.int(),
        about: z.string(),
        external: ExternalPresence,
        agency_factoids: AgencyFactoids.optional(),
        campus_factoids: CampusFactoids.optional(),
        media: z.array(Media).optional(),
        media_collections: z.array(MediaCollection).optional(),
        partnered: z.boolean().optional(),
        partners: z.array(PartnerPublic).optional(),
        address: z.union([Address, z.null()]).optional(),
        company_branch: z.union([CompanyBranch, z.null()]).optional(),
        logo_url: z.union([z.string(), z.null()]).optional(),
        branches: z
            .union([
                z.null(),
                z.array(z.object({ id: Id.int(), name: z.string(), address: z.union([Address, z.null()]) }).partial()),
            ])
            .optional(),
    })
);
export const Campus = z.lazy(() =>
    PublicPage.and(
        z.object({
            assessment_level: z.union([z.number(), z.null()]).optional(),
            company_name: z.string().optional(),
            created_at: DateTime,
            updated_at: DateTime,
            can: z
                .object({ update: z.union([z.boolean(), z.string()]) })
                .partial()
                .optional(),
        })
    )
);
export const FeesScheduleType = z.enum([
    'regular_intervals',
    'fixed_number_of_payments',
    'interval_with_number_of_payments',
]);
export const RegularIntervalUnit = z.enum(['days', 'weeks', 'months', 'years']);
export const FeesSchedule = z.object({
    duration_weeks: z.number().int(),
    schedule_type: FeesScheduleType,
    regular_interval_width: z.union([z.number(), z.null()]).default(1),
    regular_interval_unit: z.union([z.null(), RegularIntervalUnit]),
    number_of_payments: z.union([z.number(), z.null()]),
});
export const FeesStructure = z.object({
    application_fees: Decimal,
    material_fees: Decimal,
    tuition_fees: Decimal,
    commission_bonus: Decimal,
    discount: Decimal,
    advance_fees: Decimal,
    fees_currency: CurrencyCode.min(3).max(3),
});
export const Month = z.number();
export const MethodOfDelivery = z.enum([
    'face_to_face',
    'instructor_led',
    'virtual_classroom',
    'self_paced_online',
    'blended_learning',
]);
export const Package = z.lazy(() =>
    z.object({
        id: Id.int(),
        company_id: Id.int(),
        created_at: DateTime,
        updated_at: DateTime,
        enabled: z.boolean(),
        name: z.string(),
        tuition_fees: z.union([z.number(), z.null()]).optional(),
        duration_weeks: z.union([z.number(), z.null()]).optional(),
        material_fees: z.union([z.number(), z.null()]).optional(),
        application_fees: z.union([z.number(), z.null()]).optional(),
        logo_url: z.union([z.string(), z.null()]).optional(),
        partnered: z.boolean().optional(),
        company_name: z.string().optional(),
        assessment_level: z.union([z.number(), z.null()]).optional(),
        packaged_programs: z.array(PackagedProgram).optional(),
        campuses: z.array(Campus).optional(),
    })
);
export const PackagedProgram = z.lazy(() =>
    FeesSchedule.and(FeesStructure).and(
        z.object({
            id: Id.int(),
            program_id: Id.int(),
            package_id: Id.int(),
            name: z.string(),
            order: z.number().int(),
            created_at: DateTime,
            updated_at: DateTime,
            program: z.union([z.null(), Program]).optional(),
            package: z.union([z.null(), Package]).optional(),
        })
    )
);
export const Program = z.lazy(() =>
    FeesSchedule.and(FeesStructure).and(
        z.object({
            id: Id.int(),
            program_level_id: Id.int(),
            company_id: Id.int(),
            enabled: z.boolean(),
            created_at: DateTime,
            updated_at: DateTime,
            name: z.string(),
            distance: NullableDecimal.optional(),
            about: z.string(),
            requirements: z.string(),
            national_course_code: z.union([z.string(), z.null()]).optional(),
            cricos_course_code: z.union([z.string(), z.null()]).optional(),
            intakes: z.array(Month),
            method_of_delivery: MethodOfDelivery,
            partnered: z.boolean().optional(),
            campuses: z.array(Campus).optional(),
            media: z.array(Media).optional(),
            media_collections: z.array(MediaCollection).optional(),
            program_level: z.union([z.null(), ProgramLevel]).optional(),
            packaged_programs: z.array(PackagedProgram).optional(),
            assessment_level: z.union([z.number(), z.null()]).optional(),
            company_name: z.string().optional(),
            logo_url: z.union([z.string(), z.null()]).optional(),
            applications_count: z.union([z.number(), z.null()]).optional(),
        })
    )
);
export const Gender = z.enum(['male', 'female', 'other']);
export const NullableDate = coercions.nullableDate();
export const Permission = z.object({
    id: Id.int(),
    name: z.string(),
    code: z.union([z.string(), z.null()]),
    description: z.union([z.string(), z.null()]),
    category: z.string(),
    company_types: z.array(AccountType),
});
export const Role = z.object({
    id: Id.int(),
    name: z.string(),
    label: z.string(),
    rank: z.number().int(),
    deletable: z.boolean(),
    modifiable: z.boolean(),
    permissions: z.array(Permission),
});
export const User = UserPublic.and(
    z.object({
        media_storage_id: NullableId.optional(),
        gender: z.union([Gender, z.null()]).optional(),
        password_changed_at: NullableDateTime.optional(),
        parent_user_id: NullableId.optional(),
        mara_number: z.union([z.string(), z.null()]).optional(),
        assists_user_id: NullableId.optional(),
        finished_onboarding: z.boolean().optional(),
        date_of_birth: NullableDate.optional(),
        email: z.string().email(),
        email_verified_at: NullableDateTime.optional(),
        phone_primary: z.union([z.string(), z.null()]).optional(),
        phone_primary_verified_at: NullableDateTime.optional(),
        phone_secondary: z.union([z.string(), z.null()]).optional(),
        phone_secondary_verified_at: NullableDateTime.optional(),
        phone_alternate: z.union([z.string(), z.null()]).optional(),
        phone_alternate_name: z.union([z.string(), z.null()]).optional(),
        addresses: z.array(Address).optional(),
        media: z.array(Media).optional(),
        applications_count: z.number().int().optional(),
        role: Role.optional(),
        media_storage: MediaStorage.optional(),
    })
);
export const given_name = z.string();
export const additional_name = z.union([z.string(), z.null()]);
export const family_name = z.string();
export const updateUser_Body = z
    .object({
        gender: z.union([Gender, z.null()]),
        date_of_birth: NullableDate,
        phone_primary: z.union([z.string(), z.null()]),
        phone_secondary: z.union([z.string(), z.null()]),
        given_name: given_name.max(100),
        additional_name: additional_name,
        family_name: family_name.max(100),
        mara_number: z.union([z.string(), z.null()]),
    })
    .partial();
export const createSubAgent_Body = z.object({
    email: z.string().email(),
    given_name: z.string(),
    family_name: z.string(),
    additional_name: z.union([z.string(), z.null()]).optional(),
    business_name: z.union([z.string(), z.null()]).optional(),
    sub_agent_commission_percentage: z.union([z.number(), z.null()]).optional(),
    commission_visible: z.union([z.boolean(), z.null()]).optional(),
    mara_number: z.union([z.string(), z.null()]).optional(),
});
export const updateSubAgent_Body = z
    .object({
        business_name: z.union([z.string(), z.null()]),
        sub_agent_commission_percentage: z.union([z.number(), z.null()]),
        commission_visible: z.union([z.boolean(), z.null()]),
        mara_number: z.union([z.string(), z.null()]),
    })
    .partial();
export const changePassword_Body = z.object({
    old_password: z.string(),
    new_password: z.string(),
    new_password_confirmation: z.string(),
});
export const business_identification = z.union([z.string(), z.null()]);
export const cricos_number = z.union([z.string(), z.null()]);
export const trading_name = z.string();
export const rto_number = z.union([z.string(), z.null()]);
export const accepting_association_requests = z.boolean();
export const assessment_level = z.union([z.number(), z.null()]);
export const updateCompany_Body = z
    .object({
        business_identification: business_identification,
        cricos_number: cricos_number,
        trading_name: trading_name,
        rto_number: rto_number,
        accepting_association_requests: accepting_association_requests,
        assessment_level: assessment_level,
    })
    .partial();
export const assignHandler_Body = z.object({ handler_id: Id.int(), replace_handler_id: NullableId.optional() });
export const createCompanyBranch_Body = z.object({ name: z.string(), address: AddressBody });
export const email_category_config = EmailCategoryConfig;
export const status = CompanyBranchStatus;
export const updateCompanyBranch_Body = z
    .object({ email_category_config: email_category_config, status: status })
    .partial();
export const updateSignupReferralPlatform_Body = z.object({ platform: z.union([z.string(), z.null()]) }).partial();
export const createCompanyBranchUser_Body = z.object({
    email: z.string(),
    family_name: z.string(),
    given_name: z.string(),
    mara_number: z.union([z.string(), z.null()]).optional(),
    assists_user_id: NullableId.optional(),
});
export const BankDetail = z.object({
    id: Id.int(),
    account_name: z.string(),
    account_number: z.string(),
    account_bsb: z.string(),
    payment_plus_id: z.union([z.string(), z.null()]).optional(),
    verified_at: NullableDateTime.optional(),
    created_at: DateTime,
    updated_at: DateTime,
    deleted_at: NullableDateTime.optional(),
});
export const account_name = z.string();
export const account_number = z.string();
export const account_bsb = z.string();
export const payment_plus_id = z.union([z.string(), z.null()]);
export const createBankDetails_Body = z.object({
    account_name: account_name,
    account_number: account_number,
    account_bsb: account_bsb,
    payment_plus_id: payment_plus_id.optional(),
});
export const createRole_Body = z.object({ name: z.string(), rank: z.number().int().optional() });
export const syncPermissionsOfRole_Body = z.object({ permissions: z.array(Id) });
export const permissionId = z.union([Id, z.string()]);
export const LanguageCodeIso2 = z.string();
export const Language = z.object({ alpha3b: z.string(), alpha2: LanguageCodeIso2.min(2).max(2), name: z.string() });
export const Currency = z.object({
    code: CurrencyCode.min(3).max(3),
    name: z.string(),
    minor_unit: z.number().int(),
    payments_enabled: z.boolean(),
});
export const name__2 = z.union([z.string(), z.null()]).optional();
export const CountryCodeIso3 = z.string();
export const PrincipalSubdivisionCode = z.string();
export const PrincipalSubdivision = z.object({
    subdivision_code: PrincipalSubdivisionCode,
    name: z.string(),
    country_code: CountryCodeIso2.min(2).max(2),
    country_name: z.string(),
    lat: NullableDecimal,
    lng: NullableDecimal,
});
export const Country = z.object({
    name: z.string(),
    iso3: CountryCodeIso3.optional(),
    iso2: CountryCodeIso2.min(2).max(2),
    phone_code: z.union([z.string(), z.null()]),
    emoji: z.union([z.string(), z.null()]),
    currency_code: z.union([CurrencyCode, z.null()]),
    currency_symbol: z.union([z.string(), z.null()]),
    currency_name: z.union([z.string(), z.null()]),
    principal_subdivisions: z.array(PrincipalSubdivision).optional(),
});
export const City = z.object({
    name: z.string(),
    country_code: CountryCodeIso2.min(2).max(2),
    country_name: z.string(),
    principal_subdivision_code: PrincipalSubdivisionCode,
    principal_subdivision_name: z.string(),
    lat: NullableDecimal,
    lng: NullableDecimal,
});
export const PostalCode = z.object({
    id: Id.int(),
    postcode: z.string(),
    name: z.string(),
    lat: NullableDecimal,
    lng: NullableDecimal,
    principal_subdivision_code: PrincipalSubdivisionCode,
    country_code: CountryCodeIso2.min(2).max(2),
});
export const DefinedWebView = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    available: z.boolean(),
});
export const WebViewRequirements = z.object({
    id: z.string(),
    contents: z.array(
        z.object({
            title: z.string(),
            type: z.enum(['text', 'html', 'link']),
            group: z.string(),
            key: z.string(),
            required: z.boolean(),
            min_length: z.union([z.number(), z.null()]),
            max_length: z.union([z.number(), z.null()]),
        })
    ),
    images: z.array(
        z.object({
            title: z.string(),
            group: z.string(),
            key: z.string(),
            required: z.boolean(),
            constraints: z.union([
                z.object({
                    min_width: z.union([z.number(), z.null()]),
                    min_height: z.union([z.number(), z.null()]),
                    max_width: z.union([z.number(), z.null()]),
                    max_height: z.union([z.number(), z.null()]),
                    width: z.union([z.number(), z.null()]),
                    height: z.union([z.number(), z.null()]),
                    allowed_mime_types: z.union([z.array(z.string()), z.null()]),
                    max_size_bytes: z.union([z.number(), z.null()]),
                    min_size_bytes: z.union([z.number(), z.null()]),
                    aspect_ratio: z.union([z.number(), z.null()]),
                }),
                z.null(),
            ]),
        })
    ),
});
export const WebView = DefinedWebView.and(
    z.object({
        updated_at: DateTime,
        updated_by_id: NullableId,
        updated_by: z.union([UserPublic, z.null()]).optional(),
        requirements: WebViewRequirements.optional(),
        contents: z.array(z.object({ type: z.enum(['text', 'html', 'link']), key: z.string(), value: z.string() })),
        images: z.array(z.object({ alt: z.union([z.string(), z.null()]), key: z.string(), src: z.string().url() })),
    })
);
export const updateWebView_Body = z
    .object({
        contents: z.record(z.union([z.string(), z.null()])),
        images: z.record(z.union([z.instanceof(File), z.null()])),
    })
    .partial();
export const login_Body = z.object({
    email: z.string().email(),
    password: z.string(),
    app_type: z.union([z.string(), z.null()]).optional(),
});
export const registerStudent_Body = z.object({
    signup_referral_id: z.union([z.number(), z.null()]).optional(),
    signup_referral_secret: z.union([z.string(), z.null()]).optional(),
    accept_terms: z.boolean().optional(),
    email: z.string().email(),
    phone: z.string(),
    family_name: family_name.max(100),
    given_name: given_name.max(100),
    additional_name: additional_name.optional(),
});
export const registerAgency_Body = z.object({
    email: z.string().email(),
    phone: z.string(),
    trading_name: trading_name,
    business_identification: business_identification,
    family_name: family_name.max(100),
    given_name: given_name.max(100),
    additional_name: additional_name.optional(),
    referral_code: z.union([z.string(), z.null()]).optional(),
});
export const registerSchool_Body = z.object({
    email: z.string().email(),
    phone: z.string(),
    trading_name: trading_name,
    business_identification: business_identification,
    family_name: family_name.max(100),
    given_name: given_name.max(100),
    additional_name: additional_name.optional(),
    cricos_number: z.string(),
    rto_number: z.string(),
    referral_code: z.union([z.string(), z.null()]).optional(),
});
export const resetPassword_Body = z.object({
    token: z.string(),
    password: z.string(),
    password_confirmation: z.string(),
});
export const id = z.union([z.string(), z.number()]);
export const AgreementType = z.enum(['commission', 'site_usage', 'service_advice', 'service_visa', 'service_appeal']);
export const AgreementStatus = z.enum(['created', 'signed', 'terminated', 'archived']);
export const ProgramCommission = z.object({
    id: Id.int(),
    program_id: Id.int(),
    agreement_id: Id.int(),
    commission_percentage: Decimal,
    created_at: DateTime,
    updated_at: DateTime,
    program: Program.optional(),
});
export const AgreementModification = z.object({
    id: Id.int(),
    created_at: DateTime,
    updated_at: DateTime,
    agreement_id: Id.int(),
    reason: z.string(),
    start: DateTime,
    end: DateTime,
    commission_percentage: Decimal,
    invalidation_reason: z.union([z.string(), z.null()]).optional(),
    invalidated: z.boolean().optional(),
});
export const Agreement = z.object({
    id: Id.int(),
    name: z.string().optional(),
    can_be_rejected: z.boolean(),
    agreement_type: AgreementType,
    valid_upto: NullableDate,
    sender_terminated: z.boolean().optional(),
    receiver_terminated: z.boolean().optional(),
    sender_company_id: Id.int(),
    receiver_company_id: Id.int(),
    contents: z.union([z.string(), z.null()]),
    commission_percentage: Decimal,
    status: AgreementStatus,
    status_changed_at: NullableDateTime.optional(),
    meta: z.union([z.object({ pdf_error: z.union([z.string(), z.null()]) }).partial(), z.null()]).optional(),
    offer_days: z.union([z.number(), z.null()]).optional(),
    offer_percentage: z.union([z.string(), z.number(), z.null()]).optional(),
    offer_name: z.union([z.string(), z.null()]).optional(),
    created_at: DateTime,
    updated_at: DateTime,
    sender_company: Company.optional(),
    receiver_company: Company.optional(),
    media: z.array(Media).optional(),
    signatures: z.array(Signature).optional(),
    program_commissions: z.array(ProgramCommission).optional(),
    agreement_modifications: z.array(AgreementModification).optional(),
});
export const OnboardingProgress = z.object({
    conversation_id: NullableId,
    address: z.union([Address, z.null()]),
    company: Company,
    user: User,
    agreements: z.array(Agreement),
    dependent: z.union([z.null(), Agency, Campus]),
});
export const makeOnboardingProgress_Body = z
    .object({
        business_identification: z.string(),
        cricos_number: z.union([z.string(), z.null()]),
        rto_number: z.union([z.string(), z.null()]),
        password: z.string(),
        password_confirmation: z.string(),
        external: ExternalPresence,
        about: z.string(),
        logo: z.instanceof(File),
        address: AddressBody,
    })
    .partial();
export const ConversationType = z.enum([
    'support',
    'partner',
    'application',
    'service',
    'inter_company',
    'intra_company',
]);
export const ConversationMeta = z
    .object({ private: z.boolean(), student_id: NullableId, application_id: NullableId })
    .partial();
export const ConversationParticipant = z.object({
    user_id: Id.int(),
    name: z.union([z.string(), z.null()]),
    company_id: Id.int(),
    company_name: z.string().optional(),
    company_branch_id: Id.int(),
    company_type: AccountType,
    is_admin: z.boolean().optional(),
    avatar_url: z.string(),
    conversation_id: Id.int(),
    created_at: DateTime,
    updated_at: DateTime,
    deleted_at: NullableDateTime.optional(),
    notifications_enabled: z.boolean().optional(),
    read_upto_id: NullableId,
    received_upto_id: NullableId,
    is_active: z.boolean().optional(),
    removed_at: NullableDateTime.optional(),
});
export const ConversationAttachment = z.object({
    id: z.number().int(),
    conversation_message_id: z.number().int(),
    display_name: z.string(),
    content_type: z.string(),
    size_bytes: z.number().int().optional(),
    download_url: z.union([z.string(), z.null()]).optional(),
    placeholder_hash: z.union([z.string(), z.null()]),
    created_at: DateTime,
    updated_at: DateTime,
});
export const ConversationMessage = z.lazy(() =>
    z.object({
        id: z.number().int(),
        conversation_id: z.number().int(),
        user_id: z.union([z.number(), z.null()]),
        system: z.boolean(),
        message_body: z.union([z.string(), z.null()]),
        message_special: z
            .union([
                z
                    .object({
                        document_requirement_id: z.union([z.number(), z.null()]),
                        document_requirement_instructions: z.union([z.string(), z.null()]),
                        event_code: z.union([z.string(), z.null()]),
                    })
                    .partial(),
                z.null(),
            ])
            .optional(),
        created_at: DateTime,
        updated_at: DateTime,
        deleted_at: NullableDateTime.optional(),
        attachments: z.array(ConversationAttachment).optional(),
        user: z.union([z.null(), UserPublic]).optional(),
        conversation: z.union([z.null(), Conversation]).optional(),
    })
);
export const MinimalApplication = z.object({
    id: Id.int(),
    reference_id: z.union([z.string(), z.null()]).optional(),
    updated_at: DateTime,
    created_at: DateTime,
    student_id: Id.int(),
    media_storage_id: NullableId,
    application_type: z.enum(['course', 'visa']).optional(),
    applicable_id: NullableId.optional(),
    applicable_type: z.union([z.string(), z.null()]).optional(),
    visa_type: z.union([z.enum(['subclass_500', 'subclass_485', 'subclass_482', null]), z.null()]).optional(),
    target_date: z.union([z.string(), z.null()]).optional(),
    school_company_id: NullableId.optional(),
    agency_company_id: Id.int(),
    staff_company_id: Id.int(),
    school_branch_id: NullableId.optional(),
    agency_branch_id: Id.int(),
    agency_conversation_id: NullableId,
    student_conversation_id: NullableId,
    agency_staff_agreement_id: NullableId.optional(),
    school_staff_agreement_id: NullableId.optional(),
    agency_school_agreement_id: NullableId.optional(),
    intake_year: z.union([z.number(), z.null()]).optional(),
    intake_month: z.union([z.number(), z.null()]).optional(),
    status: z.string(),
    agency_branch: z.union([MinimalCompanyBranch, z.null()]).optional(),
    school_branch: z.union([MinimalCompanyBranch, z.null()]).optional(),
    student: z.union([MinimalStudent, z.null()]).optional(),
});
export const Conversation = z.lazy(() =>
    z.object({
        id: Id.int(),
        media_storage_id: NullableId,
        title: z.string(),
        conversation_type: ConversationType,
        thumbnail_url: z.union([z.string(), z.null()]).optional(),
        meta: z.union([z.null(), ConversationMeta]).optional(),
        closed_at: NullableDateTime.optional(),
        deleted_at: NullableDateTime.optional(),
        created_at: DateTime,
        updated_at: DateTime,
        participants: z.array(ConversationParticipant).optional(),
        recent_messages: z.object({ has_more: z.boolean(), messages: z.array(ConversationMessage) }).optional(),
        my_participant: z.union([z.null(), ConversationParticipant]).optional(),
        latest_message_id: NullableId.optional(),
        company_branches: z.array(MinimalCompanyBranch).optional(),
        companies: z.array(MinimalCompany).optional(),
        student: z.union([z.null(), MinimalStudent]).optional(),
        application: z.union([z.null(), MinimalApplication]).optional(),
    })
);
export const ConversationContact = z.object({
    user_id: Id.int(),
    name: z.string(),
    branch: z.union([MinimalCompanyBranch, z.null()]),
    relevance: z.union([z.number(), z.null()]),
    avatar_url: z.string(),
});
export const addConversationParticipant_Body = z.object({ user_id: Id.int() });
export const IdPaginatedResponse = z.object({ has_more: z.boolean(), limit: z.number().int() });
export const sendConversationMessage_Body = z.union([
    z.object({ attachments: z.array(z.instanceof(File)), message_body: z.string().min(1).max(16000) }),
    z.object({ message_body: z.string().min(1).max(16000) }),
    z.object({ document_requirement_id: NullableId.optional(), attachments: z.array(z.instanceof(File)) }),
]);
export const createIntraCompanyConversation_Body = z.object({
    user_ids: z.array(Id),
    title: z.string(),
    private: z.boolean().optional(),
});
export const MaritalStatus = z.enum([
    'de_facto',
    'divorced',
    'engaged',
    'married',
    'never_married',
    'separated',
    'widowed',
]);
export const StudentPersonalDetails = z.object({
    id: Id.int(),
    documentable_id: Id.int(),
    documentable_type: Morphs,
    updated_at: DateTime,
    created_at: DateTime,
    required: z.array(z.string()),
    given_name: z.union([z.string(), z.null()]).optional(),
    additional_name: z.union([z.string(), z.null()]).optional(),
    family_name: z.union([z.string(), z.null()]).optional(),
    date_of_birth: NullableDate.optional(),
    country_of_citizenship: z.union([CountryCodeIso2, z.null()]).optional(),
    passport_id: z.union([z.string(), z.null()]).optional(),
    passport_expiry_date: NullableDate.optional(),
    passport_issue_date: NullableDate.optional(),
    first_language: z.union([LanguageCodeIso2, z.null()]).optional(),
    country_of_birth: z.union([CountryCodeIso2, z.null()]).optional(),
    place_of_birth: z.union([z.string(), z.null()]).optional(),
    marital_status: z.union([MaritalStatus, z.null()]).optional(),
    gender: z.union([Gender, z.null()]).optional(),
    recognition_of_prior_learning: z.union([z.boolean(), z.null()]).optional(),
    visa_type: z.union([z.string(), z.null()]).optional(),
    visa_grant_number: z.union([z.string(), z.null()]).optional(),
    contact_email: z.union([z.string(), z.null()]).optional(),
    contact_phone: z.union([z.string(), z.null()]).optional(),
    phone_emergency: z.union([z.string(), z.null()]).optional(),
    phone_emergency_name: z.union([z.string(), z.null()]).optional(),
    phone_emergency_relationship: z.union([z.string(), z.null()]).optional(),
});
export const Student = z.object({
    id: Id.int(),
    object: Morphs,
    user_id: Id.int(),
    reference_id: z.union([z.string(), z.null()]).optional(),
    company_id: Id.int(),
    media_storage_id: Id.int(),
    agency_branch_id: Id.int(),
    full_name: z.union([z.string(), z.null()]).optional(),
    created_at: DateTime,
    updated_at: DateTime,
    avatar_url: z.union([z.string(), z.null()]).optional(),
    is_online: z.boolean().optional(),
    user: UserPublic.optional(),
    addresses: z.array(Address).optional(),
    media_storage: MediaStorage.optional(),
    personal_details: StudentPersonalDetails.optional(),
    applications_count: z.number().int().optional(),
    handlers: z.array(Handler).optional(),
    agency_branch: MinimalCompanyBranch.optional(),
    referral_platform: z.union([z.string(), z.null()]).optional(),
});
export const linkAgency_Body = z.object({ signup_referral_id: z.number().int(), signup_referral_secret: z.string() });
export const country_of_citizenship = z.union([CountryCodeIso2, z.null()]);
export const passport_id = z.union([z.string(), z.null()]);
export const passport_expiry_date = NullableDate;
export const passport_issue_date = NullableDate;
export const first_language = z.union([LanguageCodeIso2, z.null()]);
export const country_of_birth = z.union([CountryCodeIso2, z.null()]);
export const place_of_birth = z.union([z.string(), z.null()]);
export const marital_status = z.union([MaritalStatus, z.null()]);
export const gender = z.union([Gender, z.null()]);
export const recognition_of_prior_learning = z.union([z.boolean(), z.null()]);
export const visa_type = z.union([z.string(), z.null()]);
export const visa_grant_number = z.union([z.string(), z.null()]);
export const contact_email = z.union([z.string(), z.null()]);
export const contact_phone = z.union([z.string(), z.null()]);
export const phone_emergency = z.union([z.string(), z.null()]);
export const phone_emergency_name = z.union([z.string(), z.null()]);
export const phone_emergency_relationship = z.union([z.string(), z.null()]);
export const updatePersonalDetails_Body = z
    .object({
        given_name: given_name,
        additional_name: additional_name,
        family_name: family_name,
        date_of_birth: NullableDate,
        country_of_citizenship: country_of_citizenship,
        passport_id: passport_id,
        passport_expiry_date: passport_expiry_date,
        passport_issue_date: passport_issue_date,
        first_language: first_language,
        country_of_birth: country_of_birth,
        place_of_birth: place_of_birth,
        marital_status: marital_status,
        gender: gender,
        recognition_of_prior_learning: recognition_of_prior_learning,
        visa_type: visa_type,
        visa_grant_number: visa_grant_number,
        contact_email: contact_email,
        contact_phone: contact_phone,
        phone_emergency: phone_emergency,
        phone_emergency_name: phone_emergency_name,
        phone_emergency_relationship: phone_emergency_relationship,
    })
    .partial();
export const DateUtc = coercions.date();
export const AttendedInstitution = z.object({
    id: Id.int(),
    documentable_id: Id.int(),
    documentable_type: Morphs,
    hidden: z.boolean().optional(),
    institution_name: z.string(),
    institution_address: z.union([z.string(), z.null()]).optional(),
    institution_country: CountryCodeIso2.min(2).max(2),
    institution_principal_subdivision: PrincipalSubdivisionCode,
    course_name: z.string(),
    course_started_on: DateUtc,
    course_ended_on: NullableDate.optional(),
    areas_of_study: z.union([z.string(), z.null()]).optional(),
    graduation_status: z.enum(['currently_enrolled', 'completed', 'failed']),
    is_highest_qualification: z.boolean(),
    language_of_instruction: z.union([LanguageCodeIso2, z.null()]).optional(),
    qualification_earned: z.union([z.string(), z.null()]).optional(),
    remarks: z.union([z.string(), z.null()]).optional(),
    qualification_level_id: NullableId.optional(),
    education_level: z
        .union([
            z.enum([
                'high_school',
                'diploma',
                'graduate_diploma',
                'bachelors',
                'masters',
                'post_graduate',
                'doctorate',
                'other',
            ]),
            z.null(),
        ])
        .optional(),
    grading_scheme: z.union([z.enum(['gpa', 'marks', 'other']), z.null()]).optional(),
    grade_average: z.union([z.string(), z.null()]).optional(),
    transcript_url: z.union([z.string(), z.null()]).optional(),
    marksheet_url: z.union([z.string(), z.null()]).optional(),
    certificate_url: z.union([z.string(), z.null()]).optional(),
    media_links: z.array(MediaLink).optional(),
    updated_at: DateTime,
    created_at: DateTime,
});
export const hidden = z.boolean();
export const institution_name = z.string();
export const institution_address = z.union([z.string(), z.null()]);
export const institution_country = CountryCodeIso2;
export const institution_principal_subdivision = PrincipalSubdivisionCode;
export const course_name = z.string();
export const course_started_on = DateUtc;
export const course_ended_on = NullableDate;
export const areas_of_study = z.union([z.string(), z.null()]);
export const graduation_status = z.enum(['currently_enrolled', 'completed', 'failed']);
export const is_highest_qualification = z.boolean();
export const language_of_instruction = z.union([LanguageCodeIso2, z.null()]);
export const qualification_earned = z.union([z.string(), z.null()]);
export const remarks = z.union([z.string(), z.null()]);
export const qualification_level_id = NullableId;
export const education_level = z.union([
    z.enum([
        'high_school',
        'diploma',
        'graduate_diploma',
        'bachelors',
        'masters',
        'post_graduate',
        'doctorate',
        'other',
    ]),
    z.null(),
]);
export const grading_scheme = z.union([z.enum(['gpa', 'marks', 'other']), z.null()]);
export const grade_average = z.union([z.string(), z.null()]);
export const media_links = z.array(MediaLink);
export const addAttendedInstitution_Body = z.object({
    hidden: hidden.optional(),
    institution_name: institution_name,
    institution_address: institution_address.optional(),
    institution_country: institution_country,
    institution_principal_subdivision: institution_principal_subdivision,
    course_name: course_name,
    course_started_on: course_started_on,
    course_ended_on: course_ended_on.optional(),
    areas_of_study: areas_of_study.optional(),
    graduation_status: graduation_status,
    is_highest_qualification: is_highest_qualification,
    language_of_instruction: language_of_instruction.optional(),
    qualification_earned: qualification_earned.optional(),
    remarks: remarks.optional(),
    qualification_level_id: qualification_level_id.optional(),
    education_level: education_level.optional(),
    grading_scheme: grading_scheme.optional(),
    grade_average: grade_average.optional(),
    media_links: media_links.optional(),
});
export const transcript_url = z.union([z.string(), z.null()]);
export const marksheet_url = z.union([z.string(), z.null()]);
export const certificate_url = z.union([z.string(), z.null()]);
export const updateAttendedInstitution_Body = z
    .object({
        hidden: hidden,
        institution_name: institution_name,
        institution_address: institution_address,
        institution_country: institution_country,
        institution_principal_subdivision: institution_principal_subdivision,
        course_name: course_name,
        course_started_on: course_started_on,
        course_ended_on: course_ended_on,
        areas_of_study: areas_of_study,
        graduation_status: graduation_status,
        is_highest_qualification: is_highest_qualification,
        language_of_instruction: language_of_instruction,
        qualification_earned: qualification_earned,
        remarks: remarks,
        qualification_level_id: qualification_level_id,
        education_level: education_level,
        grading_scheme: grading_scheme,
        grade_average: grade_average,
        transcript_url: transcript_url,
        marksheet_url: marksheet_url,
        certificate_url: certificate_url,
        media_links: media_links,
    })
    .partial();
export const LanguageTest = z.object({
    id: Id.int(),
    documentable_id: Id.int(),
    documentable_type: Morphs,
    hidden: z.boolean().optional(),
    test_type: z.string(),
    test_unique_id: z.union([z.string(), z.null()]).optional(),
    other_test_name: z.union([z.string(), z.null()]).optional(),
    test_taken_on: NullableDate,
    test_valid_until: NullableDate.optional(),
    reading_score: z.union([z.string(), z.null()]).optional(),
    listening_score: z.union([z.string(), z.null()]).optional(),
    writing_score: z.union([z.string(), z.null()]).optional(),
    speaking_score: z.union([z.string(), z.null()]).optional(),
    overall_score: z.string(),
    created_at: DateTime.optional(),
    updated_at: DateTime.optional(),
    exam_result_url: z.union([z.string(), z.null()]).optional(),
    media_links: z.array(MediaLink).optional(),
});
export const test_type = z.string();
export const test_unique_id = z.union([z.string(), z.null()]);
export const other_test_name = z.union([z.string(), z.null()]);
export const reading_score = z.union([z.string(), z.null()]);
export const listening_score = z.union([z.string(), z.null()]);
export const writing_score = z.union([z.string(), z.null()]);
export const speaking_score = z.union([z.string(), z.null()]);
export const overall_score = z.string();
export const exam_result_url = z.union([z.string(), z.null()]);
export const addLanguageTest_Body = z.object({
    hidden: hidden.optional(),
    test_type: test_type,
    test_unique_id: test_unique_id.optional(),
    other_test_name: other_test_name.optional(),
    test_taken_on: NullableDate,
    reading_score: reading_score.optional(),
    listening_score: listening_score.optional(),
    writing_score: writing_score.optional(),
    speaking_score: speaking_score.optional(),
    overall_score: overall_score,
    exam_result_url: exam_result_url.optional(),
    media_links: media_links.optional(),
});
export const updateLanguageTest_Body = z
    .object({
        hidden: hidden,
        test_type: test_type,
        test_unique_id: test_unique_id,
        other_test_name: other_test_name,
        test_taken_on: NullableDate,
        reading_score: reading_score,
        listening_score: listening_score,
        writing_score: writing_score,
        speaking_score: speaking_score,
        overall_score: overall_score,
        exam_result_url: exam_result_url,
        media_links: media_links,
    })
    .partial();
export const EmploymentRecord = z.object({
    id: Id.int(),
    documentable_id: Id.int(),
    documentable_type: Morphs,
    hidden: z.boolean().optional(),
    employer_name: z.string().max(200),
    employer_address: z.union([z.string(), z.null()]).optional(),
    employer_contact: z.union([z.string(), z.null()]).optional(),
    employment_position: z.string().max(200),
    employment_started_on: DateUtc,
    employment_ended_on: NullableDate.optional(),
    responsibilities: z.union([z.null(), z.array(z.string())]),
    remarks: z.union([z.string(), z.null()]).optional(),
    media: z.array(Media).optional(),
    updated_at: DateTime.optional(),
    created_at: DateTime.optional(),
});
export const employer_name = z.string();
export const employer_address = z.union([z.string(), z.null()]);
export const employer_contact = z.union([z.string(), z.null()]);
export const employment_position = z.string();
export const employment_started_on = DateUtc;
export const employment_ended_on = NullableDate;
export const responsibilities = z.union([z.null(), z.array(z.string())]);
export const media = z.array(Media);
export const addEmploymentRecord_Body = z.object({
    hidden: hidden.optional(),
    employer_name: employer_name.max(200),
    employer_address: employer_address.optional(),
    employer_contact: employer_contact.optional(),
    employment_position: employment_position.max(200),
    employment_started_on: employment_started_on,
    employment_ended_on: employment_ended_on.optional(),
    responsibilities: responsibilities,
    remarks: remarks.optional(),
    media: media.optional(),
});
export const updateEmploymentRecord_Body = z
    .object({
        hidden: hidden,
        employer_name: employer_name.max(200),
        employer_address: employer_address,
        employer_contact: employer_contact,
        employment_position: employment_position.max(200),
        employment_started_on: employment_started_on,
        employment_ended_on: employment_ended_on,
        responsibilities: responsibilities,
        remarks: remarks,
        media: media,
    })
    .partial();
export const StudentResume = z.object({
    id: Id.int(),
    user_id: Id.int(),
    name: z.union([z.string(), z.null()]).optional(),
    address: z.union([z.string(), z.null()]).optional(),
    email: z.union([z.string(), z.null()]).optional(),
    phone: z.union([z.string(), z.null()]).optional(),
    career_objective: z.union([z.string(), z.null()]).optional(),
    key_skills: z.array(z.string()).optional(),
    created_at: DateTime,
    updated_at: DateTime,
    resume_url: z.union([z.string(), z.null()]),
});
export const address = z.union([z.string(), z.null()]);
export const email = z.union([z.string(), z.null()]);
export const phone = z.union([z.string(), z.null()]);
export const career_objective = z.union([z.string(), z.null()]);
export const key_skills = z.array(z.string());
export const setStudentResume_Body = z
    .object({
        name: name,
        address: address,
        email: email,
        phone: phone,
        career_objective: career_objective,
        key_skills: key_skills,
    })
    .partial();
export const FamilyRelationship = z.enum(['spouse', 'child', 'parent', 'sibling', 'guardian', 'other']);
export const FamilyMember = z.object({
    id: Id.int(),
    object: Morphs,
    student_id: Id.int(),
    relationship: FamilyRelationship,
    relationship_label: z.union([z.string(), z.null()]),
    created_at: DateTime,
    updated_at: DateTime,
    personal_details: StudentPersonalDetails.optional(),
});
export const FamilyMemberCreateRequest = z.object({
    relationship: FamilyRelationship,
    given_name: z.string().max(255),
    family_name: z.string().max(255),
    additional_name: z.union([z.string(), z.null()]).optional(),
    date_of_birth: NullableDate.optional(),
    country_of_citizenship: z.union([CountryCodeIso2, z.null()]).optional(),
    country_of_birth: z.union([CountryCodeIso2, z.null()]).optional(),
    place_of_birth: z.union([z.string(), z.null()]).optional(),
    passport_id: z.union([z.string(), z.null()]).optional(),
    passport_expiry_date: NullableDate.optional(),
    passport_issue_date: NullableDate.optional(),
    contact_email: z.union([z.string(), z.null()]).optional(),
    contact_phone: z.union([z.string(), z.null()]).optional(),
    gender: z.union([Gender, z.null()]).optional(),
    marital_status: z.union([MaritalStatus, z.null()]).optional(),
});
export const updateFamilyMember_Body = z.object({ relationship: FamilyRelationship });
export const FamilyMemberSyncRequest = z.object({ family_member_ids: z.array(Id) });
export const EventRegistrationConfig = z
    .object({
        enabled: z.boolean(),
        more_info: z.union([z.string(), z.null()]),
        starts_at: NullableDateTime,
        ends_at: NullableDateTime,
        email_required: z.boolean(),
        phone_required: z.boolean(),
    })
    .partial();
export const AdvancedPostMeta = z
    .object({ event_registration: z.union([z.null(), EventRegistrationConfig]) })
    .partial();
export const EventRegistrant = z.object({
    id: Id.int(),
    advanced_post_id: Id.int(),
    user_id: Id.int(),
    name: z.string().optional(),
    email: z.union([z.string(), z.null()]).optional(),
    phone: z.union([z.string(), z.null()]).optional(),
    created_at: DateTime,
    updated_at: DateTime,
});
export const AdvancedPost = z.object({
    id: Id.int(),
    user_id: NullableId,
    postable_id: Id.int(),
    postable_type: Morphs,
    postable_name: z.string().optional(),
    post_type: z.enum(['news', 'event', 'article']),
    title: z.string().max(400),
    content: z.string().max(10000),
    summary: z.union([z.string(), z.null()]).optional(),
    event_start_at: NullableDateTime.optional(),
    event_end_at: NullableDateTime.optional(),
    event_location: z.union([z.string(), z.null()]).optional(),
    article_authors: z.union([z.string(), z.null()]).optional(),
    company_branch_ids: z.union([z.null(), z.array(Id)]).optional(),
    company_branches: z.array(CompanyBranch).optional(),
    created_at: DateTime.optional(),
    updated_at: DateTime.optional(),
    meta: z.union([z.null(), AdvancedPostMeta]).optional(),
    media: z.array(Media).optional(),
    event_registrants: z.array(EventRegistrant).optional(),
});
export const post_type = z.enum(['news', 'event', 'article']);
export const content = z.string();
export const summary = z.union([z.string(), z.null()]);
export const event_start_at = NullableDateTime;
export const event_end_at = NullableDateTime;
export const event_location = z.union([z.string(), z.null()]);
export const company_branch_ids = z.union([z.null(), z.array(Id)]);
export const meta = z.union([z.null(), AdvancedPostMeta]);
export const addPost_Body = z.object({
    media: z.array(z.instanceof(File)).optional(),
    title: title.max(400),
    post_type: post_type,
    content: content.max(10000),
    summary: summary.optional(),
    event_start_at: event_start_at.optional(),
    event_end_at: event_end_at.optional(),
    event_location: event_location.optional(),
    company_branch_ids: company_branch_ids.optional(),
    meta: meta.optional(),
});
export const updatePost_Body = z
    .object({
        title: title.max(400),
        content: content.max(10000),
        summary: summary,
        event_start_at: event_start_at,
        event_end_at: event_end_at,
        event_location: event_location,
        meta: meta,
    })
    .partial();
export const registerForPost_Body = z.object({ name: name, email: email, phone: phone }).partial();
export const PayoutPrediction = z.object({
    company_id: Id.int(),
    company_name: z.string(),
    company_type: AccountType,
    amount: z.union([z.string(), z.null()]),
    currency: z.union([z.string(), z.null()]),
    is_valid: z.boolean().optional(),
    errors: z.array(z.string()),
});
export const CompanyPayoutStatus = z.enum(['created', 'cancelled', 'in_transit', 'paid', 'failed']);
export const CompanyPayout = z.object({
    id: Id.int(),
    reference_id: z.string(),
    company_id: Id.int(),
    company_name: z.union([z.null(), z.string()]).optional(),
    amount: z.string(),
    fee: z.union([z.string(), z.null()]).optional(),
    currency: z.string(),
    status: CompanyPayoutStatus,
    status_description: z.union([z.string(), z.null()]).optional(),
    status_changed_at: DateTime.optional(),
    arrival_date: NullableDate,
    created_at: DateTime,
    updated_at: DateTime,
    details_file_url: z.union([z.string(), z.null()]).optional(),
});
export const CompanyPayoutBatch = z.object({
    id: Id.int(),
    reference_id: z.string(),
    currency: z.string(),
    payout_source: z.union([AccountType, z.null()]).optional(),
    created_at: DateTime,
    updated_at: DateTime,
    processing_file_url: z.union([z.string(), z.null()]).optional(),
    payouts: z.array(CompanyPayout).optional(),
    bank_detail: z.union([z.null(), BankDetail]).optional(),
});
export const CreatePublicPage = z.object({
    name: z.string(),
    program_ids: z.array(Id).optional(),
    package_ids: z.array(Id).optional(),
    about: z.string().optional(),
    external: ExternalPresence.optional(),
    agency_factoids: AgencyFactoids.optional(),
    campus_factoids: CampusFactoids.optional(),
    address: AddressBody,
});
export const updateCampus_Body = z.object({ name: z.string(), address: AddressBody }).partial();
export const duration_weeks = z.number();
export const regular_interval_width = z.union([z.number(), z.null()]);
export const regular_interval_unit = z.union([z.null(), RegularIntervalUnit]);
export const number_of_payments = z.union([z.number(), z.null()]);
export const application_fees = Decimal;
export const material_fees = Decimal;
export const tuition_fees = Decimal;
export const commission_bonus = Decimal;
export const discount = Decimal;
export const advance_fees = Decimal;
export const fees_currency = CurrencyCode;
export const addProgram_Body = z.object({
    campus_ids: z.array(z.number().int()).optional(),
    duration_weeks: duration_weeks.int(),
    regular_interval_width: regular_interval_width.default(1),
    regular_interval_unit: regular_interval_unit,
    number_of_payments: number_of_payments,
    application_fees: application_fees,
    material_fees: material_fees,
    tuition_fees: tuition_fees,
    commission_bonus: commission_bonus,
    discount: discount,
    advance_fees: advance_fees,
    fees_currency: fees_currency,
    program_level_id: Id.int(),
    enabled: z.boolean(),
    name: z.string(),
    about: z.string(),
    requirements: z.string(),
    national_course_code: z.union([z.string(), z.null()]).optional(),
    cricos_course_code: z.union([z.string(), z.null()]).optional(),
    intakes: z.array(Month),
    method_of_delivery: MethodOfDelivery,
});
export const updateProgram_Body = z
    .object({
        campus_ids: z.array(z.number().int()),
        duration_weeks: duration_weeks.int(),
        regular_interval_width: regular_interval_width.default(1),
        regular_interval_unit: regular_interval_unit,
        number_of_payments: number_of_payments,
        application_fees: application_fees,
        material_fees: material_fees,
        tuition_fees: tuition_fees,
        commission_bonus: commission_bonus,
        discount: discount,
        advance_fees: advance_fees,
        fees_currency: fees_currency,
        program_level_id: Id.int(),
        enabled: z.boolean(),
        name: z.string(),
        about: z.string(),
        requirements: z.string(),
        national_course_code: z.union([z.string(), z.null()]),
        cricos_course_code: z.union([z.string(), z.null()]),
        intakes: z.array(Month),
        method_of_delivery: MethodOfDelivery,
    })
    .partial();
export const MockPaymentPlanConfig = z.object({
    program_id: NullableId.optional(),
    currency: CurrencyCode.min(3).max(3),
    application_fees: Decimal.optional(),
    tuition_fees: Decimal.optional(),
    material_fees: Decimal.optional(),
    commission_bonus: Decimal.optional(),
    start_date: DateUtc.optional(),
    agency_platform_fees: Decimal.optional(),
    school_platform_fees: Decimal.optional(),
    agency_commission: Decimal.optional(),
    number_of_payments: z.number().int().optional(),
    interval_days: z.number().int().optional(),
});
export const MockPaymentItem = z.object({
    currency: CurrencyCode.min(3).max(3),
    due_on: DateUtc,
    due_amount: Decimal,
    due_explanation: z.string(),
    agency_amount: Decimal.optional(),
    agency_explanation: z.string().optional(),
    school_amount: Decimal.optional(),
    school_explanation: z.string().optional(),
    platform_amount: Decimal.optional(),
    platform_explanation: z.string().optional(),
});
export const addPackage_Body = z.object({
    program_ids: z.array(z.number().int()).optional(),
    campus_ids: z.array(z.number().int()).optional(),
    name: name,
    company_id: Id.int(),
});
export const updatePackage_Body = z
    .object({ campus_ids: z.array(z.number().int()), enabled: enabled, name: name })
    .partial();
export const orderPackagePrograms_Body = z.object({ order: z.array(Id) });
export const syncPackagePrograms_Body = z.object({ program_ids: z.array(Id) });
export const addPackageProgram_Body = z.object({
    duration_weeks: duration_weeks.int(),
    regular_interval_width: regular_interval_width.default(1),
    regular_interval_unit: regular_interval_unit,
    number_of_payments: number_of_payments,
    application_fees: application_fees,
    material_fees: material_fees,
    tuition_fees: tuition_fees,
    commission_bonus: commission_bonus,
    discount: discount,
    advance_fees: advance_fees,
    fees_currency: fees_currency,
    program_id: Id.int(),
    name: z.string(),
    program: z.union([z.null(), Program]).optional(),
    package: z.union([z.null(), Package]).optional(),
});
export const updatePackageProgram_Body = z
    .object({
        duration_weeks: duration_weeks.int(),
        regular_interval_width: regular_interval_width.default(1),
        regular_interval_unit: regular_interval_unit,
        number_of_payments: number_of_payments,
        application_fees: application_fees,
        material_fees: material_fees,
        tuition_fees: tuition_fees,
        commission_bonus: commission_bonus,
        discount: discount,
        advance_fees: advance_fees,
        fees_currency: fees_currency,
        program_id: Id.int(),
        name: z.string(),
        program: z.union([z.null(), Program]),
        package: z.union([z.null(), Package]),
    })
    .partial();
export const updateAgency_Body = z
    .object({ name: z.string(), mara_number: z.string().max(20).nullable(), address: AddressBody })
    .partial();
export const UpdatePublicPage = z
    .object({
        about: z.string(),
        external: ExternalPresence,
        agency_factoids: AgencyFactoids,
        campus_factoids: CampusFactoids,
    })
    .partial();
export const addMedia_Body = z.object({
    custom_properties: z.union([z.null(), z.array(MediaCustomProperties)]).optional(),
    collection_name: z.string(),
    names: z.array(z.union([z.string(), z.null()])).optional(),
    media: z.array(z.instanceof(File)),
});
export const TusMediaConversion = z.object({
    tus_file_id: z.string(),
    name: z.union([z.string(), z.null()]).optional(),
    custom_properties: z.union([z.null(), MediaCustomProperties]).optional(),
    media_collection: z.union([z.string(), z.null()]).optional(),
});
export const addMediaTus_Body = z.object({ collection_name: z.string(), tus: z.array(TusMediaConversion) });
export const collection_name = z.string();
export const updateMedia_Body = z
    .object({ collection_name: collection_name, custom_properties: MediaCustomProperties })
    .partial();
export const updateSignature_Body = z.object({
    signee_name: z.string(),
    signature_uri: z.string().url().optional(),
    signee_agree_texts: z.array(z.string()),
    signature: z.union([z.instanceof(File), z.string()]).optional(),
});
export const OfferLetterStatus = z.enum(['pending', 'signed', 'revoked', 'rejected']);
export const OfferLetter = z.object({
    id: Id.int(),
    application_id: Id.int(),
    status: OfferLetterStatus,
    valid_upto: DateTime,
    created_at: DateTime,
    updated_at: DateTime,
    media: z.array(Media).optional(),
    signatures: z.array(Signature).optional(),
});
export const PaymentItem = z.object({
    id: Id.int(),
    payment_plan_id: Id.int(),
    index: z.number().int(),
    remarks: z.string(),
    due_amount: Decimal,
    due_on: DateUtc,
    gst_percentage: NullableDecimal,
    created_at: DateTime,
    updated_at: DateTime,
    can: z.object({ attempt: z.boolean() }).partial().optional(),
    payment_attempts: z.array(PaymentAttempt).optional(),
    invoice_urls: z.array(z.string()).optional(),
});
export const PaymentPlan = FeesSchedule.and(FeesStructure).and(
    z.object({
        id: Id.int(),
        program_id: NullableId.optional(),
        application_id: Id.int(),
        start_date: NullableDate.optional(),
        end_date: NullableDate.optional(),
        cancelled_at: NullableDateTime.optional(),
        description: z.string(),
        created_at: DateTime,
        updated_at: DateTime,
        payment_items: z.array(PaymentItem).optional(),
        payment_attempts: z.array(PaymentAttempt).optional(),
    })
);
export const AppliedProgram = FeesStructure.and(FeesSchedule).and(
    z.object({
        id: Id.int(),
        created_at: DateTime,
        updated_at: DateTime,
        name: z.string(),
        order: z.number().int(),
        is_offered: z.union([z.boolean(), z.null()]).optional(),
        is_accepted: z.union([z.boolean(), z.null()]).optional(),
        is_enrolled: z.union([z.boolean(), z.null()]).optional(),
        program_id: Id.int(),
        application_id: Id.int(),
        payment_plan_id: NullableId,
        program: Program.optional(),
        payment_plan: PaymentPlan.optional(),
    })
);
export const DocumentRequirement = z.object({
    id: Id.int(),
    application_id: Id.int(),
    required: z.boolean().optional(),
    document_type_id: z.union([z.number(), z.null()]),
    denied_at: NullableDateTime.optional(),
    denied_reason: z.union([z.string(), z.null()]).optional(),
    last_submitted_at: NullableDateTime.optional(),
    approved_at: NullableDateTime.optional(),
    title: z.string(),
    instructions: z.union([z.string(), z.null()]).optional(),
    created_at: DateTime,
    updated_at: DateTime,
    media_links: z.array(MediaLink),
});
export const Application = z.object({
    id: Id.int(),
    reference_id: z.union([z.string(), z.null()]).optional(),
    object: Morphs,
    name: z.union([z.string(), z.null()]).optional(),
    updated_at: DateTime,
    created_at: DateTime,
    student_id: Id.int(),
    media_storage_id: NullableId,
    application_type: z.enum(['course', 'visa']).optional(),
    applicable_id: NullableId.optional(),
    applicable_type: z.union([z.string(), z.null()]).optional(),
    visa_type: z.union([z.enum(['subclass_500', 'subclass_485', 'subclass_482', null]), z.null()]).optional(),
    target_date: z.union([z.string(), z.null()]).optional(),
    school_company_id: NullableId.optional(),
    agency_company_id: Id.int(),
    staff_company_id: Id.int(),
    school_branch_id: NullableId.optional(),
    agency_branch_id: Id.int(),
    agency_conversation_id: NullableId,
    student_conversation_id: NullableId,
    agency_staff_agreement_id: NullableId.optional(),
    school_staff_agreement_id: NullableId.optional(),
    agency_school_agreement_id: NullableId.optional(),
    intake_year: z.union([z.number(), z.null()]).optional(),
    intake_month: z.union([z.number(), z.null()]).optional(),
    status: z.string(),
    status_reason: z.union([z.string(), z.null()]).optional(),
    student: Student.optional(),
    offer_letters: z.array(OfferLetter).optional(),
    package: z.union([Package, z.null()]).optional(),
    handlers: z.array(Handler).optional(),
    applied_programs: z.array(AppliedProgram).optional(),
    media_storage: z.union([MediaStorage, z.null()]).optional(),
    document_requirements: z.array(DocumentRequirement).optional(),
    payment_plans: z.array(PaymentPlan).optional(),
    campus: Campus.optional(),
    agency: Agency.optional(),
    personal_details: z.union([StudentPersonalDetails, z.null()]).optional(),
    diverged: z.boolean().optional(),
    can: z
        .object({
            view: z.union([z.string(), z.boolean()]),
            send_offer_letter: z.union([z.string(), z.boolean()]),
            attach_acceptance_letter: z.union([z.string(), z.boolean()]),
            sign_offer_letter: z.union([z.string(), z.boolean()]),
            attach_coe: z.union([z.string(), z.boolean()]),
            submit: z.union([z.string(), z.boolean()]),
            in_review: z.union([z.string(), z.boolean()]),
            reject: z.union([z.string(), z.boolean()]),
            withdraw: z.union([z.string(), z.boolean()]),
        })
        .partial()
        .optional(),
    notes_moved_from_student: z.boolean().optional(),
});
export const addApplication_Body = z.object({
    package_id: Id.int().optional(),
    campus_id: Id.int(),
    program_id: Id.int().optional(),
    student_id: Id.int(),
    intake_month: Month.int().gte(1).lte(12),
    intake_year: z.number().int(),
});
export const updateApplication_Body = z
    .object({ status: z.enum(['submitted', 'in_review', 'rejected', 'withdrawn', 'canceled']) })
    .partial();
export const sendOfferLetter_Body = z.object({
    programs: z.array(
        FeesSchedule.and(FeesStructure).and(z.object({ applied_program_id: Id.int(), start_date: DateTime.optional() }))
    ),
    offer_letter: z.instanceof(File),
});
export const sendConfirmationOfEnrolment_Body = z.object({
    confirmation_of_enrolment: z.array(z.object({ letter: z.instanceof(File), applied_program_id: Id.int() })),
});
export const ApplicationStatus = z.enum([
    'draft',
    'submitted',
    'in_review',
    'offered',
    'accepted',
    'confirmed',
    'rejected',
    'withdrawn',
    'canceled',
    'completed',
]);
export const ApplicationEvent = z.object({
    id: Id.int(),
    application_id: Id.int(),
    created_at: DateTime,
    updated_at: DateTime,
    caused_by: z.union([z.string(), z.null()]),
    code: z.string(),
    title: z.string(),
    description: z.string(),
    data: z
        .union([
            z
                .object({
                    document_requirement_id: Id.int(),
                    application_status: ApplicationStatus,
                    subject: z.string(),
                    communication_id: Id.int(),
                })
                .partial(),
            z.null(),
        ])
        .optional(),
    changes: z
        .union([
            z.record(
                z.union([z.string(), z.array(z.any()), z.object({}).partial(), z.number(), z.boolean(), z.null()])
            ),
            z.null(),
        ])
        .optional(),
});
export const document_type_id = z.union([z.number(), z.null()]);
export const addDocumentRequirement_Body = z.object({
    title: title,
    instructions: instructions.optional(),
    required: required.optional(),
    document_type_id: document_type_id,
});
export const updateDocumentRequirement_Body = z
    .object({ title: title, instructions: instructions, required: required })
    .partial();
export const denyDocumentRequirement_Body = z.object({ denied_reason: z.union([z.string(), z.null()]) }).partial();
export const AgreementTarget = z.enum(['agency', 'college']);
export const AgreementTemplate = z.object({
    id: Id.int(),
    created_at: DateTime,
    updated_at: DateTime,
    internal_name: z.string(),
    name: z.string(),
    enabled: z.boolean(),
    contents: z.string(),
    automatically_assign: z.boolean(),
    target: AgreementTarget,
    validity_days: z.union([z.number(), z.null()]),
    agreement_type: AgreementType,
    commission_percentage: Decimal,
    offer_days: z.union([z.number(), z.null()]).optional(),
    offer_percentage: z.union([z.string(), z.number(), z.null()]).optional(),
    offer_name: z.union([z.string(), z.null()]).optional(),
});
export const contents = z.string();
export const automatically_assign = z.boolean();
export const target = AgreementTarget;
export const internal_name = z.string();
export const offer_name = z.union([z.string(), z.null()]);
export const offer_percentage = z.union([z.string(), z.number(), z.null()]);
export const offer_days = z.union([z.number(), z.null()]);
export const createAgreementTemplate_Body = z.object({
    validity_days: z.union([z.number(), z.null()]).optional(),
    name: name,
    contents: contents,
    agreement_type: AgreementType,
    automatically_assign: automatically_assign,
    target: target,
    enabled: enabled,
    commission_percentage: Decimal,
    internal_name: internal_name,
    offer_name: offer_name.optional(),
    offer_percentage: offer_percentage.optional(),
    offer_days: offer_days.optional(),
});
export const getRenderedAgreementTemplate_Body = z.object({
    sender_company_id: Id.int(),
    receiver_company_id: Id.int(),
    contents: z.string(),
});
export const validity_days = z.union([z.number(), z.null()]);
export const updateAgreementTemplate_Body = z
    .object({
        commission_percentage: Decimal,
        contents: contents,
        name: name,
        enabled: enabled,
        automatically_assign: automatically_assign,
        validity_days: validity_days,
        internal_name: internal_name,
        offer_name: offer_name,
        offer_days: offer_days,
        offer_percentage: offer_percentage,
    })
    .partial();
export const PlatformReferral = z.object({
    id: Id.int(),
    created_at: DateTime,
    updated_at: DateTime,
    enabled: z.boolean(),
    company_id: Id.int(),
    referral_code: z.string(),
    offer_days: z.union([z.number(), z.null()]),
    offer_percentage: Decimal,
});
export const addPlatformReferral_Body = z.object({
    offer_days: offer_days,
    offer_percentage: Decimal,
    enabled: enabled,
    company_id: Id.int(),
});
export const updatePlatformReferral_Body = z
    .object({ offer_days: offer_days, offer_percentage: Decimal, enabled: enabled })
    .partial();
export const receiver_company_id = Id;
export const valid_upto = NullableDate;
export const addAgreement_Body = z.object({
    receiver_company_id: receiver_company_id,
    name: name,
    agreement_type: AgreementType,
    valid_upto: valid_upto.optional(),
    commission_percentage: Decimal.optional(),
    contents: z.string().optional(),
    agreement: z.instanceof(File).optional(),
    offer_days: z.union([z.number(), z.null()]).optional(),
    offer_percentage: z.union([z.string(), z.number(), z.null()]).optional(),
    offer_name: z.union([z.string(), z.null()]).optional(),
});
export const addAgreementFromTemplate_Body = z.object({
    agreement_template_id: Id.int(),
    receiver_company_id: Id.int(),
});
export const updateAgreement_Body = z.object({
    commission_percentage: Decimal,
    valid_upto: valid_upto,
    agreement: z.instanceof(File).optional(),
    contents: z.string().optional(),
    signee_name: z.string().optional(),
    signee_agree_texts: z.array(z.string()).optional(),
    signature_uri: z.string().url().optional(),
    signature: z.union([z.instanceof(File), z.string()]).optional(),
    commissions: z.array(z.object({ program_id: Id.int(), commission_percentage: Decimal })).optional(),
});
export const AssociationRequestStatus = z.enum([
    'interested',
    'pending',
    'accepted',
    'rejected',
    'cancelled',
    'expired',
]);
export const AgentApplication = z.object({
    business_identification: z.string(),
    trading_name: z.string(),
    address: z.string(),
    contact_name: z.string(),
    contact_phone: z.string(),
    contact_email: z.string(),
    references: z.array(WorkReference),
});
export const AssociationRequest = z.object({
    id: Id.int(),
    created_at: DateTime,
    updated_at: DateTime,
    sender_company_id: Id.int(),
    receiver_company_id: Id.int(),
    initiator_company_id: Id.int(),
    flow_type: z.literal('agency_to_school'),
    requires_agent_application: z.boolean().optional(),
    agreement_id: NullableId.optional(),
    renewal_agreement_id: NullableId.optional(),
    status: AssociationRequestStatus.optional(),
    agent_application: z.union([AgentApplication, z.null()]).optional(),
    sender_terms: z.union([z.array(z.string()), z.null()]).optional(),
    receiver_company: Company.optional(),
    sender_company: Company.optional(),
    agreement: z.union([Agreement, z.null()]).optional(),
    renewal_agreement: z.union([Agreement, z.null()]).optional(),
});
export const addAssociation_Body = z.object({
    company_id: Id.int(),
    agent_application: AgentApplication.optional(),
    requires_agent_application: z.boolean(),
});
export const sendAgreement_Body = z.object({
    commission_percentage: Decimal,
    valid_upto: valid_upto,
    agreement: z.instanceof(File),
    signee_name: z.string().optional(),
    signee_agree_texts: z.array(z.string()).optional(),
    signature_uri: z.string().url().optional(),
    signature: z.union([z.instanceof(File), z.string()]).optional(),
    commissions: z.array(z.object({ program_id: Id.int(), commission_percentage: Decimal })).optional(),
});
export const SummarizedPaymentPlan = FeesSchedule.and(FeesStructure).and(
    z.object({
        id: Id.int(),
        program_id: NullableId,
        application_id: Id.int(),
        application_reference: z.string().optional(),
        student_reference: z.string().optional(),
        agency: MinimalCompany.optional(),
        school: MinimalCompany.optional(),
        start_date: NullableDate,
        end_date: NullableDate.optional(),
        cancelled_at: NullableDateTime.optional(),
        description: z.string(),
        created_at: DateTime,
        updated_at: DateTime,
        next_due: NullableDate,
        total_amount: Decimal,
        paid_amount: Decimal,
        other_amounts: z.array(
            z.object({
                label: z.string(),
                company_id: Id.int(),
                company_type: AccountType.optional(),
                total_amount: Decimal,
                received_amount: Decimal,
            })
        ),
    })
);
export const addPaymentAttemptToPlan_Body = z.object({ amount: z.string(), payment_method_id: NullableId });
export const AttemptRecordStatus = z.enum([
    'pending',
    'fulfilled',
    'available_soon',
    'available',
    'payout_initiated',
    'paid_out',
    'refunded',
    'cancelled',
]);
export const AttemptRecord = z.object({
    id: Id.int(),
    payment_attempt_id: Id.int(),
    company_id: NullableId.optional(),
    company_branch_id: NullableId.optional(),
    created_at: DateTime,
    updated_at: DateTime,
    total_amount: z.string(),
    expected_amount: z.string(),
    remarks: z.string().optional(),
    status: AttemptRecordStatus,
    paid_at: NullableDate.optional(),
    stripe_transfer_id: z.union([z.string(), z.null()]).optional(),
    invoice_urls: z.array(z.string().url()).optional(),
});
export const PaymentRecord = z.object({
    id: Id.int(),
    company_id: Id.int(),
    company_branch_id: NullableId,
    application_id: Id.int(),
    payment_item_id: Id.int(),
    invoice_id: NullableId.optional(),
    student_name: z.string(),
    application_name: z.string(),
    agency_name: z.string(),
    school_name: z.string(),
    expected_remarks: z.string(),
    total_remarks: z.string(),
    expected_amount: z.string(),
    total_amount: z.string(),
    expected_around: NullableDate,
    created_at: DateTime,
    updated_at: DateTime,
    attempt_records: z.array(AttemptRecord).optional(),
});
export const PlatformInfo = z.object({
    name: z.union([z.string(), z.null()]),
    email: z.union([z.string(), z.null()]),
    phone: z.union([z.string(), z.null()]),
    toll_free_phone: z.union([z.string(), z.null()]),
    address: z.union([z.string(), z.null()]),
});
export const Settings = z
    .object({
        show_business_in_agency_search: z.boolean(),
        show_business_in_school_search: z.boolean(),
        default_currency: CurrencyCode.min(3).max(3),
        default_country: z.union([CountryCodeIso2, z.null()]),
        payment_attempt_minimum_amount: Decimal,
        partner_request_retry_interval: z.number().int(),
        platform_info: PlatformInfo,
        application_hard_expiry_days: z.number().int(),
        application_expiry_warn_days: z.number().int(),
        agreement_expiry_warn_days: z.number().int(),
        payment_plan_ending_warn_days: z.number().int(),
        payment_due_warn_days: z.number().int(),
        payment_overdue_repeat_days: z.number().int(),
        application_stale_days: z.number().int(),
        autopay_upcoming_payments_days: z.number().int(),
        becs_transaction_limit: z.number().int(),
        dynamic: z
            .object({
                auto_submit_applications: z.boolean(),
                allow_agency_direct_add_student: z.boolean(),
                invite_student_cooldown_hours: z.number().int(),
                platform_referral_offer_percentage: NullableDecimal,
                platform_referral_offer_days: z.union([z.number(), z.null()]),
                max_discount_percentage: NullableDecimal,
                signature: z.union([z.null(), z.object({ name: z.string(), email: z.string() })]),
                external_links: z.array(z.object({ name: z.string(), url: z.string() })),
                support_email: z.union([z.string(), z.null()]),
                email_regards: z.union([z.string(), z.null()]),
                email_copyright: z.union([z.string(), z.null()]),
                auto_invoice_upcoming_payments_days: z.union([z.number(), z.null()]),
                email_exceptions: z.array(z.string().email()),
            })
            .partial(),
        media_storage: MediaStorage,
        trust_bank_detail: z.union([z.null(), BankDetail]),
        platform_bank_detail: z.union([z.null(), BankDetail]),
    })
    .partial();
export const show_business_in_agency_search = z.boolean();
export const show_business_in_school_search = z.boolean();
export const default_currency = CurrencyCode;
export const default_country = z.union([CountryCodeIso2, z.null()]);
export const payment_attempt_minimum_amount = Decimal;
export const partner_request_retry_interval = z.number();
export const platform_info = PlatformInfo;
export const application_hard_expiry_days = z.number();
export const application_expiry_warn_days = z.number();
export const agreement_expiry_warn_days = z.number();
export const payment_plan_ending_warn_days = z.number();
export const payment_due_warn_days = z.number();
export const payment_overdue_repeat_days = z.number();
export const application_stale_days = z.number();
export const autopay_upcoming_payments_days = z.number();
export const becs_transaction_limit = z.number();
export const dynamic = z
    .object({
        auto_submit_applications: z.boolean(),
        allow_agency_direct_add_student: z.boolean(),
        invite_student_cooldown_hours: z.number().int(),
        platform_referral_offer_percentage: NullableDecimal,
        platform_referral_offer_days: z.union([z.number(), z.null()]),
        max_discount_percentage: NullableDecimal,
        signature: z.union([z.null(), z.object({ name: z.string(), email: z.string() })]),
        external_links: z.array(z.object({ name: z.string(), url: z.string() })),
        support_email: z.union([z.string(), z.null()]),
        email_regards: z.union([z.string(), z.null()]),
        email_copyright: z.union([z.string(), z.null()]),
        auto_invoice_upcoming_payments_days: z.union([z.number(), z.null()]),
        email_exceptions: z.array(z.string().email()),
    })
    .partial();
export const updateSettings_Body = z
    .object({
        platform_logo: z.instanceof(File),
        agreements_auto_signature: z.instanceof(File),
        trust_bank_detail: z.object({
            account_name: account_name,
            account_number: account_number,
            account_bsb: account_bsb,
            payment_plus_id: payment_plus_id.optional(),
        }),
        platform_bank_detail: z.object({
            account_name: account_name,
            account_number: account_number,
            account_bsb: account_bsb,
            payment_plus_id: payment_plus_id.optional(),
        }),
        show_business_in_agency_search: show_business_in_agency_search,
        show_business_in_school_search: show_business_in_school_search,
        default_currency: default_currency,
        default_country: default_country,
        payment_attempt_minimum_amount: payment_attempt_minimum_amount,
        partner_request_retry_interval: partner_request_retry_interval.int(),
        platform_info: platform_info,
        application_hard_expiry_days: application_hard_expiry_days.int(),
        application_expiry_warn_days: application_expiry_warn_days.int(),
        agreement_expiry_warn_days: agreement_expiry_warn_days.int(),
        payment_plan_ending_warn_days: payment_plan_ending_warn_days.int(),
        payment_due_warn_days: payment_due_warn_days.int(),
        payment_overdue_repeat_days: payment_overdue_repeat_days.int(),
        application_stale_days: application_stale_days.int(),
        autopay_upcoming_payments_days: autopay_upcoming_payments_days.int(),
        becs_transaction_limit: becs_transaction_limit.int(),
        dynamic: dynamic,
    })
    .partial();
export const ApplicationEnums = z.object({
    language_test_types: z.array(z.object({ label: z.string(), value: z.string(), enabled: z.boolean() })),
});
export const UserContext = z.object({
    app_env: z.union([z.string(), z.null()]).optional(),
    user: User,
    students: z.array(Student),
    role: Role,
    company: Company,
    authorized_branches: z.array(CompanyBranch),
    agencies: z.array(Agency),
    campuses: z.array(Campus),
    settings: Settings,
    managed_company_ids: z.array(Id),
    enums: ApplicationEnums,
    defaults: z.object({ country: z.union([Country, z.null()]), currency: z.union([Currency, z.null()]) }).partial(),
});
export const Notification = z.object({
    id: z.string(),
    deletable: z.boolean(),
    tag: z.union([z.string(), z.null()]),
    priority: z.number().int(),
    created_at: DateTime,
    read_at: NullableDateTime.optional(),
    updated_at: DateTime.optional(),
    notifiable_id: Id.int(),
    notifiable_type: Morphs,
    data: z.object({
        title: z.string(),
        message: z.string(),
        code: z.union([z.string(), z.null()]).optional(),
        application_id: NullableId.optional(),
        agency_id: NullableId.optional(),
        campus_id: NullableId.optional(),
        student_id: NullableId.optional(),
        user_id: NullableId.optional(),
        program_id: NullableId.optional(),
        package_id: NullableId.optional(),
        association_request_id: NullableId.optional(),
        agreement_id: NullableId.optional(),
        agreement_sender_company_id: NullableId.optional(),
        agreement_receiver_company_id: NullableId.optional(),
    }),
});
export const EmailCategory = z.enum(['accounts', 'platform', 'misc', 'admissions', 'user']);
export const EmailTemplate = z.object({
    id: Id.int(),
    routable: z.boolean().optional(),
    code: z.string(),
    category: EmailCategory.optional(),
    title: z.string(),
    description: z.string(),
    has_related_entities: z.boolean(),
    root_branch_only: z.boolean(),
    subject: z.union([z.string(), z.null()]).optional(),
    content: z.union([z.string(), z.null()]).optional(),
    created_at: DateTime,
    updated_at: DateTime,
});
export const subject = z.union([z.string(), z.null()]);
export const updateEmailTemplate_Body = z.object({ subject: subject, content: content }).partial();
export const EmailRecipient = z.object({
    id: Id.int(),
    company_id: Id.int(),
    company_branch_id: Id.int(),
    email_template_id: Id.int(),
    recipients: z.array(z.string()),
    send_to_related: z.boolean(),
    created_at: DateTime,
    updated_at: DateTime,
    email_template: z.union([z.null(), EmailTemplate]).optional(),
});
export const recipients = z.array(z.string());
export const send_to_related = z.boolean();
export const addEmailRecipient_Body = z.object({
    recipients: recipients,
    send_to_related: send_to_related,
    email_template_id: Id.int(),
});
export const updateEmailRecipient_Body = z
    .object({ recipients: recipients, send_to_related: send_to_related })
    .partial();
export const StripePaymentMethod = z.object({
    id: z.string(),
    type: z.string(),
    created: z.number().optional(),
    au_becs_debit: z
        .object({
            bsb_number: z.union([z.null(), z.string()]),
            last4: z.union([z.null(), z.string()]),
            fingerprint: z.union([z.null(), z.string()]),
        })
        .partial()
        .optional(),
    card: z
        .object({
            brand: z.string(),
            last4: z.string(),
            fingerprint: z.union([z.null(), z.string()]),
            exp_month: z.number().int(),
            exp_year: z.number().int(),
            display_brand: z.union([z.null(), z.string()]),
            funding: z.string(),
        })
        .partial()
        .optional(),
});
export const PaymentMethodReference = z.object({
    id: Id.int(),
    provider: z.string(),
    reference_id: z.string(),
    reference_object: z.union([z.null(), StripePaymentMethod]),
    last_error_message: z.union([z.string(), z.null()]),
    is_primary: z.boolean(),
    is_active: z.boolean(),
    created_at: DateTime,
    updated_at: DateTime,
});
export const is_primary = z.boolean();
export const updatePaymentMethod_Body = z.object({ is_primary: is_primary }).partial();
export const sendTestEmail_Body = z.object({
    email: z.string().email(),
    subject: z.union([z.string(), z.null()]).optional(),
    html: z.union([z.string(), z.null()]).optional(),
});
export const DocumentDraftField = z.object({
    id: z.number().int(),
    field_label: z.string(),
    field_value: z.union([z.string(), z.null()]).optional(),
    binding: z.union([z.string(), z.null()]).optional(),
    document_draft_id: z.number().int(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
});
export const DocumentDraft = z.object({
    id: z.number().int(),
    name: z.string(),
    description: z.union([z.string(), z.null()]).optional(),
    template_html: z.string(),
    company_id: z.number().int(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
    deleted_at: z.union([z.string(), z.null()]).optional(),
    fields: z.array(DocumentDraftField).optional(),
    company: Company.optional(),
});
export const createDocumentDraft_Body = z
    .object({
        name: z.string(),
        description: z.union([z.string(), z.null()]),
        template_html: z.string(),
        fields: z.array(
            z
                .object({
                    field_label: z.string(),
                    field_value: z.union([z.string(), z.null()]),
                    binding: z.union([z.string(), z.null()]),
                })
                .partial()
        ),
    })
    .partial();
export const updateDocumentDraft_Body = z
    .object({
        name: z.string(),
        description: z.union([z.string(), z.null()]),
        template_html: z.string(),
        fields: z.array(
            z
                .object({
                    field_label: z.string(),
                    field_value: z.union([z.string(), z.null()]),
                    id: NullableId,
                    binding: z.union([z.string(), z.null()]),
                })
                .partial()
        ),
    })
    .partial();
export const DocumentFieldBinding = z.object({
    key: z.string(),
    label: z.string(),
    category: z.enum([
        'student',
        'address',
        'application',
        'service',
        'family',
        'agency',
        'sender',
        'assignee',
        'step_documents',
    ]),
    value_type: z.enum(['text', 'date', 'email', 'currency', 'list']),
});
export const TaskStatus = z.enum(['todo', 'doing', 'done']);
export const TaskType = z.enum(['task', 'meeting']);
export const Task = z.object({
    id: Id.int(),
    object: Morphs,
    reference_id: z.string(),
    title: z.string().max(255),
    description: z.union([z.string(), z.null()]).optional(),
    status: TaskStatus,
    type: TaskType,
    start_date: NullableDateTime.optional(),
    deadline: NullableDateTime.optional(),
    reschedule_reason: z.union([z.string(), z.null()]).optional(),
    completed_at: NullableDateTime.optional(),
    student_id: NullableId.optional(),
    taskable_id: z.union([z.number(), z.null()]).optional(),
    taskable_type: z.union([z.null(), Morphs]).optional(),
    taskable_count: z.number().int().optional(),
    parent_task_id: NullableId.optional(),
    subtask_count: z.number().int().optional(),
    company_branch_id: z.number().int(),
    company_id: z.number().int(),
    user_id: z.number().int().optional(),
    created_at: DateTime.optional(),
    updated_at: DateTime.optional(),
    user: z.union([UserPublic, z.null()]).optional(),
    handlers: z.array(Handler).optional(),
    media: z.array(Media).optional(),
    student: z.union([MinimalStudent, z.null()]).optional(),
});
export const taskable_id = z.union([z.number(), z.null()]);
export const taskable_type = z.union([z.null(), Morphs]);
export const company_branch_id = z.number();
export const parent_task_id = NullableId;
export const createTask_Body = z.object({
    tus: z.array(TusMediaConversion).optional(),
    handler_id: NullableId.optional(),
    title: title.max(255),
    description: description.optional(),
    status: TaskStatus,
    start_date: NullableDateTime.optional(),
    deadline: NullableDateTime.optional(),
    type: TaskType,
    student_id: NullableId.optional(),
    taskable_id: taskable_id.optional(),
    taskable_type: taskable_type.optional(),
    company_branch_id: company_branch_id.int(),
    parent_task_id: parent_task_id.optional(),
});
export const updateTask_Body = z
    .object({
        title: title.max(255),
        description: description,
        status: TaskStatus,
        start_date: NullableDateTime,
        deadline: NullableDateTime,
    })
    .partial();
export const rescheduleTask_Body = z.object({ deadline: DateTime, reason: z.string() });
export const EditHistory = z.object({
    body: z.string(),
    changed_at: DateTime,
    created_at: DateTime,
    changed_by: NullableId.optional(),
});
export const Comment = z.object({
    id: z.number().int(),
    body: z.string(),
    commentable_id: z.number().int(),
    commentable_type: z.string(),
    user_id: z.number().int(),
    edit_history: z.array(EditHistory),
    last_edited_at: NullableDateTime.optional(),
    created_at: DateTime,
    updated_at: DateTime,
    media: z.array(Media).optional(),
    user: z.union([UserPublic, z.null()]).optional(),
});
export const createComment_Body = z.object({ body: z.string(), tus: z.array(TusMediaConversion).optional() });
export const SmartEventType = z.enum([
    'note',
    'task',
    'meeting',
    'activity',
    'upload',
    'document_reupload',
    'payment',
    'email',
]);
export const NoteEventData = z.object({
    type: z.literal('se_note'),
    body: z.string().max(16000),
    history: z.array(EditHistory).optional(),
});
export const ActivityEventData = z.object({
    type: z.literal('se_activity'),
    old: z.record(z.unknown()).nullable(),
    new: z.record(z.unknown()).nullable(),
});
export const InvoiceEventData = z.object({
    type: z.literal('se_invoice'),
    invoice_id: z.number().int(),
    invoice_url: z.string(),
    total_amount: z.string(),
    currency: z.string(),
    due_date: z.union([z.string(), z.null()]).optional(),
    line_items_count: z.number().int(),
    payment_plan_id: z.union([z.number(), z.null()]).optional(),
    receipt_url: z.union([z.string(), z.null()]).optional(),
});
export const DocumentReuploadEventData = z.object({
    type: z.literal('se_document_reupload'),
    document_reupload_request_id: z.number().int(),
    document_title: z.string(),
    status: z.enum(['pending_approval', 'accepted', 'rejected']),
    reason: z.union([z.string(), z.null()]).optional(),
    rejection_reason: z.union([z.string(), z.null()]).optional(),
    previous_media_id: z.union([z.number(), z.null()]).optional(),
    new_media_id: z.union([z.number(), z.null()]).optional(),
});
export const EmailEventData = z.object({
    type: z.literal('se_email'),
    subject: z.string(),
    communication_id: Id.int(),
    invoice_id: Id.int().optional(),
    invoice_url: z.string().nullish(),
});
export const SmartEventData = z.discriminatedUnion('type', [
    NoteEventData,
    ActivityEventData,
    InvoiceEventData,
    DocumentReuploadEventData,
    EmailEventData,
]);
export const SmartEvent = z.object({
    id: z.number().int(),
    eventable_id: z.number().int(),
    eventable_type: Morphs,
    task_id: NullableId.optional(),
    user_id: NullableId.optional(),
    title: z.union([z.string(), z.null()]).optional(),
    description: z.union([z.string(), z.null()]).optional(),
    type: SmartEventType,
    event_data: z.union([z.null(), SmartEventData]).optional(),
    created_at: DateTime,
    updated_at: DateTime,
    task: z.union([z.null(), Task]).optional(),
    user: z.union([z.null(), UserPublic]).optional(),
    media_links: z.array(MediaLink).optional(),
});
export const Communication = z.object({
    id: Id.int(),
    student_id: Id.int(),
    loggable_id: z.number().int(),
    loggable_type: Morphs,
    channel: z.enum(['visa', 'course']),
    subject: z.string(),
    html_body: z.string(),
    text_body: z.union([z.string(), z.null()]).optional(),
    recipients: z.union([z.object({ to: z.array(z.string()), cc: z.array(z.string()) }).partial(), z.null()]),
    sent_at: DateTime,
    created_at: DateTime,
    updated_at: DateTime,
});
export const body = z.string();
export const createNoteSmartEvent_Body = z.object({ body: body.max(16000) });
export const createUploadSmartEvent_Body = z.object({
    tus: z.array(TusMediaConversion),
    title: z.string().max(255),
    description: z.string().max(2000).optional(),
});
export const DiscountLog = z.object({
    id: Id.int(),
    discountable_type: Morphs,
    discountable_id: Id.int(),
    user_id: NullableId.optional(),
    previous_discount: z.union([z.string(), z.null()]).optional(),
    new_discount: z.string(),
    reason: z.union([z.string(), z.null()]).optional(),
    user: z
        .union([z.null(), z.object({ id: Id.int(), given_name: z.string(), family_name: z.string() }).partial()])
        .optional(),
    created_at: DateTime,
    updated_at: DateTime,
});
export const OneTimePassword = z
    .object({
        id: Id.int(),
        receiver: z.string(),
        expires_at: DateTime,
        refreshable_at: DateTime,
        created_at: DateTime,
        updated_at: DateTime,
        attempts: z.number().int(),
        max_attempts: z.number().int(),
    })
    .partial();
export const Morphable = z.object({ id: Id.int(), object: Morphs });
export const JwtClaims = z.object({
    sub: z.number().int(),
    act: AccountType,
    name: z.string(),
    iat: z.number().int(),
    exp: z.number().int(),
    nbf: z.number().int().optional(),
    scopes: z.array(z.literal('onboarded')),
});
export const OtpStatus = z.object({
    receiver: z.string(),
    expires_at: DateTime,
    refreshable_at: DateTime,
    created_at: DateTime,
    updated_at: DateTime,
    attempts: z.number().int(),
    max_attempts: z.number().int().optional(),
});
export const DeviceId = z.string();
export const Password = z.string();
export const BasicAddress = z.object({
    street_address: z.string(),
    city: z.string(),
    state: z.string(),
    postal_code: z.string(),
    country: CountryCodeIso2.min(2).max(2),
});
export const Reactions = z.unknown();
export const SocialReactable = z.object({
    reactions: z.object({}).partial(),
    my_reaction: z.union([z.string(), z.null()]).optional(),
});
export const SocialComment = SocialReactable.and(
    z.object({
        id: Id.int(),
        social_post_id: Id.int().optional(),
        social_user_id: Id.int().optional(),
        content: z.string().optional(),
        created_at: DateTime,
        updated_at: DateTime,
    })
);
export const ConversationTopics = z
    .object({
        student_id: NullableId,
        student_name: z.union([z.string(), z.null()]),
        student_reference: z.union([z.string(), z.null()]),
        application_id: NullableId,
        application_reference: z.union([z.string(), z.null()]),
        application_name: z.union([z.string(), z.null()]),
    })
    .partial();
export const CompanyUser = z.object({
    user_id: Id.int(),
    company_id: Id.int(),
    company_branch_id: Id.int(),
    parent_user_id: NullableId,
    designation: z.union([z.string(), z.null()]),
    created_at: DateTime,
    updated_at: DateTime,
    permissions: z.union([z.string(), z.null()]),
    deleted_at: NullableDateTime,
    user: z.union([User, UserPublic]).optional(),
    parent_user: z.union([User, UserPublic]).optional(),
});
export const TransactionTODO = z.object({ id: Id.int() });
export const DocumentRequirementStatus = z.enum(['created', 'submitted', 'approved', 'declined']);
export const WebViewImage = z.object({ url: z.string(), name: z.string() }).partial();
export const PrettySearchTypes = z.enum(['program', 'school', 'agency', 'campus']);

export const schemas = {
    CommonError,
    Id,
    DateTime,
    ProgramLevel,
    title,
    enabled,
    createProgramLevel_Body,
    ValidationError,
    updateProgramLevel_Body,
    NullableId,
    QualificationLevel,
    DocumentType,
    instructions,
    required,
    auto_add,
    qualification_level,
    createDocumentType_Body,
    updateDocumentType_Body,
    filter_search_,
    filter_company_branch_id_,
    PaginatedResponse,
    FormFieldType,
    FormStepConfigField,
    FormStepConfig,
    UploadStepConfigItem,
    UploadStepConfig,
    WaitingStepConfig,
    Decimal,
    CurrencyCode,
    PaymentStepConfig,
    DocumentSignStepItem,
    DocumentSignStepConfig,
    QuotationStepConfig,
    ProfileGateStepConfig,
    AgreementStepItem,
    AgreementStepConfig,
    ResultStepConfig,
    ServiceStepConfig,
    ServiceTemplateStep,
    NullableDateTime,
    ServiceTemplate,
    createServiceTemplate_Body,
    updateServiceTemplate_Body,
    name,
    description,
    config,
    createServiceTemplateStep_Body,
    reorderServiceTemplateSteps_Body,
    ProfilePreCheckResult,
    updateServiceTemplateStep_Body,
    Morphs,
    AccountType,
    CompanyBranchStatus,
    MinimalCompany,
    MinimalCompanyBranch,
    MinimalStudent,
    MediaStorageType,
    MediaCustomProperties,
    MediaLink,
    Media,
    MediaCollection,
    MediaStorage,
    Signature,
    ServiceStepMediaLink,
    ServiceStep,
    Service,
    ServiceCreate,
    ServiceStepConfigUpdate,
    ServiceCreateFromTemplate,
    ServiceStats,
    ServiceUpdate,
    ServiceStepSummary,
    ServiceProgress,
    createGeneralInvoice_Body,
    GeneralInvoiceCreatedResponse,
    ServiceStepCreate,
    ServiceStepUpdate,
    ServiceStepStateUpdate,
    ServiceStepValidationStatus,
    ServiceStepComplete,
    PaymentAttemptStatus,
    PaymentAttempt,
    logServiceStepManualPayment_Body,
    reuseStepDocument_Body,
    updateServiceStepConfig_Body,
    QuotationDocumentPreview,
    sendQuotation_Body,
    createQualificationLevel_Body,
    updateQualificationLevel_Body,
    parent_id,
    MinimalSearchTypes,
    MinimalSearchItem,
    CountryCodeIso2,
    AddressBody,
    Address,
    PrettySearchItem,
    filter_assessment_level_gt_,
    filter_assessment_level_between_,
    lat,
    NullableDecimal,
    ExternalPresence,
    AgencyFactoids,
    CampusFactoids,
    PartnerPublic,
    EmailCategoryConfig,
    WorkReference,
    CompanyMetadata,
    SignupReferral,
    Agency,
    Contact,
    AccountStatus,
    UserPublic,
    Handler,
    Company,
    CompanyBranch,
    PublicPage,
    Campus,
    FeesScheduleType,
    RegularIntervalUnit,
    FeesSchedule,
    FeesStructure,
    Month,
    MethodOfDelivery,
    Package,
    PackagedProgram,
    Program,
    Gender,
    NullableDate,
    Permission,
    Role,
    User,
    given_name,
    additional_name,
    family_name,
    updateUser_Body,
    createSubAgent_Body,
    updateSubAgent_Body,
    changePassword_Body,
    business_identification,
    cricos_number,
    trading_name,
    rto_number,
    accepting_association_requests,
    assessment_level,
    updateCompany_Body,
    assignHandler_Body,
    createCompanyBranch_Body,
    email_category_config,
    status,
    updateCompanyBranch_Body,
    updateSignupReferralPlatform_Body,
    createCompanyBranchUser_Body,
    BankDetail,
    account_name,
    account_number,
    account_bsb,
    payment_plus_id,
    createBankDetails_Body,
    createRole_Body,
    syncPermissionsOfRole_Body,
    permissionId,
    LanguageCodeIso2,
    Language,
    Currency,
    name__2,
    CountryCodeIso3,
    PrincipalSubdivisionCode,
    PrincipalSubdivision,
    Country,
    City,
    PostalCode,
    DefinedWebView,
    WebViewRequirements,
    WebView,
    updateWebView_Body,
    login_Body,
    registerStudent_Body,
    registerAgency_Body,
    registerSchool_Body,
    resetPassword_Body,
    id,
    AgreementType,
    AgreementStatus,
    ProgramCommission,
    AgreementModification,
    Agreement,
    OnboardingProgress,
    makeOnboardingProgress_Body,
    ConversationType,
    ConversationMeta,
    ConversationParticipant,
    ConversationAttachment,
    ConversationMessage,
    MinimalApplication,
    Conversation,
    ConversationContact,
    addConversationParticipant_Body,
    IdPaginatedResponse,
    sendConversationMessage_Body,
    createIntraCompanyConversation_Body,
    MaritalStatus,
    StudentPersonalDetails,
    Student,
    linkAgency_Body,
    country_of_citizenship,
    passport_id,
    passport_expiry_date,
    passport_issue_date,
    first_language,
    country_of_birth,
    place_of_birth,
    marital_status,
    gender,
    recognition_of_prior_learning,
    visa_type,
    visa_grant_number,
    contact_email,
    contact_phone,
    phone_emergency,
    phone_emergency_name,
    phone_emergency_relationship,
    updatePersonalDetails_Body,
    DateUtc,
    AttendedInstitution,
    hidden,
    institution_name,
    institution_address,
    institution_country,
    institution_principal_subdivision,
    course_name,
    course_started_on,
    course_ended_on,
    areas_of_study,
    graduation_status,
    is_highest_qualification,
    language_of_instruction,
    qualification_earned,
    remarks,
    qualification_level_id,
    education_level,
    grading_scheme,
    grade_average,
    media_links,
    addAttendedInstitution_Body,
    transcript_url,
    marksheet_url,
    certificate_url,
    updateAttendedInstitution_Body,
    LanguageTest,
    test_type,
    test_unique_id,
    other_test_name,
    reading_score,
    listening_score,
    writing_score,
    speaking_score,
    overall_score,
    exam_result_url,
    addLanguageTest_Body,
    updateLanguageTest_Body,
    EmploymentRecord,
    employer_name,
    employer_address,
    employer_contact,
    employment_position,
    employment_started_on,
    employment_ended_on,
    responsibilities,
    media,
    addEmploymentRecord_Body,
    updateEmploymentRecord_Body,
    StudentResume,
    address,
    email,
    phone,
    career_objective,
    key_skills,
    setStudentResume_Body,
    FamilyRelationship,
    FamilyMember,
    FamilyMemberCreateRequest,
    updateFamilyMember_Body,
    FamilyMemberSyncRequest,
    EventRegistrationConfig,
    AdvancedPostMeta,
    EventRegistrant,
    AdvancedPost,
    post_type,
    content,
    summary,
    event_start_at,
    event_end_at,
    event_location,
    company_branch_ids,
    meta,
    addPost_Body,
    updatePost_Body,
    registerForPost_Body,
    PayoutPrediction,
    CompanyPayoutStatus,
    CompanyPayout,
    CompanyPayoutBatch,
    CreatePublicPage,
    updateCampus_Body,
    duration_weeks,
    regular_interval_width,
    regular_interval_unit,
    number_of_payments,
    application_fees,
    material_fees,
    tuition_fees,
    commission_bonus,
    discount,
    advance_fees,
    fees_currency,
    addProgram_Body,
    updateProgram_Body,
    MockPaymentPlanConfig,
    MockPaymentItem,
    addPackage_Body,
    updatePackage_Body,
    orderPackagePrograms_Body,
    syncPackagePrograms_Body,
    addPackageProgram_Body,
    updatePackageProgram_Body,
    updateAgency_Body,
    UpdatePublicPage,
    addMedia_Body,
    TusMediaConversion,
    addMediaTus_Body,
    collection_name,
    updateMedia_Body,
    updateSignature_Body,
    OfferLetterStatus,
    OfferLetter,
    PaymentItem,
    PaymentPlan,
    AppliedProgram,
    DocumentRequirement,
    Application,
    addApplication_Body,
    updateApplication_Body,
    sendOfferLetter_Body,
    sendConfirmationOfEnrolment_Body,
    ApplicationStatus,
    ApplicationEvent,
    document_type_id,
    addDocumentRequirement_Body,
    updateDocumentRequirement_Body,
    denyDocumentRequirement_Body,
    AgreementTarget,
    AgreementTemplate,
    contents,
    automatically_assign,
    target,
    internal_name,
    offer_name,
    offer_percentage,
    offer_days,
    createAgreementTemplate_Body,
    getRenderedAgreementTemplate_Body,
    validity_days,
    updateAgreementTemplate_Body,
    PlatformReferral,
    addPlatformReferral_Body,
    updatePlatformReferral_Body,
    receiver_company_id,
    valid_upto,
    addAgreement_Body,
    addAgreementFromTemplate_Body,
    updateAgreement_Body,
    AssociationRequestStatus,
    AgentApplication,
    AssociationRequest,
    addAssociation_Body,
    sendAgreement_Body,
    SummarizedPaymentPlan,
    addPaymentAttemptToPlan_Body,
    AttemptRecordStatus,
    AttemptRecord,
    PaymentRecord,
    PlatformInfo,
    Settings,
    show_business_in_agency_search,
    show_business_in_school_search,
    default_currency,
    default_country,
    payment_attempt_minimum_amount,
    partner_request_retry_interval,
    platform_info,
    application_hard_expiry_days,
    application_expiry_warn_days,
    agreement_expiry_warn_days,
    payment_plan_ending_warn_days,
    payment_due_warn_days,
    payment_overdue_repeat_days,
    application_stale_days,
    autopay_upcoming_payments_days,
    becs_transaction_limit,
    dynamic,
    updateSettings_Body,
    ApplicationEnums,
    UserContext,
    Notification,
    EmailCategory,
    EmailTemplate,
    subject,
    updateEmailTemplate_Body,
    EmailRecipient,
    recipients,
    send_to_related,
    addEmailRecipient_Body,
    updateEmailRecipient_Body,
    StripePaymentMethod,
    PaymentMethodReference,
    is_primary,
    updatePaymentMethod_Body,
    sendTestEmail_Body,
    DocumentDraftField,
    DocumentDraft,
    createDocumentDraft_Body,
    updateDocumentDraft_Body,
    DocumentFieldBinding,
    TaskStatus,
    TaskType,
    Task,
    taskable_id,
    taskable_type,
    company_branch_id,
    parent_task_id,
    createTask_Body,
    updateTask_Body,
    rescheduleTask_Body,
    EditHistory,
    Comment,
    createComment_Body,
    SmartEventType,
    NoteEventData,
    ActivityEventData,
    InvoiceEventData,
    DocumentReuploadEventData,
    EmailEventData,
    SmartEventData,
    SmartEvent,
    Communication,
    body,
    createNoteSmartEvent_Body,
    createUploadSmartEvent_Body,
    DiscountLog,
    OneTimePassword,
    Morphable,
    JwtClaims,
    OtpStatus,
    DeviceId,
    Password,
    BasicAddress,
    Reactions,
    SocialReactable,
    SocialComment,
    ConversationTopics,
    CompanyUser,
    TransactionTODO,
    DocumentRequirementStatus,
    WebViewImage,
    PrettySearchTypes,
};

const apiEndpoints = makeApi([
    {
        method: 'get',
        path: '/account',
        alias: 'getAccount',
        description: `Get account information of the currently logged-in user.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['media', 'addresses', 'applications_count', 'role'])).optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[finished_onboarding]',
                type: 'Query',
                schema: filter_search_,
            },
        ],
        response: z.object({ data: User }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/account/change-password',
        alias: 'changePassword',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: changePassword_Body,
            },
        ],
        response: z.object({ data: User }),
    },
    {
        method: 'get',
        path: '/account/company',
        alias: 'getAccountCompany',
        description: `Get company.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'company_branches',
                            'campuses',
                            'agencies',
                            'media',
                            'addresses',
                            'campuses.company_branch',
                            'agencies.company_branch',
                            'agencies.media',
                            'agencies.address',
                            'agencies.addresses',
                            'campuses.media',
                            'campuses.address',
                            'campuses.programs',
                            'campuses.addresses',
                            'company_branches.campus',
                            'company_branches.agency',
                            'company_branches.campus.address',
                            'company_branches.agency.address',
                            'bank_detail',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'company.root_address',
                            'company.contact',
                            'public_page.partners',
                            'company.handlers',
                            'company.applications',
                            'company.applications_count',
                            'company.superadmin_id',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[is_onboarded]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
        ],
        response: z.object({ data: Company }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/addresses/:addressableType/:addressableId',
        alias: 'listAddresses',
        description: `Get address.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'addressableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'addressableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'filter[address_purpose]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[address_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[media]',
                type: 'Query',
                schema: filter_search_,
            },
        ],
        response: z.object({ data: z.array(Address) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `The user is not allowed to login.`,
                schema: z.object({
                    message: z.string(),
                    code: z.union([AccountStatus, z.enum(['branch:disabled', 'branch:active', 'branch:hidden'])]),
                }),
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/addresses/:addressableType/:addressableId',
        alias: 'addAddress',
        description: `Add address.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: AddressBody,
            },
            {
                name: 'addressableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'addressableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Address }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/addresses/:addressableType/:addressableId/:addressId',
        alias: 'getAddress',
        description: `Get address.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'addressableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'addressableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'addressId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'filter[address_purpose]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[address_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[media]',
                type: 'Query',
                schema: filter_search_,
            },
        ],
        response: z.object({ data: Address }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/addresses/:addressableType/:addressableId/:addressId',
        alias: 'deleteAddress',
        description: `Delete address.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'addressableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'addressableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'addressId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/addresses/:addressableType/:addressableId/:addressId',
        alias: 'updateAddress',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: AddressBody,
            },
            {
                name: 'addressableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'addressableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'addressId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Address }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/admin/send-test-email',
        alias: 'sendTestEmail',
        description: `Send test email.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: sendTestEmail_Body,
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/agencies/:agencyId',
        alias: 'getAgency',
        description: `Get agency.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'agencyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'address',
                            'branches',
                            'company_branch',
                            'media',
                            'media_collections',
                            'signup_referral',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'public_page.logo_url',
                            'public_page.partners',
                            'public_page.partnered',
                            'agency.can.update',
                        ])
                    )
                    .optional(),
            },
        ],
        response: z.object({ data: Agency }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/agencies/:agencyId',
        alias: 'updateAgency',
        description: `Update agency.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateAgency_Body,
            },
            {
                name: 'agencyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Agency }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/agencies/:agencyId',
        alias: 'deleteAgency',
        description: `Delete agency.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'agencyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/agreement-templates',
        alias: 'listAgreementTemplates',
        description: `List agreement templates.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(AgreementTemplate) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/agreement-templates',
        alias: 'createAgreementTemplate',
        description: `Add agreement template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createAgreementTemplate_Body,
            },
        ],
        response: z.object({ data: AgreementTemplate }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/agreement-templates/:agreementTemplateId',
        alias: 'getAgreementTemplate',
        description: `Get agreement template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'agreementTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: AgreementTemplate }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/agreement-templates/:agreementTemplateId',
        alias: 'updateAgreementTemplate',
        description: `Update agreement template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateAgreementTemplate_Body,
            },
            {
                name: 'agreementTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: AgreementTemplate }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/agreement-templates/:agreementTemplateId',
        alias: 'deleteAgreementTemplate',
        description: `Delete agreement template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'agreementTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/agreement-templates/get-rendered',
        alias: 'getRenderedAgreementTemplate',
        description: `Get rendered agreement template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: getRenderedAgreementTemplate_Body,
            },
        ],
        response: z.object({ data: z.object({ contents: z.string().optional() }) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/agreement-templates/scrap-tos',
        alias: 'scrapTermsOfService',
        description: `Scrap the Terms of Service.`,
        requestFormat: 'json',
        response: z.object({ data: z.string() }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/agreements',
        alias: 'listAgreements',
        description: `Get agreements.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'sender_company',
                            'receiver_company',
                            'signatures',
                            'signatures.media',
                            'program_commissions',
                            'agreement_modifications',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[sender_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[receiver_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[platform]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[other_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[valid_upto]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agreement_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[signed_at]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Agreement) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/agreements',
        alias: 'addAgreement',
        description: `Add agreement.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addAgreement_Body,
            },
        ],
        response: z.object({ data: Agreement }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/agreements/:agreementId',
        alias: 'getAgreement',
        description: `Get agreement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'agreementId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'sender_company',
                            'receiver_company',
                            'signatures',
                            'signatures.media',
                            'program_commissions',
                            'agreement_modifications',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[sender_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[receiver_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[platform]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[other_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[valid_upto]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agreement_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[signed_at]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ],
        response: z.object({ data: Agreement }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/agreements/:agreementId',
        alias: 'updateAgreement',
        description: `Update agreement.

PHP does not parse multipart/form-data bodies on PATCH requests, so
clients must send this operation as &#x60;POST&#x60; with a &#x60;_method&#x3D;PATCH&#x60;
body field (Laravel method spoofing). The request is routed and
validated as PATCH.
`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateAgreement_Body,
            },
            {
                name: 'agreementId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Agreement }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/agreements/:agreementId',
        alias: 'terminateAgreement',
        description: `Delete agreement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'agreementId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Agreement }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/agreements/from-template',
        alias: 'addAgreementFromTemplate',
        description: `Add agreement from template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addAgreementFromTemplate_Body,
            },
        ],
        response: z.object({ data: Agreement }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/applications',
        alias: 'listApplications',
        description: `Get applications.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'student',
                            'offer_letters',
                            'offer_letters.media',
                            'offer_letters.signatures',
                            'offer_letters.signatures.media',
                            'payment_plans',
                            'payment_plans.payment_items',
                            'payment_plans.payment_attempts',
                            'document_requirements',
                            'agency_staff_agreement',
                            'school_staff_agreement',
                            'agency_school_agreement',
                            'campus',
                            'agency',
                            'package',
                            'applied_programs',
                            'applied_programs.program',
                            'media_storage',
                            'media_storage.media',
                            'personal_details',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'application.handlers',
                            'application.can.view',
                            'application.can.send_offer_letter',
                            'application.can.attach_acceptance_letter',
                            'application.can.sign_offer_letter',
                            'application.can.attach_coe',
                            'application.can.submit',
                            'application.can.withdraw',
                            'application.can.reject',
                            'application.can.in_review',
                            'student.handlers',
                            'student.full_name',
                            'student.avatar_url',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[program_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agency_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[intake_year]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intake_month]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at', 'intake', '-intake']))
                    .optional(),
            },
            {
                name: 'filter[application_type]',
                type: 'Query',
                schema: z.enum(['course', 'visa']).optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Application) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/applications',
        alias: 'addApplication',
        description: `Add application.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addApplication_Body,
            },
        ],
        response: z.object({ data: Application }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/applications/:applicationId',
        alias: 'getApplication',
        description: `Get application.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'applicationId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'student',
                            'offer_letters',
                            'offer_letters.media',
                            'offer_letters.signatures',
                            'offer_letters.signatures.media',
                            'payment_plans',
                            'payment_plans.payment_items',
                            'payment_plans.payment_attempts',
                            'document_requirements',
                            'agency_staff_agreement',
                            'school_staff_agreement',
                            'agency_school_agreement',
                            'campus',
                            'agency',
                            'package',
                            'applied_programs',
                            'applied_programs.program',
                            'media_storage',
                            'media_storage.media',
                            'personal_details',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'application.handlers',
                            'application.can.view',
                            'application.can.send_offer_letter',
                            'application.can.attach_acceptance_letter',
                            'application.can.sign_offer_letter',
                            'application.can.attach_coe',
                            'application.can.submit',
                            'application.can.withdraw',
                            'application.can.reject',
                            'application.can.in_review',
                            'student.handlers',
                            'student.full_name',
                            'student.avatar_url',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[program_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agency_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[intake_year]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intake_month]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at', 'intake', '-intake']))
                    .optional(),
            },
        ],
        response: z.object({ data: Application }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/applications/:applicationId',
        alias: 'updateApplication',
        description: `Update application.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateApplication_Body,
            },
            {
                name: 'applicationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Application }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/applications/:applicationId/attach-confirmation-of-enrolment',
        alias: 'sendConfirmationOfEnrolment',
        description: `Send confirmation of enrollment.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: sendConfirmationOfEnrolment_Body,
            },
            {
                name: 'applicationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Application }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/applications/:applicationId/document-requirements',
        alias: 'getDocumentRequirements',
        description: `Get application document requirements.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'applicationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(DocumentRequirement) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/applications/:applicationId/document-requirements',
        alias: 'addDocumentRequirement',
        description: `Add application document requirement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addDocumentRequirement_Body,
            },
            {
                name: 'applicationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: DocumentRequirement }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/applications/:applicationId/events',
        alias: 'getApplicationEvents',
        description: `Get application events.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'applicationId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(ApplicationEvent) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/applications/:applicationId/send-offer-letter',
        alias: 'sendOfferLetter',
        description: `Send offer letter.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: sendOfferLetter_Body,
            },
            {
                name: 'applicationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Application }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/association-requests',
        alias: 'getAssociations',
        description: `Get association requests.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'sender_company',
                            'receiver_company',
                            'agreement',
                            'agreement.media',
                            'agreement.signatures',
                            'agreement.signatures.media',
                            'agreement.program_commissions',
                            'renewal_agreement',
                            'renewal_agreement.media',
                            'renewal_agreement.signatures',
                            'renewal_agreement.signatures.media',
                            'renewal_agreement.program_commissions',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[sender_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[receiver_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[other_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[flow_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ],
        response: PaginatedResponse.and(
            z.object({ accepting_association_requests: z.boolean().optional(), data: z.array(AssociationRequest) })
        ),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/association-requests',
        alias: 'addAssociation',
        description: `Add association request.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addAssociation_Body,
            },
        ],
        response: z.object({ data: AssociationRequest }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/association-requests/:associationId',
        alias: 'getAssociation',
        description: `Get association request.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'associationId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'sender_company',
                            'receiver_company',
                            'agreement',
                            'agreement.media',
                            'agreement.signatures',
                            'agreement.signatures.media',
                            'agreement.program_commissions',
                            'renewal_agreement',
                            'renewal_agreement.media',
                            'renewal_agreement.signatures',
                            'renewal_agreement.signatures.media',
                            'renewal_agreement.program_commissions',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[sender_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[receiver_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[other_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[flow_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ],
        response: z.object({ data: AssociationRequest }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/association-requests/:associationId',
        alias: 'deleteAssociation',
        description: `Reject or cancel association request.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'associationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: AssociationRequest }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/association-requests/:associationId/send-agreement',
        alias: 'sendAgreement',
        description: `Send agreement.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: sendAgreement_Body,
            },
            {
                name: 'associationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.object({ agreement: Agreement, association_request: AssociationRequest }) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/attempt-records',
        alias: 'getAttemptRecords',
        description: `Get attempt records.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[payment_item_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(AttemptRecord) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/attended-institutions/:documentId',
        alias: 'getAttendedInstitution',
        description: `Get attended institution.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('media')).optional(),
            },
        ],
        response: z.object({ data: AttendedInstitution }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/attended-institutions/:documentId',
        alias: 'updateAttendedInstitution',
        description: `Update attended institution.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateAttendedInstitution_Body,
            },
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: AttendedInstitution }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/attended-institutions/:documentId',
        alias: 'deleteAttendedInstitution',
        description: `Delete attended institution.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/auth/forgot-password',
        alias: 'forgotPassword',
        description: `Initiate forgot password process.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: z.object({ email: z.string().email() }),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/auth/login',
        alias: 'login',
        description: `Logins to the application, creating a new session, if provided correct credentials. Returns a JWT token.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: login_Body,
            },
        ],
        response: z.object({ token: z.string(), me: User }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `The user is not allowed to login.`,
                schema: z.object({
                    message: z.string(),
                    code: z.union([AccountStatus, z.enum(['branch:disabled', 'branch:active', 'branch:hidden'])]),
                }),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/auth/password-reset',
        alias: 'resetPassword',
        description: `Reset password.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: resetPassword_Body,
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/auth/refresh',
        alias: 'refreshJwt',
        description: `Refresh the JWT token.`,
        requestFormat: 'json',
        response: z.object({ token: z.string() }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `The user is not allowed to login.`,
                schema: z.object({
                    message: z.string(),
                    code: z.union([AccountStatus, z.enum(['branch:disabled', 'branch:active', 'branch:hidden'])]),
                }),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/auth/register/agency',
        alias: 'registerAgency',
        description: `Register as an agency.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: registerAgency_Body,
            },
        ],
        response: z.object({}).partial().passthrough(),
        errors: [
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/auth/register/school',
        alias: 'registerSchool',
        description: `Register as a school.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: registerSchool_Body,
            },
        ],
        response: z.object({}).partial().passthrough(),
        errors: [
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/auth/register/student',
        alias: 'registerStudent',
        description: `Register as a student.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: registerStudent_Body,
            },
        ],
        response: z
            .object({ user: z.object({ id: Id.int(), company_id: Id.int(), student_id: NullableId }).partial() })
            .partial(),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/bank-details/:bankableType/:bankableId',
        alias: 'getBankDetails',
        description: `Get bank details.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'bankableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'bankableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.union([z.null(), BankDetail]) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/bank-details/:bankableType/:bankableId',
        alias: 'createBankDetails',
        description: `Create bank details.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createBankDetails_Body,
            },
            {
                name: 'bankableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'bankableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: BankDetail }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/campuses/:campusId',
        alias: 'getCampus',
        description: `Get campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(z.enum(['address', 'branches', 'company_branch', 'media', 'media_collections']))
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'public_page.logo_url',
                            'public_page.partners',
                            'public_page.partnered',
                            'campus.assessment_level',
                            'campus.company_name',
                            'campus.can.update',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
        ],
        response: z.object({ data: Campus }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/campuses/:campusId',
        alias: 'updateCampus',
        description: `Update campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateCampus_Body,
            },
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Campus }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/campuses/:campusId',
        alias: 'deleteCampus',
        description: `Delete campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/campuses/:campusId/packages',
        alias: 'getCampusPackages',
        description: `Get the packages of a campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'campuses',
                            'campuses.address',
                            'campuses.media',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'packaged_programs',
                            'packaged_programs.program',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'package.logo_url',
                            'package.partnered',
                            'package.company_name',
                            'package.assessment_level',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[id]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
        ],
        response: z.object({ data: z.array(Package) }),
    },
    {
        method: 'delete',
        path: '/campuses/:campusId/packages/:packageId',
        alias: 'removeCampusPackage',
        description: `Remove a package from a campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
    },
    {
        method: 'put',
        path: '/campuses/:campusId/packages/:packageId',
        alias: 'addCampusPackage',
        description: `Add a package to a campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Package }),
    },
    {
        method: 'get',
        path: '/campuses/:campusId/programs',
        alias: 'getCampusPrograms',
        description: `Get the programs of a campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'media_collections',
                            'campuses',
                            'campuses.address',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'campuses.media',
                            'packaged_programs',
                            'packaged_programs.package',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'program.company_name',
                            'program.logo_url',
                            'program.partnered',
                            'program.assessment_level',
                            'program.applications_count',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[partnered]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[principal_subdivision_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Program) })),
    },
    {
        method: 'delete',
        path: '/campuses/:campusId/programs/:programId',
        alias: 'removeCampusProgram',
        description: `Remove a program from a campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'programId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
    },
    {
        method: 'put',
        path: '/campuses/:campusId/programs/:programId',
        alias: 'addCampusProgram',
        description: `Add a program to a campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'programId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Program }),
    },
    {
        method: 'get',
        path: '/charts/applications-over-time',
        alias: 'getApplicationsOverTime',
        description: `Get applications over time.`,
        requestFormat: 'json',
        response: z.object({
            data: z.object({
                created: z.array(z.object({ date: DateTime, count: z.number().int() })),
                visa: z.array(z.object({ date: DateTime, count: z.number().int() })),
            }),
        }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/charts/earnings-over-time',
        alias: 'getEarningsOverTime',
        description: `Get earnings over time.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(z.object({ date: DateTime, amount: Decimal })) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/charts/new-students-over-time',
        alias: 'getNewStudentsOverTime',
        description: `Get new students over time.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(z.object({ date: DateTime, count: z.number().int() })) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/cities',
        alias: 'listCities',
        requestFormat: 'json',
        parameters: [
            {
                name: 'subdivision_code',
                type: 'Query',
                schema: name__2,
            },
            {
                name: 'name',
                type: 'Query',
                schema: name__2,
            },
            {
                name: 'country_code',
                type: 'Query',
                schema: z.string().min(2).max(2).optional(),
            },
        ],
        response: z.object({ data: z.array(City) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/comments/:comment',
        alias: 'getComment',
        requestFormat: 'json',
        parameters: [
            {
                name: 'comment',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Comment }),
    },
    {
        method: 'get',
        path: '/communications/:communication',
        alias: 'getCommunication',
        description: `Returns a logged email communication, including the rendered HTML body. Authorised to staff who can view the related student/application/service.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'communication',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: Communication,
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/companies',
        alias: 'listCompanies',
        description: `Get companies.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'company_branches',
                            'campuses',
                            'agencies',
                            'media',
                            'addresses',
                            'campuses.company_branch',
                            'agencies.company_branch',
                            'agencies.media',
                            'agencies.address',
                            'agencies.addresses',
                            'campuses.media',
                            'campuses.address',
                            'campuses.programs',
                            'campuses.addresses',
                            'company_branches.campus',
                            'company_branches.agency',
                            'company_branches.campus.address',
                            'company_branches.agency.address',
                            'bank_detail',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'company.root_address',
                            'company.contact',
                            'public_page.partners',
                            'company.handlers',
                            'company.applications',
                            'company.applications_count',
                            'company.superadmin_id',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[is_onboarded]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Company) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/companies/:companyId',
        alias: 'getCompany',
        description: `Get company.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'company_branches',
                            'campuses',
                            'agencies',
                            'media',
                            'addresses',
                            'campuses.company_branch',
                            'agencies.company_branch',
                            'agencies.media',
                            'agencies.address',
                            'agencies.addresses',
                            'campuses.media',
                            'campuses.address',
                            'campuses.programs',
                            'campuses.addresses',
                            'company_branches.campus',
                            'company_branches.agency',
                            'company_branches.campus.address',
                            'company_branches.agency.address',
                            'bank_detail',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'company.root_address',
                            'company.contact',
                            'public_page.partners',
                            'company.handlers',
                            'company.applications',
                            'company.applications_count',
                            'company.superadmin_id',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[is_onboarded]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
        ],
        response: z.object({ data: Company }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/companies/:companyId',
        alias: 'updateCompany',
        description: `Update company.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateCompany_Body,
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Company }),
    },
    {
        method: 'get',
        path: '/companies/:companyId/accounts/balance',
        alias: 'getAccountBalance',
        description: `Get Account balance.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('balance.earnings')).optional(),
            },
        ],
        response: z.object({
            data: z.object({
                earnings: Decimal.optional(),
                available: z.array(z.object({ amount: z.string(), currency: z.string() })),
            }),
        }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/companies/:companyId/agencies',
        alias: 'getAgencies',
        description: `Get agencies.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'address',
                            'branches',
                            'company_branch',
                            'media',
                            'media_collections',
                            'signup_referral',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'public_page.logo_url',
                            'public_page.partners',
                            'public_page.partnered',
                            'agency.can.update',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Agency) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/companies/:companyId/agencies',
        alias: 'addAgency',
        description: `Add agency.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: CreatePublicPage,
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Agency }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/companies/:companyId/campuses',
        alias: 'getCampuses',
        description: `Get campuses.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(z.enum(['address', 'branches', 'company_branch', 'media', 'media_collections']))
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'public_page.logo_url',
                            'public_page.partners',
                            'public_page.partnered',
                            'campus.assessment_level',
                            'campus.company_name',
                            'campus.can.update',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Campus) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/companies/:companyId/campuses',
        alias: 'addCampus',
        description: `Add campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: CreatePublicPage,
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Campus }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/companies/:companyId/company-branches',
        alias: 'getCompanyBranches',
        description: `Get company branches.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(CompanyBranch) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/companies/:companyId/company-branches',
        alias: 'createCompanyBranch',
        description: `Create company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createCompanyBranch_Body,
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: CompanyBranch }),
    },
    {
        method: 'get',
        path: '/companies/:companyId/payout-eligibility',
        alias: 'checkPayoutEligibility',
        description: `Check if a company is eligible for payouts.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.object({ errors: z.array(z.string()).optional(), eligible: z.boolean() }) }),
    },
    {
        method: 'get',
        path: '/companies/:companyId/payouts',
        alias: 'getPayouts',
        description: `Get payouts.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(CompanyPayout) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/companies/:companyId/platform-referral',
        alias: 'getPlatformReferralByCompany',
        description: `Get platform referral.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ],
        response: z.object({ data: PlatformReferral }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/companies/:companyId/programs',
        alias: 'getPrograms',
        description: `Get programs.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'media_collections',
                            'campuses',
                            'campuses.address',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'campuses.media',
                            'packaged_programs',
                            'packaged_programs.package',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'program.company_name',
                            'program.logo_url',
                            'program.partnered',
                            'program.assessment_level',
                            'program.applications_count',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[partnered]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[principal_subdivision_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Program) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/companies/:companyId/programs',
        alias: 'addProgram',
        description: `Add program.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addProgram_Body,
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Program }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/companies/:companyId/public-page',
        alias: 'getPublicPage',
        description: `Get public page.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(z.enum(['address', 'branches', 'company_branch', 'media', 'media_collections']))
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'public_page.logo_url',
                            'public_page.partners',
                            'public_page.partnered',
                        ])
                    )
                    .optional(),
            },
        ],
        response: z.object({ data: PublicPage }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/companies/:companyId/public-page',
        alias: 'updatePublicPage',
        description: `Update public page.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: UpdatePublicPage,
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: PublicPage }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/companies/:companyId/stripe/onboarding',
        alias: 'getStripeOnboardingLink',
        description: `Get Stripe onboarding link.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({
            data: z.object({ url: z.string().url(), expires_at: z.string().datetime({ offset: true }) }).partial(),
        }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/companies/:companyId/stripe/onboarding',
        alias: 'completeStripeOnboarding',
        description: `Complete Stripe onboarding.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Company }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/companies/:companyId/toggle-bank-details-lock',
        alias: 'toggleBankDetailsLock',
        description: `Toggle bank details lock.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Company }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/companies/:companyId/update-abn-info',
        alias: 'updateAbnInformation',
        description: `Update ABN information.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Company }),
    },
    {
        method: 'get',
        path: '/company-branches/:branchId',
        alias: 'getCompanyBranch',
        description: `Get company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: CompanyBranch }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/company-branches/:branchId',
        alias: 'updateCompanyBranch',
        description: `Update company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateCompanyBranch_Body,
            },
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: CompanyBranch }),
    },
    {
        method: 'get',
        path: '/company-branches/:branchId/email-recipients',
        alias: 'getEmailRecipients',
        description: `Get email recipients.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ],
        response: z.object({ data: z.array(EmailRecipient) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/company-branches/:branchId/email-recipients',
        alias: 'addEmailRecipient',
        description: `Add email recipient.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addEmailRecipient_Body,
            },
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: EmailRecipient }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/company-branches/:branchId/managed-companies',
        alias: 'getManagedCompaniesOfCompanyBranch',
        description: `Get managed companies of a company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(Company) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'put',
        path: '/company-branches/:branchId/managed-companies/:companyId',
        alias: 'attachCompanyToCompanyBranch',
        description: `Attach company to company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Company }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/company-branches/:branchId/managed-companies/:companyId',
        alias: 'detachCompanyFromCompanyBranch',
        description: `Detach company from company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/company-branches/:branchId/signup-referrals',
        alias: 'listSignupReferralsOfCompanyBranch',
        description: `List signup referrals of a company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(SignupReferral) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/company-branches/:branchId/signup-referrals',
        alias: 'createSignupReferralForCompanyBranch',
        description: `Create signup referral for a company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: z.object({ enabled: z.boolean() }),
            },
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: SignupReferral }),
    },
    {
        method: 'get',
        path: '/company-branches/:branchId/users',
        alias: 'listCompanyBranchUsers',
        description: `Get company branch users.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'addresses',
                            'applications_count',
                            'role',
                            'media_storage',
                            'media_storage.media',
                            'personal_details',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('user.applications_count')).optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[finished_onboarding]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[passport_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agency_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[not_agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[managed_by]',
                type: 'Query',
                schema: filter_search_,
            },
        ],
        response: z.object({ data: z.array(User) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/company-branches/:branchId/users',
        alias: 'createCompanyBranchUser',
        description: `Create company branch user.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createCompanyBranchUser_Body,
            },
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: User }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/conversation-attachments/:attachment/download',
        alias: 'downloadConversationAttachment',
        description: `Download a conversation attachment.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attachment',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'expires',
                type: 'Query',
                schema: z.number().int().optional(),
            },
            {
                name: 'signature',
                type: 'Query',
                schema: z.string().optional(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/conversation-attachments/:attachmentId',
        alias: 'getConversationAttachment',
        description: `Get a conversation attachment.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attachmentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ConversationAttachment }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/conversation-attachments/:attachmentId',
        alias: 'deleteConversationAttachment',
        description: `Delete a conversation attachment.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attachmentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/conversation-messages/:message',
        alias: 'getConversationMessage',
        description: `Get a message of a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('attachments')).optional(),
            },
            {
                name: 'message',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ConversationMessage }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/conversation-messages/:message',
        alias: 'deleteConversationMessage',
        description: `Delete a message of a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'message',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/conversation-messages/:message/conversation-attachments',
        alias: 'getConversationMessageAttachments',
        description: `Get the attachments of a message.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'message',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(ConversationAttachment) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/conversation-messages/:message/mark-read',
        alias: 'markConversationMessageRead',
        description: `Mark a message of a conversation as read.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'message',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ConversationParticipant }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/conversation-messages/:message/mark-received',
        alias: 'markConversationMessageReceived',
        description: `Mark a message of a conversation as received.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'message',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ConversationParticipant }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/conversations',
        alias: 'listConversations',
        description: `Get the conversations of the current user.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'recent_messages',
                            'participants',
                            'latest_message_id',
                            'my_participant',
                            'company_branches',
                            'companies',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[text]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[conversation_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[participant_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum(['created_at', '-created_at', 'updated_at', '-updated_at', 'closed_at', '-closed_at'])
                    )
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Conversation) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/conversations/:conversation',
        alias: 'getConversation',
        description: `Get a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'recent_messages',
                            'participants',
                            'latest_message_id',
                            'my_participant',
                            'company_branches',
                            'companies',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[text]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[conversation_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[participant_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum(['created_at', '-created_at', 'updated_at', '-updated_at', 'closed_at', '-closed_at'])
                    )
                    .optional(),
            },
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Conversation }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/conversations/:conversation/contacts',
        alias: 'getConversationContacts',
        description: `Get the contacts of a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(ConversationContact) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/conversations/:conversation/conversation-attachments',
        alias: 'getConversationAttachments',
        description: `Get the attachments of a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(ConversationAttachment) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/conversations/:conversation/conversation-messages',
        alias: 'getConversationMessages',
        description: `Get the messages of a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('attachments')).optional(),
            },
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'limit',
                type: 'Query',
                schema: z.number().int().gte(10).lte(500).optional(),
            },
            {
                name: 'after_id',
                type: 'Query',
                schema: z.number().int().gte(0).optional().default(0),
            },
            {
                name: 'before_id',
                type: 'Query',
                schema: z.number().int().gte(0).optional().default(0),
            },
        ],
        response: IdPaginatedResponse.and(z.object({ data: z.array(ConversationMessage) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/conversations/:conversation/conversation-messages',
        alias: 'sendConversationMessage',
        description: `Send a message to a conversation.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                description: `The message to send.`,
                type: 'Body',
                schema: sendConversationMessage_Body,
            },
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ConversationMessage }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/conversations/:conversation/participants',
        alias: 'getConversationParticipants',
        description: `Get the participants of a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('user')).optional(),
            },
        ],
        response: z.object({ data: z.array(ConversationParticipant) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/conversations/:conversation/participants',
        alias: 'addConversationParticipant',
        description: `Add a participant to a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The participant to add.`,
                type: 'Body',
                schema: addConversationParticipant_Body,
            },
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ConversationParticipant }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/conversations/:conversation/participants/:participant',
        alias: 'removeConversationParticipant',
        description: `Remove a participant from a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'participant',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ConversationParticipant }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/conversations/intra-company',
        alias: 'createIntraCompanyConversation',
        description: `Create an intra-company conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createIntraCompanyConversation_Body,
            },
        ],
        response: z.object({ data: Conversation }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/conversations/intra-company/contacts',
        alias: 'getIntraCompanyConversationContacts',
        description: `Get the contacts of an intra-company conversation.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(ConversationContact) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/conversations/partner/:companyId',
        alias: 'getPartnerConversation',
        description: `Get a conversation between two company branches.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'recent_messages',
                            'participants',
                            'latest_message_id',
                            'my_participant',
                            'company_branches',
                            'companies',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[text]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[conversation_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[participant_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum(['created_at', '-created_at', 'updated_at', '-updated_at', 'closed_at', '-closed_at'])
                    )
                    .optional(),
            },
        ],
        response: z.object({ data: Conversation }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'put',
        path: '/conversations/partner/:companyId',
        alias: 'createPartnerConversation',
        description: `Create a conversation between two company branches.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Conversation }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/countries',
        alias: 'listCountries',
        requestFormat: 'json',
        parameters: [
            {
                name: 'name',
                type: 'Query',
                schema: name__2,
            },
        ],
        response: z.object({ data: z.array(Country) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/countries/:country_code_iso2/principal-subdivisions',
        alias: 'listPrincipalSubdivisions',
        requestFormat: 'json',
        parameters: [
            {
                name: 'country_code_iso2',
                type: 'Path',
                schema: z.string().min(2).max(2),
            },
            {
                name: 'name',
                type: 'Query',
                schema: name__2,
            },
        ],
        response: z.object({ data: z.array(PrincipalSubdivision) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/currencies',
        alias: 'listCurrencies',
        requestFormat: 'json',
        response: z.object({ data: z.array(Currency) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/discount-logs/:discountableType/:discountableId',
        alias: 'listDiscountLogs',
        description: `Returns the discount change audit trail for a discountable entity.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'discountableType',
                type: 'Path',
                schema: z.enum(['program', 'payment_plan', 'applied_program', 'packaged_program']),
            },
            {
                name: 'discountableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.object({ data: z.array(DiscountLog) }).partial() }),
    },
    {
        method: 'get',
        path: '/document-drafts',
        alias: 'getAllDocumentDrafts',
        requestFormat: 'json',
        response: z.object({ data: z.array(DocumentDraft) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/document-drafts',
        alias: 'createDocumentDraft',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createDocumentDraft_Body,
            },
        ],
        response: z.object({ data: DocumentDraft }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/document-drafts/:id',
        alias: 'getDocumentDraft',
        requestFormat: 'json',
        parameters: [
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: DocumentDraft }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/document-drafts/:id',
        alias: 'updateDocumentDraft',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateDocumentDraft_Body,
            },
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: DocumentDraft }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/document-field-bindings',
        alias: 'getDocumentFieldBindings',
        requestFormat: 'json',
        response: z.object({ success: z.boolean(), data: z.array(DocumentFieldBinding) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/document-requirements/:requirementId',
        alias: 'getDocumentRequirement',
        description: `Get document requirement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'requirementId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: DocumentRequirement }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/document-requirements/:requirementId',
        alias: 'updateDocumentRequirement',
        description: `Update document requirement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateDocumentRequirement_Body,
            },
            {
                name: 'requirementId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: DocumentRequirement }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/document-requirements/:requirementId',
        alias: 'deleteDocumentRequirement',
        description: `Delete document requirement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'requirementId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/document-requirements/:requirementId/approve',
        alias: 'approveDocumentRequirement',
        description: `Approve document requirement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'requirementId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: DocumentRequirement }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/document-requirements/:requirementId/deny',
        alias: 'denyDocumentRequirement',
        description: `Deny document requirement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: denyDocumentRequirement_Body,
            },
            {
                name: 'requirementId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: DocumentRequirement }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/document-types',
        alias: 'listDocumentTypes',
        requestFormat: 'json',
        response: z.object({ data: z.array(DocumentType) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/document-types',
        alias: 'createDocumentType',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The document type to create.`,
                type: 'Body',
                schema: createDocumentType_Body,
            },
        ],
        response: z.object({ data: DocumentType }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/document-types/:documentId',
        alias: 'getDocumentType',
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: DocumentType }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/document-types/:documentId',
        alias: 'updateDocumentType',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the document type.`,
                type: 'Body',
                schema: updateDocumentType_Body,
            },
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: DocumentType }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/document-types/:documentId',
        alias: 'deleteDocumentType',
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/documentables/:documentableType/:documentableId/attended-institutions',
        alias: 'listAttendedInstitutions',
        description: `List attended institutions.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('media')).optional(),
            },
        ],
        response: z.object({ data: z.array(AttendedInstitution) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/documentables/:documentableType/:documentableId/attended-institutions',
        alias: 'addAttendedInstitution',
        description: `Add attended institution.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addAttendedInstitution_Body,
            },
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: AttendedInstitution }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/documentables/:documentableType/:documentableId/employment-records',
        alias: 'listEmploymentRecords',
        description: `List employment records.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(EmploymentRecord) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/documentables/:documentableType/:documentableId/employment-records',
        alias: 'addEmploymentRecord',
        description: `Add employment record.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addEmploymentRecord_Body,
            },
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: EmploymentRecord }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/documentables/:documentableType/:documentableId/language-tests',
        alias: 'listLanguageTests',
        description: `Get language tests.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('media')).optional(),
            },
        ],
        response: z.object({ data: z.array(LanguageTest) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/documentables/:documentableType/:documentableId/language-tests',
        alias: 'addLanguageTest',
        description: `Add language test.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addLanguageTest_Body,
            },
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: LanguageTest }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/documentables/:documentableType/:documentableId/personal-details',
        alias: 'getPersonalDetails',
        description: `Get personal details.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: StudentPersonalDetails }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'put',
        path: '/documentables/:documentableType/:documentableId/personal-details',
        alias: 'updatePersonalDetails',
        description: `Update personal details.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updatePersonalDetails_Body,
            },
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: StudentPersonalDetails }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/email-recipients/:recipientId',
        alias: 'getEmailRecipient',
        description: `Get email recipient.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'recipientId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ],
        response: z.object({ data: EmailRecipient }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/email-recipients/:recipientId',
        alias: 'updateEmailRecipient',
        description: `Update email recipient.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateEmailRecipient_Body,
            },
            {
                name: 'recipientId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: EmailRecipient }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/email-recipients/:recipientId',
        alias: 'deleteEmailRecipient',
        description: `Delete email recipient.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'recipientId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/email-templates',
        alias: 'getEmailTemplates',
        description: `Get email templates.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(EmailTemplate) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/email-templates/:emailTemplateId',
        alias: 'getEmailTemplate',
        description: `Get email template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'emailTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: EmailTemplate }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/email-templates/:emailTemplateId',
        alias: 'updateEmailTemplate',
        description: `Update email template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateEmailTemplate_Body,
            },
            {
                name: 'emailTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: EmailTemplate }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/employment-records/:documentId',
        alias: 'getEmploymentRecord',
        description: `Get employment record.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: EmploymentRecord }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/employment-records/:documentId',
        alias: 'updateEmploymentRecord',
        description: `Update employment record.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateEmploymentRecord_Body,
            },
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: EmploymentRecord }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/employment-records/:documentId',
        alias: 'deleteEmploymentRecord',
        description: `Delete employment record.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/family-members/:familyMemberId',
        alias: 'getFamilyMember',
        requestFormat: 'json',
        parameters: [
            {
                name: 'familyMemberId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: FamilyMember }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'put',
        path: '/family-members/:familyMemberId',
        alias: 'updateFamilyMember',
        description: `Only the relationship is editable here. Names, passport and contact fields live on the personal-details record — update them via &#x60;PUT /documentables/family_member/{id}/personal-details&#x60;.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateFamilyMember_Body,
            },
            {
                name: 'familyMemberId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: FamilyMember }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/family-members/:familyMemberId',
        alias: 'deleteFamilyMember',
        description: `Soft-deletes the family member and detaches it from any linked visa or insurance records.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'familyMemberId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/handlers/:handleableType/:handleableId',
        alias: 'assignHandler',
        description: `Assign manager to company.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: assignHandler_Body,
            },
            {
                name: 'handleableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'handleableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Handler }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/handlers/:handlerId',
        alias: 'removeHandler',
        description: `Remove manager from company.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'handlerId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/health',
        alias: 'getHealth',
        requestFormat: 'json',
        response: z.void(),
        errors: [
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/insurance-policies/:insurancePolicyId/family-members',
        alias: 'listInsurancePolicyFamilyMembers',
        requestFormat: 'json',
        parameters: [
            {
                name: 'insurancePolicyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(FamilyMember) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'put',
        path: '/insurance-policies/:insurancePolicyId/family-members',
        alias: 'syncInsurancePolicyFamilyMembers',
        description: `Replaces the covered set with the given IDs. Every ID must belong to the policy&#x27;s own student. Returns the resulting set.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: FamilyMemberSyncRequest,
            },
            {
                name: 'insurancePolicyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(FamilyMember) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/language-tests/:testId',
        alias: 'getLanguageTest',
        description: `Get language test.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'testId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('media')).optional(),
            },
        ],
        response: z.object({ data: LanguageTest }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/language-tests/:testId',
        alias: 'updateLanguageTest',
        description: `Update language test.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateLanguageTest_Body,
            },
            {
                name: 'testId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: LanguageTest }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/language-tests/:testId',
        alias: 'deleteLanguageTest',
        description: `Delete language test.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'testId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/languages',
        alias: 'listLanguages',
        requestFormat: 'json',
        response: z.object({ data: z.array(Language) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/media-collections/:modelType/:modelId',
        alias: 'listMediaCollections',
        description: `List all media collections for an entity.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'modelType',
                type: 'Path',
                schema: z.string(),
            },
            {
                name: 'modelId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(MediaCollection) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/media-links/:linkableType/:linkableId',
        alias: 'listMediaLinks',
        description: `List all media links for an entity.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'linkableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'linkableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(MediaLink) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/media/:attachableType/:attachableId',
        alias: 'listMedia',
        description: `List all media for an entity.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attachableType',
                type: 'Path',
                schema: z.string(),
            },
            {
                name: 'attachableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'filter[collection_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[mime_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[document_type_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ],
        response: z.object({ data: z.array(Media) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `The user is not allowed to login.`,
                schema: z.object({
                    message: z.string(),
                    code: z.union([AccountStatus, z.enum(['branch:disabled', 'branch:active', 'branch:hidden'])]),
                }),
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/media/:attachableType/:attachableId',
        alias: 'addMedia',
        description: `Add media.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addMedia_Body,
            },
            {
                name: 'attachableType',
                type: 'Path',
                schema: z.string(),
            },
            {
                name: 'attachableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(Media) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/media/:attachableType/:attachableId/download-all',
        alias: 'getMediaDownloadAllUrl',
        description: `Get URL that lets you download all media for an entity.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attachableType',
                type: 'Path',
                schema: z.string(),
            },
            {
                name: 'attachableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.object({ download_url: z.string().url() }).partial() }),
    },
    {
        method: 'post',
        path: '/media/:attachableType/:attachableId/tus',
        alias: 'addMediaTus',
        description: `Add media.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addMediaTus_Body,
            },
            {
                name: 'attachableType',
                type: 'Path',
                schema: z.string(),
            },
            {
                name: 'attachableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(Media) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/media/:mediaId',
        alias: 'showMedia',
        description: `Get a single media by ID.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'mediaId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Media }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/media/:mediaId',
        alias: 'updateMedia',
        description: `Update media.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateMedia_Body,
            },
            {
                name: 'mediaId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Media }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/media/:mediaId',
        alias: 'deleteMedia',
        description: `Delete media.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'mediaId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(Media) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'put',
        path: '/media/:mediaId/link/:linkableType/:linkableId',
        alias: 'linkMedia',
        description: `Link media to a linkable entity.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'mediaId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'linkableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'linkableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: MediaLink }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/media/:mediaId/unlink/:linkableType/:linkableId',
        alias: 'unlinkMedia',
        description: `Unlink media from a linkable entity.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'mediaId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'linkableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'linkableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: MediaLink }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/mock-payment-plan',
        alias: 'getMockPaymentPlan',
        description: `Get mock payment plan.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: MockPaymentPlanConfig,
            },
        ],
        response: z.object({ data: z.object({ items: z.array(MockPaymentItem), config: MockPaymentPlanConfig }) }),
    },
    {
        method: 'get',
        path: '/notifications',
        alias: 'getNotifications',
        description: `Get notifications.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('meta')).optional(),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
        ],
        response: PaginatedResponse.and(
            z.object({ total_unread: z.number().int().optional(), data: z.array(Notification) })
        ),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/notifications/:notificationId',
        alias: 'getNotification',
        description: `Get notification.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('meta')).optional(),
            },
        ],
        response: z.object({ data: Notification }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/notifications/:notificationId',
        alias: 'removeNotification',
        description: `Remove notification.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('meta')).optional(),
            },
        ],
        response: z.object({ total_unread: z.number().int() }).partial(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/notifications/:notificationId/read',
        alias: 'markNotificationAsRead',
        description: `Mark notification as read.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('meta')).optional(),
            },
        ],
        response: z.object({ data: z.array(Notification), total_unread: z.number().int().optional() }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/notifications/clear',
        alias: 'clearNotifications',
        description: `Clear notifications.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('meta')).optional(),
            },
        ],
        response: z.object({ data: z.array(Notification), total_unread: z.number().int().optional() }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/onboarding/:type/:id/progress',
        alias: 'getOnboardingProgress',
        description: `Get the progress of an onboarding.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'type',
                type: 'Path',
                schema: z.enum(['company', 'user']),
            },
            {
                name: 'id',
                type: 'Path',
                schema: id,
            },
        ],
        response: z.object({ data: OnboardingProgress }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/onboarding/approve/:userId',
        alias: 'approveUserOnboarding',
        description: `Approve a user&#x27;s onboarding.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'userId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: User }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/onboarding/progress',
        alias: 'makeOnboardingProgress',
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                description: `The message to send.`,
                type: 'Body',
                schema: makeOnboardingProgress_Body,
            },
        ],
        response: z.object({ data: OnboardingProgress }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/packages',
        alias: 'getPackages',
        description: `Get packages.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'campuses',
                            'campuses.address',
                            'campuses.media',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'packaged_programs',
                            'packaged_programs.program',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'package.logo_url',
                            'package.partnered',
                            'package.company_name',
                            'package.assessment_level',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[id]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Package) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/packages',
        alias: 'addPackage',
        description: `Add package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addPackage_Body,
            },
        ],
        response: z.object({ data: Package }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/packages/:packageId',
        alias: 'getPackage',
        description: `Get package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'campuses',
                            'campuses.address',
                            'campuses.media',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'packaged_programs',
                            'packaged_programs.program',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'package.logo_url',
                            'package.partnered',
                            'package.company_name',
                            'package.assessment_level',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[id]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
        ],
        response: z.object({ data: Package }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/packages/:packageId',
        alias: 'updatePackage',
        description: `Update package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updatePackage_Body,
            },
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Package }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/packages/:packageId',
        alias: 'deletePackage',
        description: `Delete package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/packages/:packageId/order',
        alias: 'orderPackagePrograms',
        description: `Order package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: orderPackagePrograms_Body,
            },
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Package }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/packages/:packageId/programs',
        alias: 'getPackagePrograms',
        description: `Get the programs of a package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'media_collections',
                            'campuses',
                            'campuses.address',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'campuses.media',
                            'packaged_programs',
                            'packaged_programs.package',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'program.company_name',
                            'program.logo_url',
                            'program.partnered',
                            'program.assessment_level',
                            'program.applications_count',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[partnered]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[principal_subdivision_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
        ],
        response: z.object({ data: z.array(PackagedProgram) }),
    },
    {
        method: 'post',
        path: '/packages/:packageId/programs',
        alias: 'addPackageProgram',
        description: `Add a program to a package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addPackageProgram_Body,
            },
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: PackagedProgram }),
    },
    {
        method: 'delete',
        path: '/packages/:packageId/programs/:programId',
        alias: 'removePackageProgram',
        description: `Remove a program from a package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'programId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
    },
    {
        method: 'patch',
        path: '/packages/:packageId/programs/:programId',
        alias: 'updatePackageProgram',
        description: `Update a program in a package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updatePackageProgram_Body,
            },
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'programId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: PackagedProgram }),
    },
    {
        method: 'post',
        path: '/packages/:packageId/sync',
        alias: 'syncPackagePrograms',
        description: `Sync package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: syncPackagePrograms_Body,
            },
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Package }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/payment-attempts/:attemptId',
        alias: 'getPaymentAttempt',
        description: `Get payment attempt.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attemptId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: PaymentAttempt }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/payment-attempts/:attemptId',
        alias: 'deletePaymentAttempt',
        description: `Cancels a pending payment attempt. For an application plan only the student may cancel. For a service plan (a service payment step or a general invoice) the student who owns the service and agency staff holding &#x60;LOG_MANUAL_PAYMENT&#x60; over the service&#x27;s agency branch may cancel. Cancelling a step&#x27;s pending attempt leaves the step unchanged and payable again.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attemptId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: PaymentAttempt }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/payment-attempts/:attemptId/distribute',
        alias: 'distributePaymentAttempt',
        description: `Distribute payment attempt.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attemptId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: PaymentAttempt }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/payment-attempts/:attemptId/verify-payment',
        alias: 'verifyPaymentAttempt',
        description: `Asks Stripe for the attempt&#x27;s current state and fulfils it if the payment has succeeded. For an application plan only the student may call this. For a service plan (a service payment step or a general invoice) the student who owns the service and agency staff holding &#x60;LOG_MANUAL_PAYMENT&#x60; over the service&#x27;s agency branch may call it. When the attempt belongs to a payment step, fulfilment also writes the step&#x27;s payment state and completes the step. A payment still clearing is returned unchanged; the step completes when Stripe confirms, by webhook or by the next verify.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attemptId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: PaymentAttempt }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/payment-items/:itemId/payment-attempts',
        alias: 'getPaymentAttempts',
        description: `Get payment attempts.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'itemId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(PaymentAttempt) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/payment-items/:itemId/payment-attempts',
        alias: 'addPaymentAttempt',
        description: `Add payment attempt.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addPaymentAttemptToPlan_Body,
            },
            {
                name: 'itemId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: PaymentAttempt }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/payment-methods',
        alias: 'getPaymentMethods',
        description: `Get payment methods.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(PaymentMethodReference) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/payment-methods/:methodId',
        alias: 'deletePaymentMethod',
        description: `Delete payment method.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'methodId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/payment-methods/:methodId',
        alias: 'updatePaymentMethod',
        description: `Update payment method.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updatePaymentMethod_Body,
            },
            {
                name: 'methodId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: PaymentMethodReference }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/payment-plans',
        alias: 'getPaymentPlans',
        description: `Get payment plans.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(z.enum(['payment_items', 'payment_attempts', 'payment_items.payment_attempts']))
                    .optional(),
            },
            {
                name: 'filter[application_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[start_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[end_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[active_between]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agency_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
        ],
        response: z.object({ data: z.array(PaymentPlan) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/payment-plans/:paymentPlanId',
        alias: 'getPaymentPlan',
        description: `Get payment plan.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'paymentPlanId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(z.enum(['payment_items', 'payment_attempts', 'payment_items.payment_attempts']))
                    .optional(),
            },
            {
                name: 'filter[application_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[start_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[end_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[active_between]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agency_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
        ],
        response: z.object({ data: PaymentPlan }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/payment-plans/:paymentPlanId',
        alias: 'cancelPaymentPlan',
        description: `Cancel payment plan.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'paymentPlanId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: PaymentPlan }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/payment-plans/:paymentPlanId/attempt',
        alias: 'addPaymentAttemptToPlan',
        description: `Add payment attempt.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addPaymentAttemptToPlan_Body,
            },
            {
                name: 'paymentPlanId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: PaymentAttempt }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/payment-plans/summarized',
        alias: 'getSummarizedPaymentPlans',
        description: `Get summarized payment plans.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(z.enum(['payment_items', 'payment_attempts', 'payment_items.payment_attempts']))
                    .optional(),
            },
            {
                name: 'filter[application_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[start_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[end_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[active_between]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agency_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(SummarizedPaymentPlan) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/payment-records',
        alias: 'getPaymentRecords',
        description: `Get payment records.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('attempt_records')).optional(),
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[expected_around_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[expected_around_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[expected_around_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[expected_around_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[expected_around_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[expected_around_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[fulfilled_at]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[application_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[payment_item_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[student_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[application_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[created_at]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[unpaid]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'created_at',
                            '-created_at',
                            'updated_at',
                            '-updated_at',
                            'expected_around',
                            '-expected_around',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(PaymentRecord) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/payout-batches',
        alias: 'getPayoutBatches',
        description: `Get payout batches.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['payouts', 'bank_detail'])).optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(CompanyPayoutBatch) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/payout-batches',
        alias: 'createPayoutBatch',
        description: `Create payout batch.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(CompanyPayoutBatch) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/payout-batches/:batchId/payouts',
        alias: 'getBatchPayouts',
        description: `Get batch payouts.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'batchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(CompanyPayout) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/payout-batches/:batchId/processing-file',
        alias: 'printBatchPayouts',
        description: `Print batch payouts.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'batchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(CompanyPayout) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/payout-batches/predict',
        alias: 'predictPayouts',
        description: `Predict payouts.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'currency',
                type: 'Query',
                schema: z.string(),
            },
        ],
        response: z.object({ data: z.array(PayoutPrediction), other_errors: z.array(z.string()).optional() }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/payout-batches/reconcile',
        alias: 'reconcilePayouts',
        description: `Reconcile payouts.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: z.object({ payments: z.instanceof(File) }),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/payouts/:companyPayout/mark-failed',
        alias: 'markPayoutFailed',
        description: `Mark payout failed.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyPayout',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: CompanyPayout }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/payouts/:companyPayout/mark-successful',
        alias: 'markPayoutSuccessful',
        description: `Mark payout successful.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyPayout',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: CompanyPayout }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/permissions',
        alias: 'getPermissions',
        description: `Get permissions.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(Permission) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/permissions/my',
        alias: 'getMyPermissions',
        description: `Get my permissions.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(Permission) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/platform-referrals',
        alias: 'getPlatformReferrals',
        description: `Get platform referrals.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(PlatformReferral) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/platform-referrals',
        alias: 'addPlatformReferral',
        description: `Add platform referral.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addPlatformReferral_Body,
            },
        ],
        response: z.object({ data: PlatformReferral }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/platform-referrals/:platformReferralId',
        alias: 'getPlatformReferral',
        description: `Get platform referral.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'platformReferralId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ],
        response: z.object({ data: PlatformReferral }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/platform-referrals/:platformReferralId',
        alias: 'updatePlatformReferral',
        description: `Update platform referral.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updatePlatformReferral_Body,
            },
            {
                name: 'platformReferralId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: PlatformReferral }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/platform-referrals/:platformReferralId',
        alias: 'deletePlatformReferral',
        description: `Delete platform referral.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'platformReferralId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/postal-codes',
        alias: 'listPostalCodes',
        requestFormat: 'json',
        parameters: [
            {
                name: 'subdivision_code',
                type: 'Query',
                schema: name__2,
            },
            {
                name: 'name',
                type: 'Query',
                schema: name__2,
            },
            {
                name: 'search',
                type: 'Query',
                schema: name__2,
            },
            {
                name: 'country_code',
                type: 'Query',
                schema: z.string().min(2).max(2).optional(),
            },
        ],
        response: z.object({ data: z.array(PostalCode) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/posts/:postableType/:postableId',
        alias: 'listPosts',
        description: `Get posts.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'postableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'postableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['media', 'event_registrants'])).optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(AdvancedPost) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `The user is not allowed to login.`,
                schema: z.object({
                    message: z.string(),
                    code: z.union([AccountStatus, z.enum(['branch:disabled', 'branch:active', 'branch:hidden'])]),
                }),
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/posts/:postableType/:postableId',
        alias: 'addPost',
        description: `Add post.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addPost_Body,
            },
            {
                name: 'postableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'postableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: AdvancedPost }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/posts/:postableType/:postableId/:postId',
        alias: 'getPost',
        description: `Get post.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'postableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'postableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'postId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['media', 'event_registrants'])).optional(),
            },
        ],
        response: z.object({ data: AdvancedPost }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/posts/:postableType/:postableId/:postId',
        alias: 'deletePost',
        description: `Delete post.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'postableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'postableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'postId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/posts/:postableType/:postableId/:postId',
        alias: 'updatePost',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updatePost_Body,
            },
            {
                name: 'postableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'postableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'postId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: AdvancedPost }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/posts/:postId/register',
        alias: 'registerForPost',
        description: `Register for post.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: registerForPost_Body,
            },
            {
                name: 'postId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: EventRegistrant }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/posts/:postId/register',
        alias: 'unregisterFromPost',
        description: `Unregister from post.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'postId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/program-levels',
        alias: 'listProgramLevels',
        requestFormat: 'json',
        response: z.object({ data: z.array(ProgramLevel) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/program-levels',
        alias: 'createProgramLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The program level to create.`,
                type: 'Body',
                schema: createProgramLevel_Body,
            },
        ],
        response: z.object({ data: ProgramLevel }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/program-levels/:programLevelId',
        alias: 'getProgramLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'programLevelId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ProgramLevel }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/program-levels/:programLevelId',
        alias: 'updateProgramLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the program level.`,
                type: 'Body',
                schema: updateProgramLevel_Body,
            },
            {
                name: 'programLevelId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ProgramLevel }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/program-levels/:programLevelId',
        alias: 'deleteProgramLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'programLevelId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/programs/:programId',
        alias: 'getProgram',
        description: `Get program.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'programId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'media_collections',
                            'campuses',
                            'campuses.address',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'campuses.media',
                            'packaged_programs',
                            'packaged_programs.package',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'program.company_name',
                            'program.logo_url',
                            'program.partnered',
                            'program.assessment_level',
                            'program.applications_count',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[partnered]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[principal_subdivision_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
        ],
        response: z.object({ data: Program }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/programs/:programId',
        alias: 'updateProgram',
        description: `Update program.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateProgram_Body,
            },
            {
                name: 'programId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Program }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/programs/:programId',
        alias: 'deleteProgram',
        description: `Delete program.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'programId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/qualification-levels',
        alias: 'listQualificationLevels',
        requestFormat: 'json',
        response: z.object({ data: z.array(QualificationLevel) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/qualification-levels',
        alias: 'createQualificationLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The qualification level to create.`,
                type: 'Body',
                schema: createQualificationLevel_Body,
            },
        ],
        response: z.object({ data: QualificationLevel }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/qualification-levels/:qualificationLevelId',
        alias: 'getQualificationLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'qualificationLevelId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: QualificationLevel }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/qualification-levels/:qualificationLevelId',
        alias: 'updateQualificationLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the qualification level.`,
                type: 'Body',
                schema: updateQualificationLevel_Body,
            },
            {
                name: 'qualificationLevelId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: QualificationLevel }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/qualification-levels/:qualificationLevelId',
        alias: 'deleteQualificationLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'qualificationLevelId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/roles',
        alias: 'getRoles',
        description: `Get roles.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(Role) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/roles',
        alias: 'createRole',
        description: `Create role.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createRole_Body,
            },
        ],
        response: z.object({ data: Role }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/roles/:roleId',
        alias: 'deleteRole',
        description: `Delete role.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'roleId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'put',
        path: '/roles/:roleId/permissions',
        alias: 'syncPermissionsOfRole',
        description: `Sync permissions of role.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: syncPermissionsOfRole_Body,
            },
            {
                name: 'roleId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Role }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'put',
        path: '/roles/:roleId/permissions/:permissionId',
        alias: 'attachPermissionToRole',
        description: `Attach permission to role.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'roleId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'permissionId',
                type: 'Path',
                schema: permissionId,
            },
        ],
        response: z.object({ data: Role }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/roles/:roleId/permissions/:permissionId',
        alias: 'detachPermissionFromRole',
        description: `Detach permission from role.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'roleId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'permissionId',
                type: 'Path',
                schema: permissionId,
            },
        ],
        response: z.object({ data: Role }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/roles/my',
        alias: 'getMyRoles',
        description: `Get my roles.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(Role) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/search/agencies',
        alias: 'searchAgencies',
        requestFormat: 'json',
        parameters: [
            {
                name: 'text',
                type: 'Query',
                schema: z.string(),
            },
            {
                name: 'partnered',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(PrettySearchItem) })),
        errors: [
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/search/campuses',
        alias: 'searchCampuses',
        description: `Search campuses.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(z.enum(['address', 'branches', 'company_branch', 'media', 'media_collections']))
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'public_page.logo_url',
                            'public_page.partners',
                            'public_page.partnered',
                            'campus.assessment_level',
                            'campus.company_name',
                            'campus.can.update',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'lat',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'lng',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'radius',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Campus) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/search/minimal',
        alias: 'searchMinimal',
        requestFormat: 'json',
        parameters: [
            {
                name: 'text',
                type: 'Query',
                schema: z.string(),
            },
            {
                name: 'type',
                type: 'Query',
                schema: z.enum([
                    'program',
                    'school',
                    'agency',
                    'campus',
                    'agency_branch',
                    'student',
                    'user',
                    'company',
                ]),
            },
            {
                name: 'parent_id',
                type: 'Query',
                schema: parent_id,
            },
            {
                name: 'partnered',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'parent_type',
                type: 'Query',
                schema: z
                    .enum([
                        'user',
                        'student',
                        'agency',
                        'campus',
                        'company',
                        'company_branch',
                        'attended_institution',
                        'language_test',
                        'program',
                        'document_requirement',
                        'application',
                        'agreement',
                        'media_storage',
                        'payment_attempt',
                        'payment_item',
                        'attempt_record',
                        'association_request',
                        'student_resume',
                        'settings',
                        'package',
                        'offer_letter',
                        'signature',
                        'task',
                        'comment',
                        'service_template',
                        'service',
                        'smart_event',
                        'family_member',
                        'advanced_posts',
                        'company_payout_batch',
                        'company_payout',
                        'service_step',
                        'packaged_program',
                        'payment_plan',
                        'applied_program',
                        'manual_payment_log',
                    ])
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(MinimalSearchItem) })),
        errors: [
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/search/packages',
        alias: 'searchPackages',
        description: `Search packages.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'campuses',
                            'campuses.address',
                            'campuses.media',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'packaged_programs',
                            'packaged_programs.program',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'package.logo_url',
                            'package.partnered',
                            'package.company_name',
                            'package.assessment_level',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[id]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'filter[partnered]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'lat',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'lng',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'radius',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Package) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/search/programs',
        alias: 'searchPrograms',
        description: `Search programs.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'media_collections',
                            'campuses',
                            'campuses.address',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'campuses.media',
                            'packaged_programs',
                            'packaged_programs.package',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'program.company_name',
                            'program.logo_url',
                            'program.partnered',
                            'program.assessment_level',
                            'program.applications_count',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[partnered]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[principal_subdivision_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'lat',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'lng',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'radius',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Program) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/search/schools',
        alias: 'searchSchools',
        requestFormat: 'json',
        parameters: [
            {
                name: 'text',
                type: 'Query',
                schema: z.string(),
            },
            {
                name: 'partnered',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(PrettySearchItem) })),
        errors: [
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/service-steps',
        alias: 'listServiceSteps',
        requestFormat: 'json',
        parameters: [
            {
                name: 'service_id',
                type: 'Query',
                schema: z.number().int().optional(),
            },
            {
                name: 'status',
                type: 'Query',
                schema: z.enum(['pending', 'in_progress', 'completed', 'cancelled']).optional(),
            },
        ],
        response: z.object({ data: z.array(ServiceStep) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/service-steps',
        alias: 'createServiceStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The service step to create.`,
                type: 'Body',
                schema: ServiceStepCreate,
            },
        ],
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/service-steps/:serviceStepId',
        alias: 'getServiceStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/service-steps/:serviceStepId',
        alias: 'updateServiceStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the service step.`,
                type: 'Body',
                schema: ServiceStepUpdate,
            },
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/service-steps/:serviceStepId',
        alias: 'deleteServiceStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/service-steps/:serviceStepId/cancel',
        alias: 'cancelServiceStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/service-steps/:serviceStepId/complete',
        alias: 'completeServiceStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `Result data from step completion.`,
                type: 'Body',
                schema: ServiceStepComplete.optional(),
            },
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/service-steps/:serviceStepId/config',
        alias: 'updateServiceStepConfig',
        description: `Requires &#x60;managePayments&#x60; on the step (404 when the caller cannot see the service, 403 otherwise); 400 when the step is not a payment step. Rewrites the config and recomputes the state&#x27;s &#x60;amount&#x60;, &#x60;final_amount&#x60; and &#x60;remaining_amount&#x60; (keeping any discount and paid amount), and records an activity event when the amount changed. When the amount changes, a pending card attempt on the step is cancelled first, Stripe intent included; if it turns out to have succeeded already the edit is refused with 409 &#x60;paid_by_card&#x60;.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateServiceStepConfig_Body,
            },
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 409,
                description: `The step has been paid by card, so the change would contradict money already taken: a manual payment, a price or discount edit, or a rollback is refused and nothing changes. Also returned when the step&#x27;s pending card attempt turned out to have succeeded at Stripe while being cancelled for the change. &#x60;code&#x60; is &#x60;paid_by_card&#x60;. Refunds are not supported in this version.`,
                schema: CommonError.and(z.object({ code: z.literal('paid_by_card') })),
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/service-steps/:serviceStepId/log-manual-payment',
        alias: 'logServiceStepManualPayment',
        description: `Records a payment taken outside Stripe. Requires &#x60;managePayments&#x60; on the step: service managers, or accounts staff with &#x60;LOG_MANUAL_PAYMENT&#x60; over the service&#x27;s agency branch (404 when the caller cannot see the service, 403 otherwise). Writes &#x60;paid_amount&#x60;, &#x60;remaining_amount&#x60;, &#x60;status&#x60; (&#x60;partial&#x60; or &#x60;paid&#x60;), &#x60;payment_method&#x60;, &#x60;payment_reference&#x60;, &#x60;payment_collected_by&#x60; / &#x60;payment_collected_at&#x60; and appends &#x60;notes&#x60;, creates a ManualPaymentLog awaiting the accountant&#x27;s verification, and records a service event. It does not complete the step: that is still &#x60;POST /service-steps/{id}/complete&#x60;, which a payment step allows once its status is &#x60;paid&#x60;. A cash payment on a step with GST and no existing discount is given the GST amount as an automatic discount. A pending card attempt on the step is cancelled first, Stripe intent included, so the student cannot also pay the old balance by card; if that attempt turns out to have succeeded already the manual payment is refused with 409 &#x60;paid_by_card&#x60;.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: logServiceStepManualPayment_Body,
            },
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 409,
                description: `The step has been paid by card, so the change would contradict money already taken: a manual payment, a price or discount edit, or a rollback is refused and nothing changes. Also returned when the step&#x27;s pending card attempt turned out to have succeeded at Stripe while being cancelled for the change. &#x60;code&#x60; is &#x60;paid_by_card&#x60;. Refunds are not supported in this version.`,
                schema: CommonError.and(z.object({ code: z.literal('paid_by_card') })),
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/service-steps/:serviceStepId/payment-attempt',
        alias: 'startServiceStepPaymentAttempt',
        description: `Starts a Stripe card payment for the full amount owed on a payment step, or returns the pending attempt that already exists for it. There is no request body: the step is the deduplication key, so a retried or double-clicked request can never create a second charge.
**Who.** The student who owns the service, or agency staff holding &#x60;LOG_MANUAL_PAYMENT&#x60; with authority over the service&#x27;s agency branch (&#x60;ServiceStepPolicy::handlesPayments&#x60;). A caller who cannot see the service gets 404, exactly as if the step did not exist (&#x60;ServiceStepPolicy::view&#x60; denies as not found); one who can see it but may not pay gets 403.
**Amount charged.** &#x60;state.remaining_amount&#x60;, falling back to &#x60;state.final_amount&#x60;, exactly as the step displays it. The caller cannot send an amount and part payment is not supported. &#x60;gst_inclusive: false&#x60; does not change this figure: &#x60;final_amount&#x60; and &#x60;remaining_amount&#x60; are derived from &#x60;payment_amount&#x60; less any discount with no GST added on top (&#x60;PaymentStep::getInitialState&#x60;, &#x60;CreateServiceFromTemplate&#x60;, &#x60;ServiceStepController::updateState&#x60; / &#x60;updateConfig&#x60;), the step invoice treats &#x60;final_amount&#x60; as GST-inclusive (&#x60;RaisePaymentStepInvoice&#x60; → &#x60;InvoiceParticular::amount()&#x60;), and both SPA views show the same number — so charging it is the only amount that equals the balance the student sees.
**Idempotency.** 201 when a new attempt (and, the first time, the step&#x27;s payment plan and its single item) is created; 200 when a pending attempt for the same amount already exists and is returned unchanged. A pending attempt for a different amount — the price or discount changed — is cancelled and replaced (201). The implementation serialises on the step so two simultaneous requests yield one attempt. Repeating the request is always safe.
**Not payable (409).** The step is locked behind an earlier step, is completed, skipped or cancelled, has &#x60;payment_type&#x60; set to something other than &#x60;stripe&#x60;, or has nothing left to pay. The body says which.
&#x60;client_secret&#x60; is present only when the current user may pay the attempt (&#x60;can.pay&#x60;), which is what the Payment Element needs. Verifying and cancelling use the existing payment-attempt endpoints.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: PaymentAttempt }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 409,
                description: `The step cannot take a card payment right now: locked, completed, skipped or cancelled, &#x60;payment_type&#x60; excludes &#x60;stripe&#x60;, or nothing is owed. &#x60;code&#x60; is one of &#x60;step_locked&#x60;, &#x60;step_closed&#x60;, &#x60;payment_type_excluded&#x60;, &#x60;nothing_owed&#x60;.`,
                schema: CommonError.and(
                    z.object({ code: z.enum(['step_locked', 'step_closed', 'payment_type_excluded', 'nothing_owed']) })
                ),
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/service-steps/:serviceStepId/payment-attempt',
        alias: 'getServiceStepPaymentAttempt',
        description: `The step&#x27;s most recent card payment attempt — pending, processing or fulfilled — or &#x60;null&#x60; when the step has never started one. Lets the payment step show a payment that is already in flight (for example a bank debit still clearing) and resume it instead of starting again. Same visibility rules as POST: 404 when the caller cannot see the service, 403 when they can see it but may not handle its payments. Cancelled attempts are not returned.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.union([PaymentAttempt, z.null()]) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/service-steps/:serviceStepId/quotation-preview',
        alias: 'getQuotationPreview',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ success: z.boolean(), data: z.array(QuotationDocumentPreview) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/service-steps/:serviceStepId/recheck-profile-gate',
        alias: 'recheckProfileGate',
        description: `Re-runs the resolver for the service&#x27;s quotation documents. When every bound field now resolves, the gate is marked complete and the state machine auto-advances.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ success: z.boolean(), data: z.object({}).partial() }),
        errors: [
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
        ],
    },
    {
        method: 'post',
        path: '/service-steps/:serviceStepId/reuse-document',
        alias: 'reuseStepDocument',
        description: `Requires &#x60;fill&#x60; on the target step. Copies the uploaded file of the source step&#x27;s upload that carries the same document schema into the target upload slot and records the source step id on it. 400 when the target is not an upload step; 422 (standard validation shape, keyed by the offending field) when the slot, the schema or the source step do not match, or the source has no file.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: reuseStepDocument_Body,
            },
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ message: z.string(), step: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/service-steps/:serviceStepId/rollback',
        alias: 'rollbackServiceStep',
        description: `Requires &#x60;manage&#x60; on the step (404 when the caller cannot see the service, 403 otherwise). Resets the step to &#x60;pending&#x60; with its initial state and clears its result; when the step&#x27;s config has &#x60;rollback_cascades&#x60;, every later step is reset the same way. Only a &#x60;completed&#x60; step can be rolled back (422 otherwise). A pending card attempt on any step being reset is cancelled first, Stripe intent included. A step paid by card cannot be rolled back while refunds are unsupported: 409 &#x60;paid_by_card&#x60;, nothing changes.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 409,
                description: `The step has been paid by card, so the change would contradict money already taken: a manual payment, a price or discount edit, or a rollback is refused and nothing changes. Also returned when the step&#x27;s pending card attempt turned out to have succeeded at Stripe while being cancelled for the change. &#x60;code&#x60; is &#x60;paid_by_card&#x60;. Refunds are not supported in this version.`,
                schema: CommonError.and(z.object({ code: z.literal('paid_by_card') })),
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/service-steps/:serviceStepId/send-invoice',
        alias: 'sendServiceStepInvoice',
        description: `Re-sends the invoice email for a payment step&#x27;s existing invoice to the student. Re-send only: step invoices are raised by the automatic path (agreement/document-sign completion); manual invoicing uses the general invoice endpoint. Returns 422 when no invoice has been raised for the step. A 200 confirms the email was queued, not that it was delivered (the mailable no-ops when its email template is disabled).`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ message: z.string(), invoice_id: Id.int(), reference_id: z.string() }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/service-steps/:serviceStepId/send-quotation',
        alias: 'sendQuotation',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: sendQuotation_Body.optional(),
            },
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ success: z.boolean(), message: z.string() }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/service-steps/:serviceStepId/start',
        alias: 'startServiceStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'put',
        path: '/service-steps/:serviceStepId/state',
        alias: 'updateServiceStepState',
        description: `Requires &#x60;fill&#x60; on the step. On a payment step, when the update changes what the step owes (&#x60;remaining_amount&#x60;, else &#x60;final_amount&#x60;) from what its pending card attempt would charge — a discount, a price edit or marking it paid — that attempt is cancelled first, Stripe intent included; if it turns out to have succeeded already the update is refused with 409 &#x60;paid_by_card&#x60;.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `Updated state data for the step.`,
                type: 'Body',
                schema: ServiceStepStateUpdate,
            },
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 409,
                description: `The step has been paid by card, so the change would contradict money already taken: a manual payment, a price or discount edit, or a rollback is refused and nothing changes. Also returned when the step&#x27;s pending card attempt turned out to have succeeded at Stripe while being cancelled for the change. &#x60;code&#x60; is &#x60;paid_by_card&#x60;. Refunds are not supported in this version.`,
                schema: CommonError.and(z.object({ code: z.literal('paid_by_card') })),
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/service-steps/:serviceStepId/validation',
        alias: 'getServiceStepValidationStatus',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceStepValidationStatus }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/service-template-steps/:id',
        alias: 'getServiceTemplateStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceTemplateStep }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/service-template-steps/:id',
        alias: 'updateServiceTemplateStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the service template step.`,
                type: 'Body',
                schema: updateServiceTemplateStep_Body,
            },
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceTemplateStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/service-template-steps/:id',
        alias: 'deleteServiceTemplateStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/service-templates',
        alias: 'listServiceTemplates',
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('steps')).optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(ServiceTemplate) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/service-templates',
        alias: 'createServiceTemplate',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The service template to create.`,
                type: 'Body',
                schema: createServiceTemplate_Body,
            },
        ],
        response: z.object({ data: ServiceTemplate }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/service-templates/:serviceTemplateId',
        alias: 'getServiceTemplate',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('steps')).optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
        ],
        response: z.object({ data: ServiceTemplate }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/service-templates/:serviceTemplateId',
        alias: 'updateServiceTemplate',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the service template.`,
                type: 'Body',
                schema: updateServiceTemplate_Body,
            },
            {
                name: 'serviceTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceTemplate }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/service-templates/:serviceTemplateId/profile-precheck',
        alias: 'profilePreCheck',
        description: `Resolves the template&#x27;s quotation-step documents against the student and reports which profile-bound fields are still empty, so the wizard can surface gaps before creating the service.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'student_id',
                type: 'Query',
                schema: z.number().int(),
            },
        ],
        response: z.object({ success: z.boolean(), data: ProfilePreCheckResult }),
        errors: [
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
        ],
    },
    {
        method: 'get',
        path: '/service-templates/:serviceTemplateId/service-template-steps',
        alias: 'listServiceTemplateSteps',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(ServiceTemplateStep) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/service-templates/:serviceTemplateId/service-template-steps',
        alias: 'createServiceTemplateStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The template step to create.`,
                type: 'Body',
                schema: createServiceTemplateStep_Body,
            },
            {
                name: 'serviceTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceTemplateStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/service-templates/:serviceTemplateId/service-template-steps/reorder',
        alias: 'reorderServiceTemplateSteps',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The order of the service template steps.`,
                type: 'Body',
                schema: reorderServiceTemplateSteps_Body,
            },
            {
                name: 'serviceTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceTemplate }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/services',
        alias: 'listServices',
        requestFormat: 'json',
        response: z.object({ data: z.array(Service) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/services',
        alias: 'createService',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The service to create.`,
                type: 'Body',
                schema: ServiceCreate,
            },
        ],
        response: z.object({ data: Service }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/services/:service/invoices',
        alias: 'createGeneralInvoice',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createGeneralInvoice_Body,
            },
            {
                name: 'service',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: GeneralInvoiceCreatedResponse,
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
        ],
    },
    {
        method: 'get',
        path: '/services/:serviceId',
        alias: 'getService',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Service }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/services/:serviceId',
        alias: 'updateService',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the service.`,
                type: 'Body',
                schema: ServiceUpdate,
            },
            {
                name: 'serviceId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Service }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'delete',
        path: '/services/:serviceId',
        alias: 'deleteService',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/services/:serviceId/progress',
        alias: 'getServiceProgress',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: ServiceProgress }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/services/stats',
        alias: 'getServiceStats',
        requestFormat: 'json',
        response: z.object({ data: ServiceStats }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/services/templates/:serviceTemplateId/create',
        alias: 'createServiceFromTemplate',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The service to create from template with step configurations.`,
                type: 'Body',
                schema: ServiceCreateFromTemplate,
            },
            {
                name: 'serviceTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Service }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/settings',
        alias: 'getSettings',
        description: `Get settings.`,
        requestFormat: 'json',
        response: z.object({ data: Settings }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/settings',
        alias: 'updateSettings',
        description: `Update Settings.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateSettings_Body,
            },
        ],
        response: z.object({ data: Settings }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/signatures/:signatureId',
        alias: 'updateSignature',
        description: `Update signature.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateSignature_Body,
            },
            {
                name: 'signatureId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Signature }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/signup-referrals/:referralId/invite',
        alias: 'inviteUserToSignUp',
        description: `Invite a user to sign up.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: z.object({ email: z.string() }),
            },
            {
                name: 'referralId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ message: z.string(), success: z.boolean() }).partial(),
    },
    {
        method: 'patch',
        path: '/signup-referrals/:referralId/platform',
        alias: 'updateSignupReferralPlatform',
        description: `Update the platform source tag (e.g. WhatsApp, Facebook) on a signup referral for tracking purposes.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateSignupReferralPlatform_Body,
            },
            {
                name: 'referralId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.object({ data: SignupReferral }).partial() }),
    },
    {
        method: 'get',
        path: '/smart-events/:smartEvent',
        alias: 'getSmartEvent',
        requestFormat: 'json',
        parameters: [
            {
                name: 'smartEvent',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: SmartEvent }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/smart-events/:smartEvent/notes',
        alias: 'updateNoteSmartEvent',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createNoteSmartEvent_Body,
            },
            {
                name: 'smartEvent',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: SmartEvent }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/smart-events/services/:service',
        alias: 'listSmartEvents',
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media_links.media',
                            'task',
                            'user',
                            'task.media',
                            'task.handlers',
                            'task.user',
                            'task.student',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[eventable_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[eventable_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[task_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[user_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[title]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[description]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[created_at]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[updated_at]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'created_at',
                            '-created_at',
                            'updated_at',
                            '-updated_at',
                            'title',
                            '-title',
                            'type',
                            '-type',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'service',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(SmartEvent) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/smart-events/services/:service/notes',
        alias: 'createNoteSmartEvent',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createNoteSmartEvent_Body,
            },
            {
                name: 'service',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: SmartEvent }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/smart-events/services/:service/uploads',
        alias: 'createUploadSmartEvent',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createUploadSmartEvent_Body,
            },
            {
                name: 'service',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: SmartEvent }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/students',
        alias: 'getStudents',
        description: `Get students.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'addresses',
                            'role',
                            'media',
                            'media_storage',
                            'media_storage.media',
                            'personal_details',
                            'agency_branch',
                            'agency_branch.agency',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'student.avatar_url',
                            'student.applications_count',
                            'student.handlers',
                            'student.full_name',
                            'student.referral_platform',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[passport_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[finished_onboarding]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[not_agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[managed_by]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Student) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/students/:studentId',
        alias: 'getStudent',
        description: `Get student.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'studentId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'addresses',
                            'role',
                            'media',
                            'media_storage',
                            'media_storage.media',
                            'personal_details',
                            'agency_branch',
                            'agency_branch.agency',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'student.avatar_url',
                            'student.applications_count',
                            'student.handlers',
                            'student.full_name',
                            'student.referral_platform',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[passport_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[finished_onboarding]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[not_agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[managed_by]',
                type: 'Query',
                schema: filter_search_,
            },
        ],
        response: z.object({ data: Student }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/students/:studentId/family-members',
        alias: 'listStudentFamilyMembers',
        description: `Family members are returned oldest-first, each with its personal details loaded.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'studentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(FamilyMember) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/students/:studentId/family-members',
        alias: 'createStudentFamilyMember',
        description: `Creates the family member and its personal-details record in one call. The personal details are parented to the student&#x27;s own personal details.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: FamilyMemberCreateRequest,
            },
            {
                name: 'studentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: FamilyMember }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/students/:studentId/resume',
        alias: 'getStudentResume',
        description: `Get resume.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'studentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: StudentResume }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'put',
        path: '/students/:studentId/resume',
        alias: 'setStudentResume',
        description: `Set student resume details.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: setStudentResume_Body,
            },
            {
                name: 'studentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: StudentResume }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/students/link-agency',
        alias: 'linkAgency',
        description: `Link student with new agency.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: linkAgency_Body,
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'addresses',
                            'role',
                            'media',
                            'media_storage',
                            'media_storage.media',
                            'personal_details',
                            'agency_branch',
                            'agency_branch.agency',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'student.avatar_url',
                            'student.applications_count',
                            'student.handlers',
                            'student.full_name',
                            'student.referral_platform',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[passport_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[finished_onboarding]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[not_agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[managed_by]',
                type: 'Query',
                schema: filter_search_,
            },
        ],
        response: z.object({ data: Student }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/sub-agents',
        alias: 'createSubAgent',
        description: `Create a sub-agent under the calling agency user. Agency users with manage_users only; a sub-agent cannot create another sub-agent (403).`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createSubAgent_Body,
            },
        ],
        response: z.object({ data: User }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/sub-agents/:subAgentId',
        alias: 'updateSubAgent',
        description: `Update a sub-agent of the calling user&#x27;s agency. A sub-agent cannot update another sub-agent (403). 404 when the user is not a sub-agent of this agency.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateSubAgent_Body,
            },
            {
                name: 'subAgentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: User }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/tasks',
        alias: 'listTasks',
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['media', 'handlers', 'user', 'student'])).optional(),
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[taskable_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[taskable_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[user_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[handler_ids]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[start_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[deadline_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[created_at_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[updated_at_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'created_at',
                            '-created_at',
                            'updated_at',
                            '-updated_at',
                            'start_date',
                            '-start_date',
                            'deadline',
                            '-deadline',
                            'title',
                            '-title',
                            'status',
                            '-status',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Task) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/tasks',
        alias: 'createTask',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createTask_Body,
            },
        ],
        response: z.object({ data: Task }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/tasks/:id',
        alias: 'getTask',
        requestFormat: 'json',
        parameters: [
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['media', 'handlers', 'user', 'student'])).optional(),
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[taskable_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[taskable_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[user_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[handler_ids]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[start_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[deadline_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[created_at_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[updated_at_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'created_at',
                            '-created_at',
                            'updated_at',
                            '-updated_at',
                            'start_date',
                            '-start_date',
                            'deadline',
                            '-deadline',
                            'title',
                            '-title',
                            'status',
                            '-status',
                        ])
                    )
                    .optional(),
            },
        ],
        response: z.object({ data: Task }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/tasks/:id',
        alias: 'updateTask',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateTask_Body,
            },
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Task }),
        errors: [
            {
                status: 404,
                description: `Task not found`,
                schema: z.void(),
            },
            {
                status: 422,
                description: `Validation error`,
                schema: z.void(),
            },
        ],
    },
    {
        method: 'patch',
        path: '/tasks/:id/reschedule',
        alias: 'rescheduleTask',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: rescheduleTask_Body,
            },
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Task }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/tasks/:task/comments',
        alias: 'listComments',
        requestFormat: 'json',
        parameters: [
            {
                name: 'task',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['user', 'media'])).optional(),
            },
            {
                name: 'filter[commentable_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[commentable_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(Comment) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/tasks/:task/comments',
        alias: 'createComment',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createComment_Body,
            },
            {
                name: 'task',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: Comment }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/tasks/counts',
        alias: 'getTaskCounts',
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['media', 'handlers', 'user', 'student'])).optional(),
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[taskable_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[taskable_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[user_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[handler_ids]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[start_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[deadline_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[created_at_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[updated_at_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'created_at',
                            '-created_at',
                            'updated_at',
                            '-updated_at',
                            'start_date',
                            '-start_date',
                            'deadline',
                            '-deadline',
                            'title',
                            '-title',
                            'status',
                            '-status',
                        ])
                    )
                    .optional(),
            },
        ],
        response: z.object({
            data: z
                .object({
                    total: z.number().int(),
                    todo: z.number().int(),
                    doing: z.number().int(),
                    done: z.number().int(),
                })
                .partial(),
        }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/user-context',
        alias: 'getUserContext',
        description: `Get user context.`,
        requestFormat: 'json',
        response: z.object({ data: UserContext }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/users',
        alias: 'listUsers',
        description: `List users.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'addresses',
                            'applications_count',
                            'role',
                            'media_storage',
                            'media_storage.media',
                            'personal_details',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('user.applications_count')).optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[finished_onboarding]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[passport_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agency_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[not_agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[managed_by]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ],
        response: PaginatedResponse.and(z.object({ data: z.array(User) })),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'patch',
        path: '/users/:userId',
        alias: 'updateUser',
        description: `Update information of the given user.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the user.`,
                type: 'Body',
                schema: updateUser_Body,
            },
            {
                name: 'userId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: User }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'put',
        path: '/users/:userId/roles/:roleId',
        alias: 'attachRoleToUser',
        description: `Attach role to user.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'userId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'roleId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: User }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/users/:userId/suspend',
        alias: 'suspendUser',
        description: `Suspend a user.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: z.object({ reason: z.string() }),
            },
            {
                name: 'userId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: User }),
    },
    {
        method: 'delete',
        path: '/users/:userId/tokens',
        alias: 'expireUserTokens',
        description: `This will log out the user from all devices. TODO -- It is automatically done if a user changes password or their account is suspended.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'userId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.void(),
    },
    {
        method: 'get',
        path: '/version',
        alias: 'getAppVersion',
        description: `Public probe used by the shared CI/CD pipeline&#x27;s verify stage. Returns the APP_VERSION the deploy stamped into the environment so the pipeline can confirm the new release is live and roll back otherwise. No authentication and no data envelope.`,
        requestFormat: 'json',
        response: z.object({ status: z.literal('ok'), version: z.string(), commit_sha: z.string().nullish() }),
        errors: [
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/visa-applications/:visaApplicationId/family-members',
        alias: 'listVisaApplicationFamilyMembers',
        requestFormat: 'json',
        parameters: [
            {
                name: 'visaApplicationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(FamilyMember) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'put',
        path: '/visa-applications/:visaApplicationId/family-members',
        alias: 'syncVisaApplicationFamilyMembers',
        description: `Replaces the linked set with the given IDs. Every ID must belong to the visa application&#x27;s own student. Returns the resulting set.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: FamilyMemberSyncRequest,
            },
            {
                name: 'visaApplicationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ],
        response: z.object({ data: z.array(FamilyMember) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/webhooks/stripe',
        alias: 'stripeWebhook',
        description: `Called by Stripe, not by clients. The raw body is verified against the &#x60;stripe-signature&#x60; header using the current webhook secret, then each configured legacy secret; a payload that matches none is rejected with 400. A verified event of a handled type is queued and processed asynchronously against the payment attempt it names: a succeeded payment is fulfilled, which for a service payment step also writes the step&#x27;s payment state and completes the step. Receiving the same event twice changes nothing the second time. Always answers 204 for a verified event — handled or not, and before processing — so Stripe does not retry.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: z.object({}).partial().passthrough(),
            },
            {
                name: 'stripe-signature',
                type: 'Header',
                schema: z.string(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
        ],
    },
    {
        method: 'get',
        path: '/webviews/:webview_id',
        alias: 'getWebView',
        requestFormat: 'json',
        parameters: [
            {
                name: 'webview_id',
                type: 'Path',
                schema: z.string(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('requirements')).optional(),
            },
        ],
        response: z.object({ data: WebView }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'post',
        path: '/webviews/:webview_id',
        alias: 'updateWebView',
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateWebView_Body,
            },
            {
                name: 'webview_id',
                type: 'Path',
                schema: z.string(),
            },
        ],
        response: z.object({ data: WebView }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
        ],
    },
    {
        method: 'get',
        path: '/webviews/:webview_id/images/:key',
        alias: 'getWebViewImage',
        requestFormat: 'json',
        parameters: [
            {
                name: 'webview_id',
                type: 'Path',
                schema: z.string(),
            },
            {
                name: 'key',
                type: 'Path',
                schema: z.string(),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/webviews/:webview_id/requirements',
        alias: 'getWebViewRequirements',
        requestFormat: 'json',
        parameters: [
            {
                name: 'webview_id',
                type: 'Path',
                schema: z.string(),
            },
        ],
        response: z.object({ data: WebViewRequirements }),
        errors: [
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/webviews/defined',
        alias: 'listDefinedWebViews',
        description: `Gets the web views that are available in the application. This does not return the current data of any webview, only the available web views with basic information.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(DefinedWebView) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/widgets/applications-last-updated',
        alias: 'getApplicationsLastUpdated',
        description: `Get applications last updated.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(Application) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/widgets/applications-requiring-action',
        alias: 'getApplicationsRequiringAction',
        description: `Get applications requiring action.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(Application) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/widgets/companies-being-onboarded',
        alias: 'getCompaniesBeingOnboarded',
        description: `Get companies being onboarded.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'company_type',
                type: 'Query',
                schema: z.enum(['student', 'staff', 'school', 'agency']).optional(),
            },
        ],
        response: z.object({ data: z.array(Company) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/widgets/latest-messages',
        alias: 'getLatestMessages',
        description: `Get latest messages.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(ConversationMessage) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/widgets/new-students',
        alias: 'getNewStudents',
        description: `Get new students.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(Student) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/widgets/stat-collection',
        alias: 'getStatCollection',
        description: `Get stat collection.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(z.object({ name: z.string(), value: z.string() })) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/widgets/top-agents',
        alias: 'getTopAgents',
        description: `Get top agents.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'limit',
                type: 'Query',
                schema: z.number().int().optional(),
            },
        ],
        response: z.object({
            data: z.array(z.object({ applications_count: z.number().int(), company: MinimalCompany })),
        }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/widgets/top-programs',
        alias: 'getTopPrograms',
        description: `Get top programs.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'limit',
                type: 'Query',
                schema: z.number().int().optional(),
            },
        ],
        response: z.object({ data: z.array(z.object({ applications_count: z.number().int(), program: Program })) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/widgets/top-schools',
        alias: 'getTopSchools',
        description: `Get top schools.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'limit',
                type: 'Query',
                schema: z.number().int().optional(),
            },
        ],
        response: z.object({
            data: z.array(z.object({ applications_count: z.number().int(), company: MinimalCompany })),
        }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    {
        method: 'get',
        path: '/widgets/upcoming-payment-items',
        alias: 'getUpcomingPaymentItems',
        description: `Get upcoming payment items.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(PaymentItem) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
]);

export const endpoints = {
    getAccount: {
        method: 'get',
        path: '/account',
        alias: 'getAccount',
        description: `Get account information of the currently logged-in user.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['media', 'addresses', 'applications_count', 'role'])).optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[finished_onboarding]',
                type: 'Query',
                schema: filter_search_,
            },
        ] as const,
        response: z.object({ data: User }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    changePassword: {
        method: 'post',
        path: '/account/change-password',
        alias: 'changePassword',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: changePassword_Body,
            },
        ] as const,
        response: z.object({ data: User }),
    },
    getAccountCompany: {
        method: 'get',
        path: '/account/company',
        alias: 'getAccountCompany',
        description: `Get company.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'company_branches',
                            'campuses',
                            'agencies',
                            'media',
                            'addresses',
                            'campuses.company_branch',
                            'agencies.company_branch',
                            'agencies.media',
                            'agencies.address',
                            'agencies.addresses',
                            'campuses.media',
                            'campuses.address',
                            'campuses.programs',
                            'campuses.addresses',
                            'company_branches.campus',
                            'company_branches.agency',
                            'company_branches.campus.address',
                            'company_branches.agency.address',
                            'bank_detail',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'company.root_address',
                            'company.contact',
                            'public_page.partners',
                            'company.handlers',
                            'company.applications',
                            'company.applications_count',
                            'company.superadmin_id',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[is_onboarded]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
        ] as const,
        response: z.object({ data: Company }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listAddresses: {
        method: 'get',
        path: '/addresses/:addressableType/:addressableId',
        alias: 'listAddresses',
        description: `Get address.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'addressableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'addressableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'filter[address_purpose]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[address_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[media]',
                type: 'Query',
                schema: filter_search_,
            },
        ] as const,
        response: z.object({ data: z.array(Address) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `The user is not allowed to login.`,
                schema: z.object({
                    message: z.string(),
                    code: z.union([AccountStatus, z.enum(['branch:disabled', 'branch:active', 'branch:hidden'])]),
                }),
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    addAddress: {
        method: 'post',
        path: '/addresses/:addressableType/:addressableId',
        alias: 'addAddress',
        description: `Add address.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: AddressBody,
            },
            {
                name: 'addressableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'addressableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Address }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getAddress: {
        method: 'get',
        path: '/addresses/:addressableType/:addressableId/:addressId',
        alias: 'getAddress',
        description: `Get address.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'addressableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'addressableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'addressId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'filter[address_purpose]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[address_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[media]',
                type: 'Query',
                schema: filter_search_,
            },
        ] as const,
        response: z.object({ data: Address }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteAddress: {
        method: 'delete',
        path: '/addresses/:addressableType/:addressableId/:addressId',
        alias: 'deleteAddress',
        description: `Delete address.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'addressableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'addressableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'addressId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateAddress: {
        method: 'patch',
        path: '/addresses/:addressableType/:addressableId/:addressId',
        alias: 'updateAddress',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: AddressBody,
            },
            {
                name: 'addressableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'addressableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'addressId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Address }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    sendTestEmail: {
        method: 'post',
        path: '/admin/send-test-email',
        alias: 'sendTestEmail',
        description: `Send test email.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: sendTestEmail_Body,
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getAgency: {
        method: 'get',
        path: '/agencies/:agencyId',
        alias: 'getAgency',
        description: `Get agency.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'agencyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'address',
                            'branches',
                            'company_branch',
                            'media',
                            'media_collections',
                            'signup_referral',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'public_page.logo_url',
                            'public_page.partners',
                            'public_page.partnered',
                            'agency.can.update',
                        ])
                    )
                    .optional(),
            },
        ] as const,
        response: z.object({ data: Agency }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateAgency: {
        method: 'patch',
        path: '/agencies/:agencyId',
        alias: 'updateAgency',
        description: `Update agency.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateAgency_Body,
            },
            {
                name: 'agencyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Agency }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteAgency: {
        method: 'delete',
        path: '/agencies/:agencyId',
        alias: 'deleteAgency',
        description: `Delete agency.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'agencyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listAgreementTemplates: {
        method: 'get',
        path: '/agreement-templates',
        alias: 'listAgreementTemplates',
        description: `List agreement templates.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(AgreementTemplate) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createAgreementTemplate: {
        method: 'post',
        path: '/agreement-templates',
        alias: 'createAgreementTemplate',
        description: `Add agreement template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createAgreementTemplate_Body,
            },
        ] as const,
        response: z.object({ data: AgreementTemplate }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getAgreementTemplate: {
        method: 'get',
        path: '/agreement-templates/:agreementTemplateId',
        alias: 'getAgreementTemplate',
        description: `Get agreement template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'agreementTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: AgreementTemplate }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateAgreementTemplate: {
        method: 'patch',
        path: '/agreement-templates/:agreementTemplateId',
        alias: 'updateAgreementTemplate',
        description: `Update agreement template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateAgreementTemplate_Body,
            },
            {
                name: 'agreementTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: AgreementTemplate }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteAgreementTemplate: {
        method: 'delete',
        path: '/agreement-templates/:agreementTemplateId',
        alias: 'deleteAgreementTemplate',
        description: `Delete agreement template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'agreementTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getRenderedAgreementTemplate: {
        method: 'post',
        path: '/agreement-templates/get-rendered',
        alias: 'getRenderedAgreementTemplate',
        description: `Get rendered agreement template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: getRenderedAgreementTemplate_Body,
            },
        ] as const,
        response: z.object({ data: z.object({ contents: z.string().optional() }) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    scrapTermsOfService: {
        method: 'get',
        path: '/agreement-templates/scrap-tos',
        alias: 'scrapTermsOfService',
        description: `Scrap the Terms of Service.`,
        requestFormat: 'json',
        response: z.object({ data: z.string() }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listAgreements: {
        method: 'get',
        path: '/agreements',
        alias: 'listAgreements',
        description: `Get agreements.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'sender_company',
                            'receiver_company',
                            'signatures',
                            'signatures.media',
                            'program_commissions',
                            'agreement_modifications',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[sender_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[receiver_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[platform]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[other_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[valid_upto]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agreement_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[signed_at]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Agreement) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addAgreement: {
        method: 'post',
        path: '/agreements',
        alias: 'addAgreement',
        description: `Add agreement.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addAgreement_Body,
            },
        ] as const,
        response: z.object({ data: Agreement }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getAgreement: {
        method: 'get',
        path: '/agreements/:agreementId',
        alias: 'getAgreement',
        description: `Get agreement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'agreementId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'sender_company',
                            'receiver_company',
                            'signatures',
                            'signatures.media',
                            'program_commissions',
                            'agreement_modifications',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[sender_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[receiver_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[platform]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[other_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[valid_upto]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agreement_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[signed_at]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ] as const,
        response: z.object({ data: Agreement }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateAgreement: {
        method: 'patch',
        path: '/agreements/:agreementId',
        alias: 'updateAgreement',
        description: `Update agreement.

PHP does not parse multipart/form-data bodies on PATCH requests, so
clients must send this operation as &#x60;POST&#x60; with a &#x60;_method&#x3D;PATCH&#x60;
body field (Laravel method spoofing). The request is routed and
validated as PATCH.
`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateAgreement_Body,
            },
            {
                name: 'agreementId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Agreement }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    terminateAgreement: {
        method: 'delete',
        path: '/agreements/:agreementId',
        alias: 'terminateAgreement',
        description: `Delete agreement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'agreementId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Agreement }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addAgreementFromTemplate: {
        method: 'post',
        path: '/agreements/from-template',
        alias: 'addAgreementFromTemplate',
        description: `Add agreement from template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addAgreementFromTemplate_Body,
            },
        ] as const,
        response: z.object({ data: Agreement }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listApplications: {
        method: 'get',
        path: '/applications',
        alias: 'listApplications',
        description: `Get applications.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'student',
                            'offer_letters',
                            'offer_letters.media',
                            'offer_letters.signatures',
                            'offer_letters.signatures.media',
                            'payment_plans',
                            'payment_plans.payment_items',
                            'payment_plans.payment_attempts',
                            'document_requirements',
                            'agency_staff_agreement',
                            'school_staff_agreement',
                            'agency_school_agreement',
                            'campus',
                            'agency',
                            'package',
                            'applied_programs',
                            'applied_programs.program',
                            'media_storage',
                            'media_storage.media',
                            'personal_details',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'application.handlers',
                            'application.can.view',
                            'application.can.send_offer_letter',
                            'application.can.attach_acceptance_letter',
                            'application.can.sign_offer_letter',
                            'application.can.attach_coe',
                            'application.can.submit',
                            'application.can.withdraw',
                            'application.can.reject',
                            'application.can.in_review',
                            'student.handlers',
                            'student.full_name',
                            'student.avatar_url',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[program_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agency_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[intake_year]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intake_month]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at', 'intake', '-intake']))
                    .optional(),
            },
            {
                name: 'filter[application_type]',
                type: 'Query',
                schema: z.enum(['course', 'visa']).optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Application) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addApplication: {
        method: 'post',
        path: '/applications',
        alias: 'addApplication',
        description: `Add application.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addApplication_Body,
            },
        ] as const,
        response: z.object({ data: Application }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getApplication: {
        method: 'get',
        path: '/applications/:applicationId',
        alias: 'getApplication',
        description: `Get application.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'applicationId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'student',
                            'offer_letters',
                            'offer_letters.media',
                            'offer_letters.signatures',
                            'offer_letters.signatures.media',
                            'payment_plans',
                            'payment_plans.payment_items',
                            'payment_plans.payment_attempts',
                            'document_requirements',
                            'agency_staff_agreement',
                            'school_staff_agreement',
                            'agency_school_agreement',
                            'campus',
                            'agency',
                            'package',
                            'applied_programs',
                            'applied_programs.program',
                            'media_storage',
                            'media_storage.media',
                            'personal_details',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'application.handlers',
                            'application.can.view',
                            'application.can.send_offer_letter',
                            'application.can.attach_acceptance_letter',
                            'application.can.sign_offer_letter',
                            'application.can.attach_coe',
                            'application.can.submit',
                            'application.can.withdraw',
                            'application.can.reject',
                            'application.can.in_review',
                            'student.handlers',
                            'student.full_name',
                            'student.avatar_url',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[program_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agency_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[intake_year]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intake_month]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at', 'intake', '-intake']))
                    .optional(),
            },
        ] as const,
        response: z.object({ data: Application }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateApplication: {
        method: 'post',
        path: '/applications/:applicationId',
        alias: 'updateApplication',
        description: `Update application.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateApplication_Body,
            },
            {
                name: 'applicationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Application }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    sendConfirmationOfEnrolment: {
        method: 'post',
        path: '/applications/:applicationId/attach-confirmation-of-enrolment',
        alias: 'sendConfirmationOfEnrolment',
        description: `Send confirmation of enrollment.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: sendConfirmationOfEnrolment_Body,
            },
            {
                name: 'applicationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Application }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getDocumentRequirements: {
        method: 'get',
        path: '/applications/:applicationId/document-requirements',
        alias: 'getDocumentRequirements',
        description: `Get application document requirements.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'applicationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(DocumentRequirement) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addDocumentRequirement: {
        method: 'post',
        path: '/applications/:applicationId/document-requirements',
        alias: 'addDocumentRequirement',
        description: `Add application document requirement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addDocumentRequirement_Body,
            },
            {
                name: 'applicationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: DocumentRequirement }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getApplicationEvents: {
        method: 'get',
        path: '/applications/:applicationId/events',
        alias: 'getApplicationEvents',
        description: `Get application events.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'applicationId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(ApplicationEvent) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    sendOfferLetter: {
        method: 'post',
        path: '/applications/:applicationId/send-offer-letter',
        alias: 'sendOfferLetter',
        description: `Send offer letter.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: sendOfferLetter_Body,
            },
            {
                name: 'applicationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Application }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getAssociations: {
        method: 'get',
        path: '/association-requests',
        alias: 'getAssociations',
        description: `Get association requests.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'sender_company',
                            'receiver_company',
                            'agreement',
                            'agreement.media',
                            'agreement.signatures',
                            'agreement.signatures.media',
                            'agreement.program_commissions',
                            'renewal_agreement',
                            'renewal_agreement.media',
                            'renewal_agreement.signatures',
                            'renewal_agreement.signatures.media',
                            'renewal_agreement.program_commissions',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[sender_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[receiver_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[other_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[flow_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ] as const,
        response: PaginatedResponse.and(
            z.object({ accepting_association_requests: z.boolean().optional(), data: z.array(AssociationRequest) })
        ),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addAssociation: {
        method: 'post',
        path: '/association-requests',
        alias: 'addAssociation',
        description: `Add association request.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addAssociation_Body,
            },
        ] as const,
        response: z.object({ data: AssociationRequest }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getAssociation: {
        method: 'get',
        path: '/association-requests/:associationId',
        alias: 'getAssociation',
        description: `Get association request.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'associationId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'sender_company',
                            'receiver_company',
                            'agreement',
                            'agreement.media',
                            'agreement.signatures',
                            'agreement.signatures.media',
                            'agreement.program_commissions',
                            'renewal_agreement',
                            'renewal_agreement.media',
                            'renewal_agreement.signatures',
                            'renewal_agreement.signatures.media',
                            'renewal_agreement.program_commissions',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[sender_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[receiver_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[other_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[flow_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ] as const,
        response: z.object({ data: AssociationRequest }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteAssociation: {
        method: 'delete',
        path: '/association-requests/:associationId',
        alias: 'deleteAssociation',
        description: `Reject or cancel association request.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'associationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: AssociationRequest }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    sendAgreement: {
        method: 'post',
        path: '/association-requests/:associationId/send-agreement',
        alias: 'sendAgreement',
        description: `Send agreement.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: sendAgreement_Body,
            },
            {
                name: 'associationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.object({ agreement: Agreement, association_request: AssociationRequest }) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getAttemptRecords: {
        method: 'get',
        path: '/attempt-records',
        alias: 'getAttemptRecords',
        description: `Get attempt records.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[payment_item_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(AttemptRecord) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getAttendedInstitution: {
        method: 'get',
        path: '/attended-institutions/:documentId',
        alias: 'getAttendedInstitution',
        description: `Get attended institution.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('media')).optional(),
            },
        ] as const,
        response: z.object({ data: AttendedInstitution }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateAttendedInstitution: {
        method: 'patch',
        path: '/attended-institutions/:documentId',
        alias: 'updateAttendedInstitution',
        description: `Update attended institution.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateAttendedInstitution_Body,
            },
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: AttendedInstitution }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteAttendedInstitution: {
        method: 'delete',
        path: '/attended-institutions/:documentId',
        alias: 'deleteAttendedInstitution',
        description: `Delete attended institution.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    forgotPassword: {
        method: 'post',
        path: '/auth/forgot-password',
        alias: 'forgotPassword',
        description: `Initiate forgot password process.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: z.object({ email: z.string().email() }),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    login: {
        method: 'post',
        path: '/auth/login',
        alias: 'login',
        description: `Logins to the application, creating a new session, if provided correct credentials. Returns a JWT token.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: login_Body,
            },
        ] as const,
        response: z.object({ token: z.string(), me: User }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `The user is not allowed to login.`,
                schema: z.object({
                    message: z.string(),
                    code: z.union([AccountStatus, z.enum(['branch:disabled', 'branch:active', 'branch:hidden'])]),
                }),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    resetPassword: {
        method: 'post',
        path: '/auth/password-reset',
        alias: 'resetPassword',
        description: `Reset password.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: resetPassword_Body,
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    refreshJwt: {
        method: 'post',
        path: '/auth/refresh',
        alias: 'refreshJwt',
        description: `Refresh the JWT token.`,
        requestFormat: 'json',
        response: z.object({ token: z.string() }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `The user is not allowed to login.`,
                schema: z.object({
                    message: z.string(),
                    code: z.union([AccountStatus, z.enum(['branch:disabled', 'branch:active', 'branch:hidden'])]),
                }),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    registerAgency: {
        method: 'post',
        path: '/auth/register/agency',
        alias: 'registerAgency',
        description: `Register as an agency.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: registerAgency_Body,
            },
        ] as const,
        response: z.object({}).partial().passthrough(),
        errors: [
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    registerSchool: {
        method: 'post',
        path: '/auth/register/school',
        alias: 'registerSchool',
        description: `Register as a school.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: registerSchool_Body,
            },
        ] as const,
        response: z.object({}).partial().passthrough(),
        errors: [
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    registerStudent: {
        method: 'post',
        path: '/auth/register/student',
        alias: 'registerStudent',
        description: `Register as a student.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: registerStudent_Body,
            },
        ] as const,
        response: z
            .object({ user: z.object({ id: Id.int(), company_id: Id.int(), student_id: NullableId }).partial() })
            .partial(),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getBankDetails: {
        method: 'get',
        path: '/bank-details/:bankableType/:bankableId',
        alias: 'getBankDetails',
        description: `Get bank details.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'bankableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'bankableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.union([z.null(), BankDetail]) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createBankDetails: {
        method: 'post',
        path: '/bank-details/:bankableType/:bankableId',
        alias: 'createBankDetails',
        description: `Create bank details.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createBankDetails_Body,
            },
            {
                name: 'bankableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'bankableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: BankDetail }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getCampus: {
        method: 'get',
        path: '/campuses/:campusId',
        alias: 'getCampus',
        description: `Get campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(z.enum(['address', 'branches', 'company_branch', 'media', 'media_collections']))
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'public_page.logo_url',
                            'public_page.partners',
                            'public_page.partnered',
                            'campus.assessment_level',
                            'campus.company_name',
                            'campus.can.update',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
        ] as const,
        response: z.object({ data: Campus }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateCampus: {
        method: 'patch',
        path: '/campuses/:campusId',
        alias: 'updateCampus',
        description: `Update campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateCampus_Body,
            },
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Campus }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteCampus: {
        method: 'delete',
        path: '/campuses/:campusId',
        alias: 'deleteCampus',
        description: `Delete campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getCampusPackages: {
        method: 'get',
        path: '/campuses/:campusId/packages',
        alias: 'getCampusPackages',
        description: `Get the packages of a campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'campuses',
                            'campuses.address',
                            'campuses.media',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'packaged_programs',
                            'packaged_programs.program',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'package.logo_url',
                            'package.partnered',
                            'package.company_name',
                            'package.assessment_level',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[id]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
        ] as const,
        response: z.object({ data: z.array(Package) }),
    },
    removeCampusPackage: {
        method: 'delete',
        path: '/campuses/:campusId/packages/:packageId',
        alias: 'removeCampusPackage',
        description: `Remove a package from a campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
    },
    addCampusPackage: {
        method: 'put',
        path: '/campuses/:campusId/packages/:packageId',
        alias: 'addCampusPackage',
        description: `Add a package to a campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Package }),
    },
    getCampusPrograms: {
        method: 'get',
        path: '/campuses/:campusId/programs',
        alias: 'getCampusPrograms',
        description: `Get the programs of a campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'media_collections',
                            'campuses',
                            'campuses.address',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'campuses.media',
                            'packaged_programs',
                            'packaged_programs.package',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'program.company_name',
                            'program.logo_url',
                            'program.partnered',
                            'program.assessment_level',
                            'program.applications_count',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[partnered]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[principal_subdivision_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Program) })),
    },
    removeCampusProgram: {
        method: 'delete',
        path: '/campuses/:campusId/programs/:programId',
        alias: 'removeCampusProgram',
        description: `Remove a program from a campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'programId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
    },
    addCampusProgram: {
        method: 'put',
        path: '/campuses/:campusId/programs/:programId',
        alias: 'addCampusProgram',
        description: `Add a program to a campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'campusId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'programId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Program }),
    },
    getApplicationsOverTime: {
        method: 'get',
        path: '/charts/applications-over-time',
        alias: 'getApplicationsOverTime',
        description: `Get applications over time.`,
        requestFormat: 'json',
        response: z.object({
            data: z.object({
                created: z.array(z.object({ date: DateTime, count: z.number().int() })),
                visa: z.array(z.object({ date: DateTime, count: z.number().int() })),
            }),
        }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getEarningsOverTime: {
        method: 'get',
        path: '/charts/earnings-over-time',
        alias: 'getEarningsOverTime',
        description: `Get earnings over time.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(z.object({ date: DateTime, amount: Decimal })) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getNewStudentsOverTime: {
        method: 'get',
        path: '/charts/new-students-over-time',
        alias: 'getNewStudentsOverTime',
        description: `Get new students over time.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(z.object({ date: DateTime, count: z.number().int() })) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listCities: {
        method: 'get',
        path: '/cities',
        alias: 'listCities',
        requestFormat: 'json',
        parameters: [
            {
                name: 'subdivision_code',
                type: 'Query',
                schema: name__2,
            },
            {
                name: 'name',
                type: 'Query',
                schema: name__2,
            },
            {
                name: 'country_code',
                type: 'Query',
                schema: z.string().min(2).max(2).optional(),
            },
        ] as const,
        response: z.object({ data: z.array(City) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getComment: {
        method: 'get',
        path: '/comments/:comment',
        alias: 'getComment',
        requestFormat: 'json',
        parameters: [
            {
                name: 'comment',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Comment }),
    },
    getCommunication: {
        method: 'get',
        path: '/communications/:communication',
        alias: 'getCommunication',
        description: `Returns a logged email communication, including the rendered HTML body. Authorised to staff who can view the related student/application/service.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'communication',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: Communication,
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listCompanies: {
        method: 'get',
        path: '/companies',
        alias: 'listCompanies',
        description: `Get companies.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'company_branches',
                            'campuses',
                            'agencies',
                            'media',
                            'addresses',
                            'campuses.company_branch',
                            'agencies.company_branch',
                            'agencies.media',
                            'agencies.address',
                            'agencies.addresses',
                            'campuses.media',
                            'campuses.address',
                            'campuses.programs',
                            'campuses.addresses',
                            'company_branches.campus',
                            'company_branches.agency',
                            'company_branches.campus.address',
                            'company_branches.agency.address',
                            'bank_detail',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'company.root_address',
                            'company.contact',
                            'public_page.partners',
                            'company.handlers',
                            'company.applications',
                            'company.applications_count',
                            'company.superadmin_id',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[is_onboarded]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Company) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getCompany: {
        method: 'get',
        path: '/companies/:companyId',
        alias: 'getCompany',
        description: `Get company.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'company_branches',
                            'campuses',
                            'agencies',
                            'media',
                            'addresses',
                            'campuses.company_branch',
                            'agencies.company_branch',
                            'agencies.media',
                            'agencies.address',
                            'agencies.addresses',
                            'campuses.media',
                            'campuses.address',
                            'campuses.programs',
                            'campuses.addresses',
                            'company_branches.campus',
                            'company_branches.agency',
                            'company_branches.campus.address',
                            'company_branches.agency.address',
                            'bank_detail',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'company.root_address',
                            'company.contact',
                            'public_page.partners',
                            'company.handlers',
                            'company.applications',
                            'company.applications_count',
                            'company.superadmin_id',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[is_onboarded]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
        ] as const,
        response: z.object({ data: Company }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateCompany: {
        method: 'patch',
        path: '/companies/:companyId',
        alias: 'updateCompany',
        description: `Update company.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateCompany_Body,
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Company }),
    },
    getAccountBalance: {
        method: 'get',
        path: '/companies/:companyId/accounts/balance',
        alias: 'getAccountBalance',
        description: `Get Account balance.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('balance.earnings')).optional(),
            },
        ] as const,
        response: z.object({
            data: z.object({
                earnings: Decimal.optional(),
                available: z.array(z.object({ amount: z.string(), currency: z.string() })),
            }),
        }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getAgencies: {
        method: 'get',
        path: '/companies/:companyId/agencies',
        alias: 'getAgencies',
        description: `Get agencies.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'address',
                            'branches',
                            'company_branch',
                            'media',
                            'media_collections',
                            'signup_referral',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'public_page.logo_url',
                            'public_page.partners',
                            'public_page.partnered',
                            'agency.can.update',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Agency) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addAgency: {
        method: 'post',
        path: '/companies/:companyId/agencies',
        alias: 'addAgency',
        description: `Add agency.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: CreatePublicPage,
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Agency }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getCampuses: {
        method: 'get',
        path: '/companies/:companyId/campuses',
        alias: 'getCampuses',
        description: `Get campuses.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(z.enum(['address', 'branches', 'company_branch', 'media', 'media_collections']))
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'public_page.logo_url',
                            'public_page.partners',
                            'public_page.partnered',
                            'campus.assessment_level',
                            'campus.company_name',
                            'campus.can.update',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Campus) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addCampus: {
        method: 'post',
        path: '/companies/:companyId/campuses',
        alias: 'addCampus',
        description: `Add campus.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: CreatePublicPage,
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Campus }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getCompanyBranches: {
        method: 'get',
        path: '/companies/:companyId/company-branches',
        alias: 'getCompanyBranches',
        description: `Get company branches.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(CompanyBranch) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createCompanyBranch: {
        method: 'post',
        path: '/companies/:companyId/company-branches',
        alias: 'createCompanyBranch',
        description: `Create company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createCompanyBranch_Body,
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: CompanyBranch }),
    },
    checkPayoutEligibility: {
        method: 'get',
        path: '/companies/:companyId/payout-eligibility',
        alias: 'checkPayoutEligibility',
        description: `Check if a company is eligible for payouts.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.object({ errors: z.array(z.string()).optional(), eligible: z.boolean() }) }),
    },
    getPayouts: {
        method: 'get',
        path: '/companies/:companyId/payouts',
        alias: 'getPayouts',
        description: `Get payouts.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(CompanyPayout) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPlatformReferralByCompany: {
        method: 'get',
        path: '/companies/:companyId/platform-referral',
        alias: 'getPlatformReferralByCompany',
        description: `Get platform referral.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ] as const,
        response: z.object({ data: PlatformReferral }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPrograms: {
        method: 'get',
        path: '/companies/:companyId/programs',
        alias: 'getPrograms',
        description: `Get programs.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'media_collections',
                            'campuses',
                            'campuses.address',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'campuses.media',
                            'packaged_programs',
                            'packaged_programs.package',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'program.company_name',
                            'program.logo_url',
                            'program.partnered',
                            'program.assessment_level',
                            'program.applications_count',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[partnered]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[principal_subdivision_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Program) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addProgram: {
        method: 'post',
        path: '/companies/:companyId/programs',
        alias: 'addProgram',
        description: `Add program.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addProgram_Body,
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Program }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPublicPage: {
        method: 'get',
        path: '/companies/:companyId/public-page',
        alias: 'getPublicPage',
        description: `Get public page.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(z.enum(['address', 'branches', 'company_branch', 'media', 'media_collections']))
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'public_page.logo_url',
                            'public_page.partners',
                            'public_page.partnered',
                        ])
                    )
                    .optional(),
            },
        ] as const,
        response: z.object({ data: PublicPage }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updatePublicPage: {
        method: 'patch',
        path: '/companies/:companyId/public-page',
        alias: 'updatePublicPage',
        description: `Update public page.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: UpdatePublicPage,
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: PublicPage }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getStripeOnboardingLink: {
        method: 'get',
        path: '/companies/:companyId/stripe/onboarding',
        alias: 'getStripeOnboardingLink',
        description: `Get Stripe onboarding link.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({
            data: z.object({ url: z.string().url(), expires_at: z.string().datetime({ offset: true }) }).partial(),
        }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    completeStripeOnboarding: {
        method: 'post',
        path: '/companies/:companyId/stripe/onboarding',
        alias: 'completeStripeOnboarding',
        description: `Complete Stripe onboarding.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Company }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    toggleBankDetailsLock: {
        method: 'post',
        path: '/companies/:companyId/toggle-bank-details-lock',
        alias: 'toggleBankDetailsLock',
        description: `Toggle bank details lock.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Company }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateAbnInformation: {
        method: 'post',
        path: '/companies/:companyId/update-abn-info',
        alias: 'updateAbnInformation',
        description: `Update ABN information.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Company }),
    },
    getCompanyBranch: {
        method: 'get',
        path: '/company-branches/:branchId',
        alias: 'getCompanyBranch',
        description: `Get company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: CompanyBranch }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateCompanyBranch: {
        method: 'patch',
        path: '/company-branches/:branchId',
        alias: 'updateCompanyBranch',
        description: `Update company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateCompanyBranch_Body,
            },
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: CompanyBranch }),
    },
    getEmailRecipients: {
        method: 'get',
        path: '/company-branches/:branchId/email-recipients',
        alias: 'getEmailRecipients',
        description: `Get email recipients.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ] as const,
        response: z.object({ data: z.array(EmailRecipient) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addEmailRecipient: {
        method: 'post',
        path: '/company-branches/:branchId/email-recipients',
        alias: 'addEmailRecipient',
        description: `Add email recipient.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addEmailRecipient_Body,
            },
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: EmailRecipient }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getManagedCompaniesOfCompanyBranch: {
        method: 'get',
        path: '/company-branches/:branchId/managed-companies',
        alias: 'getManagedCompaniesOfCompanyBranch',
        description: `Get managed companies of a company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(Company) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    attachCompanyToCompanyBranch: {
        method: 'put',
        path: '/company-branches/:branchId/managed-companies/:companyId',
        alias: 'attachCompanyToCompanyBranch',
        description: `Attach company to company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Company }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    detachCompanyFromCompanyBranch: {
        method: 'delete',
        path: '/company-branches/:branchId/managed-companies/:companyId',
        alias: 'detachCompanyFromCompanyBranch',
        description: `Detach company from company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listSignupReferralsOfCompanyBranch: {
        method: 'get',
        path: '/company-branches/:branchId/signup-referrals',
        alias: 'listSignupReferralsOfCompanyBranch',
        description: `List signup referrals of a company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(SignupReferral) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createSignupReferralForCompanyBranch: {
        method: 'post',
        path: '/company-branches/:branchId/signup-referrals',
        alias: 'createSignupReferralForCompanyBranch',
        description: `Create signup referral for a company branch.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: z.object({ enabled: z.boolean() }),
            },
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: SignupReferral }),
    },
    listCompanyBranchUsers: {
        method: 'get',
        path: '/company-branches/:branchId/users',
        alias: 'listCompanyBranchUsers',
        description: `Get company branch users.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'addresses',
                            'applications_count',
                            'role',
                            'media_storage',
                            'media_storage.media',
                            'personal_details',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('user.applications_count')).optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[finished_onboarding]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[passport_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agency_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[not_agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[managed_by]',
                type: 'Query',
                schema: filter_search_,
            },
        ] as const,
        response: z.object({ data: z.array(User) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createCompanyBranchUser: {
        method: 'post',
        path: '/company-branches/:branchId/users',
        alias: 'createCompanyBranchUser',
        description: `Create company branch user.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createCompanyBranchUser_Body,
            },
            {
                name: 'branchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: User }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    downloadConversationAttachment: {
        method: 'get',
        path: '/conversation-attachments/:attachment/download',
        alias: 'downloadConversationAttachment',
        description: `Download a conversation attachment.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attachment',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'expires',
                type: 'Query',
                schema: z.number().int().optional(),
            },
            {
                name: 'signature',
                type: 'Query',
                schema: z.string().optional(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    getConversationAttachment: {
        method: 'get',
        path: '/conversation-attachments/:attachmentId',
        alias: 'getConversationAttachment',
        description: `Get a conversation attachment.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attachmentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ConversationAttachment }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteConversationAttachment: {
        method: 'delete',
        path: '/conversation-attachments/:attachmentId',
        alias: 'deleteConversationAttachment',
        description: `Delete a conversation attachment.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attachmentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getConversationMessage: {
        method: 'get',
        path: '/conversation-messages/:message',
        alias: 'getConversationMessage',
        description: `Get a message of a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('attachments')).optional(),
            },
            {
                name: 'message',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ConversationMessage }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    deleteConversationMessage: {
        method: 'delete',
        path: '/conversation-messages/:message',
        alias: 'deleteConversationMessage',
        description: `Delete a message of a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'message',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    getConversationMessageAttachments: {
        method: 'get',
        path: '/conversation-messages/:message/conversation-attachments',
        alias: 'getConversationMessageAttachments',
        description: `Get the attachments of a message.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'message',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(ConversationAttachment) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    markConversationMessageRead: {
        method: 'post',
        path: '/conversation-messages/:message/mark-read',
        alias: 'markConversationMessageRead',
        description: `Mark a message of a conversation as read.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'message',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ConversationParticipant }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    markConversationMessageReceived: {
        method: 'post',
        path: '/conversation-messages/:message/mark-received',
        alias: 'markConversationMessageReceived',
        description: `Mark a message of a conversation as received.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'message',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ConversationParticipant }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listConversations: {
        method: 'get',
        path: '/conversations',
        alias: 'listConversations',
        description: `Get the conversations of the current user.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'recent_messages',
                            'participants',
                            'latest_message_id',
                            'my_participant',
                            'company_branches',
                            'companies',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[text]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[conversation_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[participant_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum(['created_at', '-created_at', 'updated_at', '-updated_at', 'closed_at', '-closed_at'])
                    )
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Conversation) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    getConversation: {
        method: 'get',
        path: '/conversations/:conversation',
        alias: 'getConversation',
        description: `Get a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'recent_messages',
                            'participants',
                            'latest_message_id',
                            'my_participant',
                            'company_branches',
                            'companies',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[text]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[conversation_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[participant_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum(['created_at', '-created_at', 'updated_at', '-updated_at', 'closed_at', '-closed_at'])
                    )
                    .optional(),
            },
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Conversation }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    getConversationContacts: {
        method: 'get',
        path: '/conversations/:conversation/contacts',
        alias: 'getConversationContacts',
        description: `Get the contacts of a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(ConversationContact) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getConversationAttachments: {
        method: 'get',
        path: '/conversations/:conversation/conversation-attachments',
        alias: 'getConversationAttachments',
        description: `Get the attachments of a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(ConversationAttachment) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    getConversationMessages: {
        method: 'get',
        path: '/conversations/:conversation/conversation-messages',
        alias: 'getConversationMessages',
        description: `Get the messages of a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('attachments')).optional(),
            },
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'limit',
                type: 'Query',
                schema: z.number().int().gte(10).lte(500).optional(),
            },
            {
                name: 'after_id',
                type: 'Query',
                schema: z.number().int().gte(0).optional().default(0),
            },
            {
                name: 'before_id',
                type: 'Query',
                schema: z.number().int().gte(0).optional().default(0),
            },
        ] as const,
        response: IdPaginatedResponse.and(z.object({ data: z.array(ConversationMessage) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    sendConversationMessage: {
        method: 'post',
        path: '/conversations/:conversation/conversation-messages',
        alias: 'sendConversationMessage',
        description: `Send a message to a conversation.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                description: `The message to send.`,
                type: 'Body',
                schema: sendConversationMessage_Body,
            },
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ConversationMessage }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    getConversationParticipants: {
        method: 'get',
        path: '/conversations/:conversation/participants',
        alias: 'getConversationParticipants',
        description: `Get the participants of a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('user')).optional(),
            },
        ] as const,
        response: z.object({ data: z.array(ConversationParticipant) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addConversationParticipant: {
        method: 'post',
        path: '/conversations/:conversation/participants',
        alias: 'addConversationParticipant',
        description: `Add a participant to a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The participant to add.`,
                type: 'Body',
                schema: addConversationParticipant_Body,
            },
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ConversationParticipant }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    removeConversationParticipant: {
        method: 'delete',
        path: '/conversations/:conversation/participants/:participant',
        alias: 'removeConversationParticipant',
        description: `Remove a participant from a conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'conversation',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'participant',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ConversationParticipant }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createIntraCompanyConversation: {
        method: 'post',
        path: '/conversations/intra-company',
        alias: 'createIntraCompanyConversation',
        description: `Create an intra-company conversation.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createIntraCompanyConversation_Body,
            },
        ] as const,
        response: z.object({ data: Conversation }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getIntraCompanyConversationContacts: {
        method: 'get',
        path: '/conversations/intra-company/contacts',
        alias: 'getIntraCompanyConversationContacts',
        description: `Get the contacts of an intra-company conversation.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(ConversationContact) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPartnerConversation: {
        method: 'get',
        path: '/conversations/partner/:companyId',
        alias: 'getPartnerConversation',
        description: `Get a conversation between two company branches.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'recent_messages',
                            'participants',
                            'latest_message_id',
                            'my_participant',
                            'company_branches',
                            'companies',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[text]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[conversation_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[participant_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum(['created_at', '-created_at', 'updated_at', '-updated_at', 'closed_at', '-closed_at'])
                    )
                    .optional(),
            },
        ] as const,
        response: z.object({ data: Conversation }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    createPartnerConversation: {
        method: 'put',
        path: '/conversations/partner/:companyId',
        alias: 'createPartnerConversation',
        description: `Create a conversation between two company branches.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Conversation }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listCountries: {
        method: 'get',
        path: '/countries',
        alias: 'listCountries',
        requestFormat: 'json',
        parameters: [
            {
                name: 'name',
                type: 'Query',
                schema: name__2,
            },
        ] as const,
        response: z.object({ data: z.array(Country) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listPrincipalSubdivisions: {
        method: 'get',
        path: '/countries/:country_code_iso2/principal-subdivisions',
        alias: 'listPrincipalSubdivisions',
        requestFormat: 'json',
        parameters: [
            {
                name: 'country_code_iso2',
                type: 'Path',
                schema: z.string().min(2).max(2),
            },
            {
                name: 'name',
                type: 'Query',
                schema: name__2,
            },
        ] as const,
        response: z.object({ data: z.array(PrincipalSubdivision) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listCurrencies: {
        method: 'get',
        path: '/currencies',
        alias: 'listCurrencies',
        requestFormat: 'json',
        response: z.object({ data: z.array(Currency) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listDiscountLogs: {
        method: 'get',
        path: '/discount-logs/:discountableType/:discountableId',
        alias: 'listDiscountLogs',
        description: `Returns the discount change audit trail for a discountable entity.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'discountableType',
                type: 'Path',
                schema: z.enum(['program', 'payment_plan', 'applied_program', 'packaged_program']),
            },
            {
                name: 'discountableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.object({ data: z.array(DiscountLog) }).partial() }),
    },
    getAllDocumentDrafts: {
        method: 'get',
        path: '/document-drafts',
        alias: 'getAllDocumentDrafts',
        requestFormat: 'json',
        response: z.object({ data: z.array(DocumentDraft) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createDocumentDraft: {
        method: 'post',
        path: '/document-drafts',
        alias: 'createDocumentDraft',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createDocumentDraft_Body,
            },
        ] as const,
        response: z.object({ data: DocumentDraft }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getDocumentDraft: {
        method: 'get',
        path: '/document-drafts/:id',
        alias: 'getDocumentDraft',
        requestFormat: 'json',
        parameters: [
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: DocumentDraft }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateDocumentDraft: {
        method: 'patch',
        path: '/document-drafts/:id',
        alias: 'updateDocumentDraft',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateDocumentDraft_Body,
            },
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: DocumentDraft }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getDocumentFieldBindings: {
        method: 'get',
        path: '/document-field-bindings',
        alias: 'getDocumentFieldBindings',
        requestFormat: 'json',
        response: z.object({ success: z.boolean(), data: z.array(DocumentFieldBinding) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getDocumentRequirement: {
        method: 'get',
        path: '/document-requirements/:requirementId',
        alias: 'getDocumentRequirement',
        description: `Get document requirement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'requirementId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: DocumentRequirement }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateDocumentRequirement: {
        method: 'patch',
        path: '/document-requirements/:requirementId',
        alias: 'updateDocumentRequirement',
        description: `Update document requirement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateDocumentRequirement_Body,
            },
            {
                name: 'requirementId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: DocumentRequirement }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteDocumentRequirement: {
        method: 'delete',
        path: '/document-requirements/:requirementId',
        alias: 'deleteDocumentRequirement',
        description: `Delete document requirement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'requirementId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    approveDocumentRequirement: {
        method: 'post',
        path: '/document-requirements/:requirementId/approve',
        alias: 'approveDocumentRequirement',
        description: `Approve document requirement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'requirementId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: DocumentRequirement }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    denyDocumentRequirement: {
        method: 'post',
        path: '/document-requirements/:requirementId/deny',
        alias: 'denyDocumentRequirement',
        description: `Deny document requirement.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: denyDocumentRequirement_Body,
            },
            {
                name: 'requirementId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: DocumentRequirement }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listDocumentTypes: {
        method: 'get',
        path: '/document-types',
        alias: 'listDocumentTypes',
        requestFormat: 'json',
        response: z.object({ data: z.array(DocumentType) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createDocumentType: {
        method: 'post',
        path: '/document-types',
        alias: 'createDocumentType',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The document type to create.`,
                type: 'Body',
                schema: createDocumentType_Body,
            },
        ] as const,
        response: z.object({ data: DocumentType }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getDocumentType: {
        method: 'get',
        path: '/document-types/:documentId',
        alias: 'getDocumentType',
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: DocumentType }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateDocumentType: {
        method: 'patch',
        path: '/document-types/:documentId',
        alias: 'updateDocumentType',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the document type.`,
                type: 'Body',
                schema: updateDocumentType_Body,
            },
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: DocumentType }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteDocumentType: {
        method: 'delete',
        path: '/document-types/:documentId',
        alias: 'deleteDocumentType',
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listAttendedInstitutions: {
        method: 'get',
        path: '/documentables/:documentableType/:documentableId/attended-institutions',
        alias: 'listAttendedInstitutions',
        description: `List attended institutions.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('media')).optional(),
            },
        ] as const,
        response: z.object({ data: z.array(AttendedInstitution) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addAttendedInstitution: {
        method: 'post',
        path: '/documentables/:documentableType/:documentableId/attended-institutions',
        alias: 'addAttendedInstitution',
        description: `Add attended institution.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addAttendedInstitution_Body,
            },
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: AttendedInstitution }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listEmploymentRecords: {
        method: 'get',
        path: '/documentables/:documentableType/:documentableId/employment-records',
        alias: 'listEmploymentRecords',
        description: `List employment records.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(EmploymentRecord) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addEmploymentRecord: {
        method: 'post',
        path: '/documentables/:documentableType/:documentableId/employment-records',
        alias: 'addEmploymentRecord',
        description: `Add employment record.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addEmploymentRecord_Body,
            },
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: EmploymentRecord }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listLanguageTests: {
        method: 'get',
        path: '/documentables/:documentableType/:documentableId/language-tests',
        alias: 'listLanguageTests',
        description: `Get language tests.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('media')).optional(),
            },
        ] as const,
        response: z.object({ data: z.array(LanguageTest) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addLanguageTest: {
        method: 'post',
        path: '/documentables/:documentableType/:documentableId/language-tests',
        alias: 'addLanguageTest',
        description: `Add language test.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addLanguageTest_Body,
            },
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: LanguageTest }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPersonalDetails: {
        method: 'get',
        path: '/documentables/:documentableType/:documentableId/personal-details',
        alias: 'getPersonalDetails',
        description: `Get personal details.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: StudentPersonalDetails }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updatePersonalDetails: {
        method: 'put',
        path: '/documentables/:documentableType/:documentableId/personal-details',
        alias: 'updatePersonalDetails',
        description: `Update personal details.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updatePersonalDetails_Body,
            },
            {
                name: 'documentableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'documentableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: StudentPersonalDetails }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getEmailRecipient: {
        method: 'get',
        path: '/email-recipients/:recipientId',
        alias: 'getEmailRecipient',
        description: `Get email recipient.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'recipientId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ] as const,
        response: z.object({ data: EmailRecipient }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateEmailRecipient: {
        method: 'patch',
        path: '/email-recipients/:recipientId',
        alias: 'updateEmailRecipient',
        description: `Update email recipient.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateEmailRecipient_Body,
            },
            {
                name: 'recipientId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: EmailRecipient }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteEmailRecipient: {
        method: 'delete',
        path: '/email-recipients/:recipientId',
        alias: 'deleteEmailRecipient',
        description: `Delete email recipient.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'recipientId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getEmailTemplates: {
        method: 'get',
        path: '/email-templates',
        alias: 'getEmailTemplates',
        description: `Get email templates.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(EmailTemplate) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getEmailTemplate: {
        method: 'get',
        path: '/email-templates/:emailTemplateId',
        alias: 'getEmailTemplate',
        description: `Get email template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'emailTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: EmailTemplate }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateEmailTemplate: {
        method: 'patch',
        path: '/email-templates/:emailTemplateId',
        alias: 'updateEmailTemplate',
        description: `Update email template.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateEmailTemplate_Body,
            },
            {
                name: 'emailTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: EmailTemplate }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getEmploymentRecord: {
        method: 'get',
        path: '/employment-records/:documentId',
        alias: 'getEmploymentRecord',
        description: `Get employment record.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: EmploymentRecord }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateEmploymentRecord: {
        method: 'patch',
        path: '/employment-records/:documentId',
        alias: 'updateEmploymentRecord',
        description: `Update employment record.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateEmploymentRecord_Body,
            },
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: EmploymentRecord }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteEmploymentRecord: {
        method: 'delete',
        path: '/employment-records/:documentId',
        alias: 'deleteEmploymentRecord',
        description: `Delete employment record.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'documentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getFamilyMember: {
        method: 'get',
        path: '/family-members/:familyMemberId',
        alias: 'getFamilyMember',
        requestFormat: 'json',
        parameters: [
            {
                name: 'familyMemberId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: FamilyMember }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateFamilyMember: {
        method: 'put',
        path: '/family-members/:familyMemberId',
        alias: 'updateFamilyMember',
        description: `Only the relationship is editable here. Names, passport and contact fields live on the personal-details record — update them via &#x60;PUT /documentables/family_member/{id}/personal-details&#x60;.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateFamilyMember_Body,
            },
            {
                name: 'familyMemberId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: FamilyMember }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteFamilyMember: {
        method: 'delete',
        path: '/family-members/:familyMemberId',
        alias: 'deleteFamilyMember',
        description: `Soft-deletes the family member and detaches it from any linked visa or insurance records.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'familyMemberId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    assignHandler: {
        method: 'post',
        path: '/handlers/:handleableType/:handleableId',
        alias: 'assignHandler',
        description: `Assign manager to company.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: assignHandler_Body,
            },
            {
                name: 'handleableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'handleableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Handler }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    removeHandler: {
        method: 'delete',
        path: '/handlers/:handlerId',
        alias: 'removeHandler',
        description: `Remove manager from company.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'handlerId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getHealth: {
        method: 'get',
        path: '/health',
        alias: 'getHealth',
        requestFormat: 'json',
        response: z.void(),
        errors: [
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listInsurancePolicyFamilyMembers: {
        method: 'get',
        path: '/insurance-policies/:insurancePolicyId/family-members',
        alias: 'listInsurancePolicyFamilyMembers',
        requestFormat: 'json',
        parameters: [
            {
                name: 'insurancePolicyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(FamilyMember) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    syncInsurancePolicyFamilyMembers: {
        method: 'put',
        path: '/insurance-policies/:insurancePolicyId/family-members',
        alias: 'syncInsurancePolicyFamilyMembers',
        description: `Replaces the covered set with the given IDs. Every ID must belong to the policy&#x27;s own student. Returns the resulting set.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: FamilyMemberSyncRequest,
            },
            {
                name: 'insurancePolicyId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(FamilyMember) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getLanguageTest: {
        method: 'get',
        path: '/language-tests/:testId',
        alias: 'getLanguageTest',
        description: `Get language test.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'testId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('media')).optional(),
            },
        ] as const,
        response: z.object({ data: LanguageTest }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateLanguageTest: {
        method: 'patch',
        path: '/language-tests/:testId',
        alias: 'updateLanguageTest',
        description: `Update language test.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateLanguageTest_Body,
            },
            {
                name: 'testId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: LanguageTest }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteLanguageTest: {
        method: 'delete',
        path: '/language-tests/:testId',
        alias: 'deleteLanguageTest',
        description: `Delete language test.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'testId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listLanguages: {
        method: 'get',
        path: '/languages',
        alias: 'listLanguages',
        requestFormat: 'json',
        response: z.object({ data: z.array(Language) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listMediaCollections: {
        method: 'get',
        path: '/media-collections/:modelType/:modelId',
        alias: 'listMediaCollections',
        description: `List all media collections for an entity.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'modelType',
                type: 'Path',
                schema: z.string(),
            },
            {
                name: 'modelId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(MediaCollection) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listMediaLinks: {
        method: 'get',
        path: '/media-links/:linkableType/:linkableId',
        alias: 'listMediaLinks',
        description: `List all media links for an entity.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'linkableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'linkableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(MediaLink) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listMedia: {
        method: 'get',
        path: '/media/:attachableType/:attachableId',
        alias: 'listMedia',
        description: `List all media for an entity.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attachableType',
                type: 'Path',
                schema: z.string(),
            },
            {
                name: 'attachableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'filter[collection_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[mime_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[document_type_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ] as const,
        response: z.object({ data: z.array(Media) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `The user is not allowed to login.`,
                schema: z.object({
                    message: z.string(),
                    code: z.union([AccountStatus, z.enum(['branch:disabled', 'branch:active', 'branch:hidden'])]),
                }),
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    addMedia: {
        method: 'post',
        path: '/media/:attachableType/:attachableId',
        alias: 'addMedia',
        description: `Add media.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addMedia_Body,
            },
            {
                name: 'attachableType',
                type: 'Path',
                schema: z.string(),
            },
            {
                name: 'attachableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(Media) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getMediaDownloadAllUrl: {
        method: 'get',
        path: '/media/:attachableType/:attachableId/download-all',
        alias: 'getMediaDownloadAllUrl',
        description: `Get URL that lets you download all media for an entity.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attachableType',
                type: 'Path',
                schema: z.string(),
            },
            {
                name: 'attachableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.object({ download_url: z.string().url() }).partial() }),
    },
    addMediaTus: {
        method: 'post',
        path: '/media/:attachableType/:attachableId/tus',
        alias: 'addMediaTus',
        description: `Add media.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addMediaTus_Body,
            },
            {
                name: 'attachableType',
                type: 'Path',
                schema: z.string(),
            },
            {
                name: 'attachableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(Media) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    showMedia: {
        method: 'get',
        path: '/media/:mediaId',
        alias: 'showMedia',
        description: `Get a single media by ID.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'mediaId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Media }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateMedia: {
        method: 'patch',
        path: '/media/:mediaId',
        alias: 'updateMedia',
        description: `Update media.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateMedia_Body,
            },
            {
                name: 'mediaId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Media }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteMedia: {
        method: 'delete',
        path: '/media/:mediaId',
        alias: 'deleteMedia',
        description: `Delete media.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'mediaId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(Media) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    linkMedia: {
        method: 'put',
        path: '/media/:mediaId/link/:linkableType/:linkableId',
        alias: 'linkMedia',
        description: `Link media to a linkable entity.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'mediaId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'linkableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'linkableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: MediaLink }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    unlinkMedia: {
        method: 'delete',
        path: '/media/:mediaId/unlink/:linkableType/:linkableId',
        alias: 'unlinkMedia',
        description: `Unlink media from a linkable entity.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'mediaId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'linkableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'linkableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: MediaLink }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getMockPaymentPlan: {
        method: 'post',
        path: '/mock-payment-plan',
        alias: 'getMockPaymentPlan',
        description: `Get mock payment plan.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: MockPaymentPlanConfig,
            },
        ] as const,
        response: z.object({ data: z.object({ items: z.array(MockPaymentItem), config: MockPaymentPlanConfig }) }),
    },
    getNotifications: {
        method: 'get',
        path: '/notifications',
        alias: 'getNotifications',
        description: `Get notifications.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('meta')).optional(),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
        ] as const,
        response: PaginatedResponse.and(
            z.object({ total_unread: z.number().int().optional(), data: z.array(Notification) })
        ),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getNotification: {
        method: 'get',
        path: '/notifications/:notificationId',
        alias: 'getNotification',
        description: `Get notification.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('meta')).optional(),
            },
        ] as const,
        response: z.object({ data: Notification }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    removeNotification: {
        method: 'delete',
        path: '/notifications/:notificationId',
        alias: 'removeNotification',
        description: `Remove notification.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('meta')).optional(),
            },
        ] as const,
        response: z.object({ total_unread: z.number().int() }).partial(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    markNotificationAsRead: {
        method: 'post',
        path: '/notifications/:notificationId/read',
        alias: 'markNotificationAsRead',
        description: `Mark notification as read.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('meta')).optional(),
            },
        ] as const,
        response: z.object({ data: z.array(Notification), total_unread: z.number().int().optional() }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    clearNotifications: {
        method: 'delete',
        path: '/notifications/clear',
        alias: 'clearNotifications',
        description: `Clear notifications.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('meta')).optional(),
            },
        ] as const,
        response: z.object({ data: z.array(Notification), total_unread: z.number().int().optional() }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getOnboardingProgress: {
        method: 'get',
        path: '/onboarding/:type/:id/progress',
        alias: 'getOnboardingProgress',
        description: `Get the progress of an onboarding.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'type',
                type: 'Path',
                schema: z.enum(['company', 'user']),
            },
            {
                name: 'id',
                type: 'Path',
                schema: id,
            },
        ] as const,
        response: z.object({ data: OnboardingProgress }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    approveUserOnboarding: {
        method: 'post',
        path: '/onboarding/approve/:userId',
        alias: 'approveUserOnboarding',
        description: `Approve a user&#x27;s onboarding.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'userId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: User }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    makeOnboardingProgress: {
        method: 'post',
        path: '/onboarding/progress',
        alias: 'makeOnboardingProgress',
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                description: `The message to send.`,
                type: 'Body',
                schema: makeOnboardingProgress_Body,
            },
        ] as const,
        response: z.object({ data: OnboardingProgress }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPackages: {
        method: 'get',
        path: '/packages',
        alias: 'getPackages',
        description: `Get packages.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'campuses',
                            'campuses.address',
                            'campuses.media',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'packaged_programs',
                            'packaged_programs.program',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'package.logo_url',
                            'package.partnered',
                            'package.company_name',
                            'package.assessment_level',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[id]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Package) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addPackage: {
        method: 'post',
        path: '/packages',
        alias: 'addPackage',
        description: `Add package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addPackage_Body,
            },
        ] as const,
        response: z.object({ data: Package }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPackage: {
        method: 'get',
        path: '/packages/:packageId',
        alias: 'getPackage',
        description: `Get package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'campuses',
                            'campuses.address',
                            'campuses.media',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'packaged_programs',
                            'packaged_programs.program',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'package.logo_url',
                            'package.partnered',
                            'package.company_name',
                            'package.assessment_level',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[id]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
        ] as const,
        response: z.object({ data: Package }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updatePackage: {
        method: 'patch',
        path: '/packages/:packageId',
        alias: 'updatePackage',
        description: `Update package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updatePackage_Body,
            },
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Package }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deletePackage: {
        method: 'delete',
        path: '/packages/:packageId',
        alias: 'deletePackage',
        description: `Delete package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    orderPackagePrograms: {
        method: 'post',
        path: '/packages/:packageId/order',
        alias: 'orderPackagePrograms',
        description: `Order package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: orderPackagePrograms_Body,
            },
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Package }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPackagePrograms: {
        method: 'get',
        path: '/packages/:packageId/programs',
        alias: 'getPackagePrograms',
        description: `Get the programs of a package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'media_collections',
                            'campuses',
                            'campuses.address',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'campuses.media',
                            'packaged_programs',
                            'packaged_programs.package',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'program.company_name',
                            'program.logo_url',
                            'program.partnered',
                            'program.assessment_level',
                            'program.applications_count',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[partnered]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[principal_subdivision_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
        ] as const,
        response: z.object({ data: z.array(PackagedProgram) }),
    },
    addPackageProgram: {
        method: 'post',
        path: '/packages/:packageId/programs',
        alias: 'addPackageProgram',
        description: `Add a program to a package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addPackageProgram_Body,
            },
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: PackagedProgram }),
    },
    removePackageProgram: {
        method: 'delete',
        path: '/packages/:packageId/programs/:programId',
        alias: 'removePackageProgram',
        description: `Remove a program from a package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'programId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
    },
    updatePackageProgram: {
        method: 'patch',
        path: '/packages/:packageId/programs/:programId',
        alias: 'updatePackageProgram',
        description: `Update a program in a package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updatePackageProgram_Body,
            },
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'programId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: PackagedProgram }),
    },
    syncPackagePrograms: {
        method: 'post',
        path: '/packages/:packageId/sync',
        alias: 'syncPackagePrograms',
        description: `Sync package.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: syncPackagePrograms_Body,
            },
            {
                name: 'packageId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Package }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPaymentAttempt: {
        method: 'get',
        path: '/payment-attempts/:attemptId',
        alias: 'getPaymentAttempt',
        description: `Get payment attempt.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attemptId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: PaymentAttempt }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deletePaymentAttempt: {
        method: 'delete',
        path: '/payment-attempts/:attemptId',
        alias: 'deletePaymentAttempt',
        description: `Cancels a pending payment attempt. For an application plan only the student may cancel. For a service plan (a service payment step or a general invoice) the student who owns the service and agency staff holding &#x60;LOG_MANUAL_PAYMENT&#x60; over the service&#x27;s agency branch may cancel. Cancelling a step&#x27;s pending attempt leaves the step unchanged and payable again.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attemptId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: PaymentAttempt }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    distributePaymentAttempt: {
        method: 'post',
        path: '/payment-attempts/:attemptId/distribute',
        alias: 'distributePaymentAttempt',
        description: `Distribute payment attempt.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attemptId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: PaymentAttempt }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    verifyPaymentAttempt: {
        method: 'post',
        path: '/payment-attempts/:attemptId/verify-payment',
        alias: 'verifyPaymentAttempt',
        description: `Asks Stripe for the attempt&#x27;s current state and fulfils it if the payment has succeeded. For an application plan only the student may call this. For a service plan (a service payment step or a general invoice) the student who owns the service and agency staff holding &#x60;LOG_MANUAL_PAYMENT&#x60; over the service&#x27;s agency branch may call it. When the attempt belongs to a payment step, fulfilment also writes the step&#x27;s payment state and completes the step. A payment still clearing is returned unchanged; the step completes when Stripe confirms, by webhook or by the next verify.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'attemptId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: PaymentAttempt }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPaymentAttempts: {
        method: 'get',
        path: '/payment-items/:itemId/payment-attempts',
        alias: 'getPaymentAttempts',
        description: `Get payment attempts.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'itemId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(PaymentAttempt) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addPaymentAttempt: {
        method: 'post',
        path: '/payment-items/:itemId/payment-attempts',
        alias: 'addPaymentAttempt',
        description: `Add payment attempt.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addPaymentAttemptToPlan_Body,
            },
            {
                name: 'itemId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: PaymentAttempt }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPaymentMethods: {
        method: 'get',
        path: '/payment-methods',
        alias: 'getPaymentMethods',
        description: `Get payment methods.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(PaymentMethodReference) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deletePaymentMethod: {
        method: 'delete',
        path: '/payment-methods/:methodId',
        alias: 'deletePaymentMethod',
        description: `Delete payment method.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'methodId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updatePaymentMethod: {
        method: 'patch',
        path: '/payment-methods/:methodId',
        alias: 'updatePaymentMethod',
        description: `Update payment method.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updatePaymentMethod_Body,
            },
            {
                name: 'methodId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: PaymentMethodReference }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPaymentPlans: {
        method: 'get',
        path: '/payment-plans',
        alias: 'getPaymentPlans',
        description: `Get payment plans.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(z.enum(['payment_items', 'payment_attempts', 'payment_items.payment_attempts']))
                    .optional(),
            },
            {
                name: 'filter[application_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[start_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[end_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[active_between]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agency_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
        ] as const,
        response: z.object({ data: z.array(PaymentPlan) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPaymentPlan: {
        method: 'get',
        path: '/payment-plans/:paymentPlanId',
        alias: 'getPaymentPlan',
        description: `Get payment plan.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'paymentPlanId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(z.enum(['payment_items', 'payment_attempts', 'payment_items.payment_attempts']))
                    .optional(),
            },
            {
                name: 'filter[application_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[start_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[end_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[active_between]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agency_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
        ] as const,
        response: z.object({ data: PaymentPlan }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    cancelPaymentPlan: {
        method: 'delete',
        path: '/payment-plans/:paymentPlanId',
        alias: 'cancelPaymentPlan',
        description: `Cancel payment plan.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'paymentPlanId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: PaymentPlan }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addPaymentAttemptToPlan: {
        method: 'post',
        path: '/payment-plans/:paymentPlanId/attempt',
        alias: 'addPaymentAttemptToPlan',
        description: `Add payment attempt.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addPaymentAttemptToPlan_Body,
            },
            {
                name: 'paymentPlanId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: PaymentAttempt }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getSummarizedPaymentPlans: {
        method: 'get',
        path: '/payment-plans/summarized',
        alias: 'getSummarizedPaymentPlans',
        description: `Get summarized payment plans.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(z.enum(['payment_items', 'payment_attempts', 'payment_items.payment_attempts']))
                    .optional(),
            },
            {
                name: 'filter[application_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[start_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[end_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[end_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[active_between]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agency_company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(SummarizedPaymentPlan) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPaymentRecords: {
        method: 'get',
        path: '/payment-records',
        alias: 'getPaymentRecords',
        description: `Get payment records.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('attempt_records')).optional(),
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[expected_around_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[expected_around_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[expected_around_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[expected_around_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[expected_around_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[expected_around_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[fulfilled_at]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[application_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[payment_item_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[student_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[application_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[created_at]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[unpaid]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'created_at',
                            '-created_at',
                            'updated_at',
                            '-updated_at',
                            'expected_around',
                            '-expected_around',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(PaymentRecord) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPayoutBatches: {
        method: 'get',
        path: '/payout-batches',
        alias: 'getPayoutBatches',
        description: `Get payout batches.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['payouts', 'bank_detail'])).optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(CompanyPayoutBatch) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createPayoutBatch: {
        method: 'post',
        path: '/payout-batches',
        alias: 'createPayoutBatch',
        description: `Create payout batch.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(CompanyPayoutBatch) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getBatchPayouts: {
        method: 'get',
        path: '/payout-batches/:batchId/payouts',
        alias: 'getBatchPayouts',
        description: `Get batch payouts.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'batchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(CompanyPayout) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    printBatchPayouts: {
        method: 'get',
        path: '/payout-batches/:batchId/processing-file',
        alias: 'printBatchPayouts',
        description: `Print batch payouts.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'batchId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(CompanyPayout) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    predictPayouts: {
        method: 'get',
        path: '/payout-batches/predict',
        alias: 'predictPayouts',
        description: `Predict payouts.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'currency',
                type: 'Query',
                schema: z.string(),
            },
        ] as const,
        response: z.object({ data: z.array(PayoutPrediction), other_errors: z.array(z.string()).optional() }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    reconcilePayouts: {
        method: 'post',
        path: '/payout-batches/reconcile',
        alias: 'reconcilePayouts',
        description: `Reconcile payouts.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: z.object({ payments: z.instanceof(File) }),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    markPayoutFailed: {
        method: 'post',
        path: '/payouts/:companyPayout/mark-failed',
        alias: 'markPayoutFailed',
        description: `Mark payout failed.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyPayout',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: CompanyPayout }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    markPayoutSuccessful: {
        method: 'post',
        path: '/payouts/:companyPayout/mark-successful',
        alias: 'markPayoutSuccessful',
        description: `Mark payout successful.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'companyPayout',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: CompanyPayout }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPermissions: {
        method: 'get',
        path: '/permissions',
        alias: 'getPermissions',
        description: `Get permissions.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(Permission) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getMyPermissions: {
        method: 'get',
        path: '/permissions/my',
        alias: 'getMyPermissions',
        description: `Get my permissions.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(Permission) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPlatformReferrals: {
        method: 'get',
        path: '/platform-referrals',
        alias: 'getPlatformReferrals',
        description: `Get platform referrals.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(PlatformReferral) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    addPlatformReferral: {
        method: 'post',
        path: '/platform-referrals',
        alias: 'addPlatformReferral',
        description: `Add platform referral.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addPlatformReferral_Body,
            },
        ] as const,
        response: z.object({ data: PlatformReferral }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPlatformReferral: {
        method: 'get',
        path: '/platform-referrals/:platformReferralId',
        alias: 'getPlatformReferral',
        description: `Get platform referral.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'platformReferralId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
        ] as const,
        response: z.object({ data: PlatformReferral }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updatePlatformReferral: {
        method: 'patch',
        path: '/platform-referrals/:platformReferralId',
        alias: 'updatePlatformReferral',
        description: `Update platform referral.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updatePlatformReferral_Body,
            },
            {
                name: 'platformReferralId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: PlatformReferral }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deletePlatformReferral: {
        method: 'delete',
        path: '/platform-referrals/:platformReferralId',
        alias: 'deletePlatformReferral',
        description: `Delete platform referral.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'platformReferralId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listPostalCodes: {
        method: 'get',
        path: '/postal-codes',
        alias: 'listPostalCodes',
        requestFormat: 'json',
        parameters: [
            {
                name: 'subdivision_code',
                type: 'Query',
                schema: name__2,
            },
            {
                name: 'name',
                type: 'Query',
                schema: name__2,
            },
            {
                name: 'search',
                type: 'Query',
                schema: name__2,
            },
            {
                name: 'country_code',
                type: 'Query',
                schema: z.string().min(2).max(2).optional(),
            },
        ] as const,
        response: z.object({ data: z.array(PostalCode) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listPosts: {
        method: 'get',
        path: '/posts/:postableType/:postableId',
        alias: 'listPosts',
        description: `Get posts.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'postableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'postableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['media', 'event_registrants'])).optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(AdvancedPost) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `The user is not allowed to login.`,
                schema: z.object({
                    message: z.string(),
                    code: z.union([AccountStatus, z.enum(['branch:disabled', 'branch:active', 'branch:hidden'])]),
                }),
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    addPost: {
        method: 'post',
        path: '/posts/:postableType/:postableId',
        alias: 'addPost',
        description: `Add post.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: addPost_Body,
            },
            {
                name: 'postableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'postableId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: AdvancedPost }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getPost: {
        method: 'get',
        path: '/posts/:postableType/:postableId/:postId',
        alias: 'getPost',
        description: `Get post.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'postableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'postableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'postId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['media', 'event_registrants'])).optional(),
            },
        ] as const,
        response: z.object({ data: AdvancedPost }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deletePost: {
        method: 'delete',
        path: '/posts/:postableType/:postableId/:postId',
        alias: 'deletePost',
        description: `Delete post.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'postableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'postableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'postId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updatePost: {
        method: 'patch',
        path: '/posts/:postableType/:postableId/:postId',
        alias: 'updatePost',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updatePost_Body,
            },
            {
                name: 'postableType',
                type: 'Path',
                schema: z.enum([
                    'user',
                    'student',
                    'agency',
                    'campus',
                    'company',
                    'company_branch',
                    'attended_institution',
                    'language_test',
                    'program',
                    'document_requirement',
                    'application',
                    'agreement',
                    'media_storage',
                    'payment_attempt',
                    'payment_item',
                    'attempt_record',
                    'association_request',
                    'student_resume',
                    'settings',
                    'package',
                    'offer_letter',
                    'signature',
                    'task',
                    'comment',
                    'service_template',
                    'service',
                    'smart_event',
                    'family_member',
                    'advanced_posts',
                    'company_payout_batch',
                    'company_payout',
                    'service_step',
                    'packaged_program',
                    'payment_plan',
                    'applied_program',
                    'manual_payment_log',
                ]),
            },
            {
                name: 'postableId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'postId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: AdvancedPost }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    registerForPost: {
        method: 'post',
        path: '/posts/:postId/register',
        alias: 'registerForPost',
        description: `Register for post.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: registerForPost_Body,
            },
            {
                name: 'postId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: EventRegistrant }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    unregisterFromPost: {
        method: 'delete',
        path: '/posts/:postId/register',
        alias: 'unregisterFromPost',
        description: `Unregister from post.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'postId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listProgramLevels: {
        method: 'get',
        path: '/program-levels',
        alias: 'listProgramLevels',
        requestFormat: 'json',
        response: z.object({ data: z.array(ProgramLevel) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createProgramLevel: {
        method: 'post',
        path: '/program-levels',
        alias: 'createProgramLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The program level to create.`,
                type: 'Body',
                schema: createProgramLevel_Body,
            },
        ] as const,
        response: z.object({ data: ProgramLevel }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getProgramLevel: {
        method: 'get',
        path: '/program-levels/:programLevelId',
        alias: 'getProgramLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'programLevelId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ProgramLevel }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateProgramLevel: {
        method: 'patch',
        path: '/program-levels/:programLevelId',
        alias: 'updateProgramLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the program level.`,
                type: 'Body',
                schema: updateProgramLevel_Body,
            },
            {
                name: 'programLevelId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ProgramLevel }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteProgramLevel: {
        method: 'delete',
        path: '/program-levels/:programLevelId',
        alias: 'deleteProgramLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'programLevelId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getProgram: {
        method: 'get',
        path: '/programs/:programId',
        alias: 'getProgram',
        description: `Get program.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'programId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'media_collections',
                            'campuses',
                            'campuses.address',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'campuses.media',
                            'packaged_programs',
                            'packaged_programs.package',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'program.company_name',
                            'program.logo_url',
                            'program.partnered',
                            'program.assessment_level',
                            'program.applications_count',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[partnered]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[principal_subdivision_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
        ] as const,
        response: z.object({ data: Program }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateProgram: {
        method: 'patch',
        path: '/programs/:programId',
        alias: 'updateProgram',
        description: `Update program.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateProgram_Body,
            },
            {
                name: 'programId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Program }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteProgram: {
        method: 'delete',
        path: '/programs/:programId',
        alias: 'deleteProgram',
        description: `Delete program.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'programId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listQualificationLevels: {
        method: 'get',
        path: '/qualification-levels',
        alias: 'listQualificationLevels',
        requestFormat: 'json',
        response: z.object({ data: z.array(QualificationLevel) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createQualificationLevel: {
        method: 'post',
        path: '/qualification-levels',
        alias: 'createQualificationLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The qualification level to create.`,
                type: 'Body',
                schema: createQualificationLevel_Body,
            },
        ] as const,
        response: z.object({ data: QualificationLevel }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getQualificationLevel: {
        method: 'get',
        path: '/qualification-levels/:qualificationLevelId',
        alias: 'getQualificationLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'qualificationLevelId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: QualificationLevel }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateQualificationLevel: {
        method: 'patch',
        path: '/qualification-levels/:qualificationLevelId',
        alias: 'updateQualificationLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the qualification level.`,
                type: 'Body',
                schema: updateQualificationLevel_Body,
            },
            {
                name: 'qualificationLevelId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: QualificationLevel }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteQualificationLevel: {
        method: 'delete',
        path: '/qualification-levels/:qualificationLevelId',
        alias: 'deleteQualificationLevel',
        requestFormat: 'json',
        parameters: [
            {
                name: 'qualificationLevelId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getRoles: {
        method: 'get',
        path: '/roles',
        alias: 'getRoles',
        description: `Get roles.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(Role) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createRole: {
        method: 'post',
        path: '/roles',
        alias: 'createRole',
        description: `Create role.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createRole_Body,
            },
        ] as const,
        response: z.object({ data: Role }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteRole: {
        method: 'delete',
        path: '/roles/:roleId',
        alias: 'deleteRole',
        description: `Delete role.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'roleId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    syncPermissionsOfRole: {
        method: 'put',
        path: '/roles/:roleId/permissions',
        alias: 'syncPermissionsOfRole',
        description: `Sync permissions of role.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: syncPermissionsOfRole_Body,
            },
            {
                name: 'roleId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Role }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    attachPermissionToRole: {
        method: 'put',
        path: '/roles/:roleId/permissions/:permissionId',
        alias: 'attachPermissionToRole',
        description: `Attach permission to role.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'roleId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'permissionId',
                type: 'Path',
                schema: permissionId,
            },
        ] as const,
        response: z.object({ data: Role }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    detachPermissionFromRole: {
        method: 'delete',
        path: '/roles/:roleId/permissions/:permissionId',
        alias: 'detachPermissionFromRole',
        description: `Detach permission from role.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'roleId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'permissionId',
                type: 'Path',
                schema: permissionId,
            },
        ] as const,
        response: z.object({ data: Role }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getMyRoles: {
        method: 'get',
        path: '/roles/my',
        alias: 'getMyRoles',
        description: `Get my roles.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(Role) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    searchAgencies: {
        method: 'get',
        path: '/search/agencies',
        alias: 'searchAgencies',
        requestFormat: 'json',
        parameters: [
            {
                name: 'text',
                type: 'Query',
                schema: z.string(),
            },
            {
                name: 'partnered',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(PrettySearchItem) })),
        errors: [
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    searchCampuses: {
        method: 'get',
        path: '/search/campuses',
        alias: 'searchCampuses',
        description: `Search campuses.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(z.enum(['address', 'branches', 'company_branch', 'media', 'media_collections']))
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'public_page.display_name',
                            'public_page.logo_url',
                            'public_page.partners',
                            'public_page.partnered',
                            'campus.assessment_level',
                            'campus.company_name',
                            'campus.can.update',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'lat',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'lng',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'radius',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Campus) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    searchMinimal: {
        method: 'get',
        path: '/search/minimal',
        alias: 'searchMinimal',
        requestFormat: 'json',
        parameters: [
            {
                name: 'text',
                type: 'Query',
                schema: z.string(),
            },
            {
                name: 'type',
                type: 'Query',
                schema: z.enum([
                    'program',
                    'school',
                    'agency',
                    'campus',
                    'agency_branch',
                    'student',
                    'user',
                    'company',
                ]),
            },
            {
                name: 'parent_id',
                type: 'Query',
                schema: parent_id,
            },
            {
                name: 'partnered',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'parent_type',
                type: 'Query',
                schema: z
                    .enum([
                        'user',
                        'student',
                        'agency',
                        'campus',
                        'company',
                        'company_branch',
                        'attended_institution',
                        'language_test',
                        'program',
                        'document_requirement',
                        'application',
                        'agreement',
                        'media_storage',
                        'payment_attempt',
                        'payment_item',
                        'attempt_record',
                        'association_request',
                        'student_resume',
                        'settings',
                        'package',
                        'offer_letter',
                        'signature',
                        'task',
                        'comment',
                        'service_template',
                        'service',
                        'smart_event',
                        'family_member',
                        'advanced_posts',
                        'company_payout_batch',
                        'company_payout',
                        'service_step',
                        'packaged_program',
                        'payment_plan',
                        'applied_program',
                        'manual_payment_log',
                    ])
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(MinimalSearchItem) })),
        errors: [
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    searchPackages: {
        method: 'get',
        path: '/search/packages',
        alias: 'searchPackages',
        description: `Search packages.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'campuses',
                            'campuses.address',
                            'campuses.media',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'packaged_programs',
                            'packaged_programs.program',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'package.logo_url',
                            'package.partnered',
                            'package.company_name',
                            'package.assessment_level',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[id]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'filter[partnered]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'lat',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'lng',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'radius',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Package) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    searchPrograms: {
        method: 'get',
        path: '/search/programs',
        alias: 'searchPrograms',
        description: `Search programs.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'media_collections',
                            'campuses',
                            'campuses.address',
                            'distanced_campuses',
                            'distanced_campuses.address',
                            'campuses.media',
                            'packaged_programs',
                            'packaged_programs.package',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'program.company_name',
                            'program.logo_url',
                            'program.partnered',
                            'program.assessment_level',
                            'program.applications_count',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[school_name]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[enabled]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[has_campuses]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[partnered]',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[tuition_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[tuition_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[material_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[material_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[assessment_level_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[assessment_level_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[duration_weeks_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[duration_weeks_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[application_fees_gt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_eq]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_gte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lt]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_lte]',
                type: 'Query',
                schema: filter_assessment_level_gt_,
            },
            {
                name: 'filter[application_fees_between]',
                type: 'Query',
                schema: filter_assessment_level_between_,
            },
            {
                name: 'filter[campuses.id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[method_of_delivery]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[intakes]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[principal_subdivision_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'lat',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'lng',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'radius',
                type: 'Query',
                schema: lat,
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Program) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    searchSchools: {
        method: 'get',
        path: '/search/schools',
        alias: 'searchSchools',
        requestFormat: 'json',
        parameters: [
            {
                name: 'text',
                type: 'Query',
                schema: z.string(),
            },
            {
                name: 'partnered',
                type: 'Query',
                schema: z.boolean().optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(PrettySearchItem) })),
        errors: [
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listServiceSteps: {
        method: 'get',
        path: '/service-steps',
        alias: 'listServiceSteps',
        requestFormat: 'json',
        parameters: [
            {
                name: 'service_id',
                type: 'Query',
                schema: z.number().int().optional(),
            },
            {
                name: 'status',
                type: 'Query',
                schema: z.enum(['pending', 'in_progress', 'completed', 'cancelled']).optional(),
            },
        ] as const,
        response: z.object({ data: z.array(ServiceStep) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createServiceStep: {
        method: 'post',
        path: '/service-steps',
        alias: 'createServiceStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The service step to create.`,
                type: 'Body',
                schema: ServiceStepCreate,
            },
        ] as const,
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getServiceStep: {
        method: 'get',
        path: '/service-steps/:serviceStepId',
        alias: 'getServiceStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateServiceStep: {
        method: 'patch',
        path: '/service-steps/:serviceStepId',
        alias: 'updateServiceStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the service step.`,
                type: 'Body',
                schema: ServiceStepUpdate,
            },
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteServiceStep: {
        method: 'delete',
        path: '/service-steps/:serviceStepId',
        alias: 'deleteServiceStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    cancelServiceStep: {
        method: 'post',
        path: '/service-steps/:serviceStepId/cancel',
        alias: 'cancelServiceStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    completeServiceStep: {
        method: 'post',
        path: '/service-steps/:serviceStepId/complete',
        alias: 'completeServiceStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `Result data from step completion.`,
                type: 'Body',
                schema: ServiceStepComplete.optional(),
            },
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateServiceStepConfig: {
        method: 'patch',
        path: '/service-steps/:serviceStepId/config',
        alias: 'updateServiceStepConfig',
        description: `Requires &#x60;managePayments&#x60; on the step (404 when the caller cannot see the service, 403 otherwise); 400 when the step is not a payment step. Rewrites the config and recomputes the state&#x27;s &#x60;amount&#x60;, &#x60;final_amount&#x60; and &#x60;remaining_amount&#x60; (keeping any discount and paid amount), and records an activity event when the amount changed. When the amount changes, a pending card attempt on the step is cancelled first, Stripe intent included; if it turns out to have succeeded already the edit is refused with 409 &#x60;paid_by_card&#x60;.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateServiceStepConfig_Body,
            },
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 409,
                description: `The step has been paid by card, so the change would contradict money already taken: a manual payment, a price or discount edit, or a rollback is refused and nothing changes. Also returned when the step&#x27;s pending card attempt turned out to have succeeded at Stripe while being cancelled for the change. &#x60;code&#x60; is &#x60;paid_by_card&#x60;. Refunds are not supported in this version.`,
                schema: CommonError.and(z.object({ code: z.literal('paid_by_card') })),
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    logServiceStepManualPayment: {
        method: 'post',
        path: '/service-steps/:serviceStepId/log-manual-payment',
        alias: 'logServiceStepManualPayment',
        description: `Records a payment taken outside Stripe. Requires &#x60;managePayments&#x60; on the step: service managers, or accounts staff with &#x60;LOG_MANUAL_PAYMENT&#x60; over the service&#x27;s agency branch (404 when the caller cannot see the service, 403 otherwise). Writes &#x60;paid_amount&#x60;, &#x60;remaining_amount&#x60;, &#x60;status&#x60; (&#x60;partial&#x60; or &#x60;paid&#x60;), &#x60;payment_method&#x60;, &#x60;payment_reference&#x60;, &#x60;payment_collected_by&#x60; / &#x60;payment_collected_at&#x60; and appends &#x60;notes&#x60;, creates a ManualPaymentLog awaiting the accountant&#x27;s verification, and records a service event. It does not complete the step: that is still &#x60;POST /service-steps/{id}/complete&#x60;, which a payment step allows once its status is &#x60;paid&#x60;. A cash payment on a step with GST and no existing discount is given the GST amount as an automatic discount. A pending card attempt on the step is cancelled first, Stripe intent included, so the student cannot also pay the old balance by card; if that attempt turns out to have succeeded already the manual payment is refused with 409 &#x60;paid_by_card&#x60;.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: logServiceStepManualPayment_Body,
            },
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 409,
                description: `The step has been paid by card, so the change would contradict money already taken: a manual payment, a price or discount edit, or a rollback is refused and nothing changes. Also returned when the step&#x27;s pending card attempt turned out to have succeeded at Stripe while being cancelled for the change. &#x60;code&#x60; is &#x60;paid_by_card&#x60;. Refunds are not supported in this version.`,
                schema: CommonError.and(z.object({ code: z.literal('paid_by_card') })),
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    startServiceStepPaymentAttempt: {
        method: 'post',
        path: '/service-steps/:serviceStepId/payment-attempt',
        alias: 'startServiceStepPaymentAttempt',
        description: `Starts a Stripe card payment for the full amount owed on a payment step, or returns the pending attempt that already exists for it. There is no request body: the step is the deduplication key, so a retried or double-clicked request can never create a second charge.
**Who.** The student who owns the service, or agency staff holding &#x60;LOG_MANUAL_PAYMENT&#x60; with authority over the service&#x27;s agency branch (&#x60;ServiceStepPolicy::handlesPayments&#x60;). A caller who cannot see the service gets 404, exactly as if the step did not exist (&#x60;ServiceStepPolicy::view&#x60; denies as not found); one who can see it but may not pay gets 403.
**Amount charged.** &#x60;state.remaining_amount&#x60;, falling back to &#x60;state.final_amount&#x60;, exactly as the step displays it. The caller cannot send an amount and part payment is not supported. &#x60;gst_inclusive: false&#x60; does not change this figure: &#x60;final_amount&#x60; and &#x60;remaining_amount&#x60; are derived from &#x60;payment_amount&#x60; less any discount with no GST added on top (&#x60;PaymentStep::getInitialState&#x60;, &#x60;CreateServiceFromTemplate&#x60;, &#x60;ServiceStepController::updateState&#x60; / &#x60;updateConfig&#x60;), the step invoice treats &#x60;final_amount&#x60; as GST-inclusive (&#x60;RaisePaymentStepInvoice&#x60; → &#x60;InvoiceParticular::amount()&#x60;), and both SPA views show the same number — so charging it is the only amount that equals the balance the student sees.
**Idempotency.** 201 when a new attempt (and, the first time, the step&#x27;s payment plan and its single item) is created; 200 when a pending attempt for the same amount already exists and is returned unchanged. A pending attempt for a different amount — the price or discount changed — is cancelled and replaced (201). The implementation serialises on the step so two simultaneous requests yield one attempt. Repeating the request is always safe.
**Not payable (409).** The step is locked behind an earlier step, is completed, skipped or cancelled, has &#x60;payment_type&#x60; set to something other than &#x60;stripe&#x60;, or has nothing left to pay. The body says which.
&#x60;client_secret&#x60; is present only when the current user may pay the attempt (&#x60;can.pay&#x60;), which is what the Payment Element needs. Verifying and cancelling use the existing payment-attempt endpoints.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: PaymentAttempt }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 409,
                description: `The step cannot take a card payment right now: locked, completed, skipped or cancelled, &#x60;payment_type&#x60; excludes &#x60;stripe&#x60;, or nothing is owed. &#x60;code&#x60; is one of &#x60;step_locked&#x60;, &#x60;step_closed&#x60;, &#x60;payment_type_excluded&#x60;, &#x60;nothing_owed&#x60;.`,
                schema: CommonError.and(
                    z.object({ code: z.enum(['step_locked', 'step_closed', 'payment_type_excluded', 'nothing_owed']) })
                ),
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getServiceStepPaymentAttempt: {
        method: 'get',
        path: '/service-steps/:serviceStepId/payment-attempt',
        alias: 'getServiceStepPaymentAttempt',
        description: `The step&#x27;s most recent card payment attempt — pending, processing or fulfilled — or &#x60;null&#x60; when the step has never started one. Lets the payment step show a payment that is already in flight (for example a bank debit still clearing) and resume it instead of starting again. Same visibility rules as POST: 404 when the caller cannot see the service, 403 when they can see it but may not handle its payments. Cancelled attempts are not returned.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.union([PaymentAttempt, z.null()]) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getQuotationPreview: {
        method: 'get',
        path: '/service-steps/:serviceStepId/quotation-preview',
        alias: 'getQuotationPreview',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ success: z.boolean(), data: z.array(QuotationDocumentPreview) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    recheckProfileGate: {
        method: 'post',
        path: '/service-steps/:serviceStepId/recheck-profile-gate',
        alias: 'recheckProfileGate',
        description: `Re-runs the resolver for the service&#x27;s quotation documents. When every bound field now resolves, the gate is marked complete and the state machine auto-advances.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ success: z.boolean(), data: z.object({}).partial() }),
        errors: [
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
        ],
    },
    reuseStepDocument: {
        method: 'post',
        path: '/service-steps/:serviceStepId/reuse-document',
        alias: 'reuseStepDocument',
        description: `Requires &#x60;fill&#x60; on the target step. Copies the uploaded file of the source step&#x27;s upload that carries the same document schema into the target upload slot and records the source step id on it. 400 when the target is not an upload step; 422 (standard validation shape, keyed by the offending field) when the slot, the schema or the source step do not match, or the source has no file.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: reuseStepDocument_Body,
            },
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ message: z.string(), step: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    rollbackServiceStep: {
        method: 'post',
        path: '/service-steps/:serviceStepId/rollback',
        alias: 'rollbackServiceStep',
        description: `Requires &#x60;manage&#x60; on the step (404 when the caller cannot see the service, 403 otherwise). Resets the step to &#x60;pending&#x60; with its initial state and clears its result; when the step&#x27;s config has &#x60;rollback_cascades&#x60;, every later step is reset the same way. Only a &#x60;completed&#x60; step can be rolled back (422 otherwise). A pending card attempt on any step being reset is cancelled first, Stripe intent included. A step paid by card cannot be rolled back while refunds are unsupported: 409 &#x60;paid_by_card&#x60;, nothing changes.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 409,
                description: `The step has been paid by card, so the change would contradict money already taken: a manual payment, a price or discount edit, or a rollback is refused and nothing changes. Also returned when the step&#x27;s pending card attempt turned out to have succeeded at Stripe while being cancelled for the change. &#x60;code&#x60; is &#x60;paid_by_card&#x60;. Refunds are not supported in this version.`,
                schema: CommonError.and(z.object({ code: z.literal('paid_by_card') })),
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    sendServiceStepInvoice: {
        method: 'post',
        path: '/service-steps/:serviceStepId/send-invoice',
        alias: 'sendServiceStepInvoice',
        description: `Re-sends the invoice email for a payment step&#x27;s existing invoice to the student. Re-send only: step invoices are raised by the automatic path (agreement/document-sign completion); manual invoicing uses the general invoice endpoint. Returns 422 when no invoice has been raised for the step. A 200 confirms the email was queued, not that it was delivered (the mailable no-ops when its email template is disabled).`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ message: z.string(), invoice_id: Id.int(), reference_id: z.string() }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    sendQuotation: {
        method: 'post',
        path: '/service-steps/:serviceStepId/send-quotation',
        alias: 'sendQuotation',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: sendQuotation_Body.optional(),
            },
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ success: z.boolean(), message: z.string() }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    startServiceStep: {
        method: 'post',
        path: '/service-steps/:serviceStepId/start',
        alias: 'startServiceStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateServiceStepState: {
        method: 'put',
        path: '/service-steps/:serviceStepId/state',
        alias: 'updateServiceStepState',
        description: `Requires &#x60;fill&#x60; on the step. On a payment step, when the update changes what the step owes (&#x60;remaining_amount&#x60;, else &#x60;final_amount&#x60;) from what its pending card attempt would charge — a discount, a price edit or marking it paid — that attempt is cancelled first, Stripe intent included; if it turns out to have succeeded already the update is refused with 409 &#x60;paid_by_card&#x60;.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `Updated state data for the step.`,
                type: 'Body',
                schema: ServiceStepStateUpdate,
            },
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 409,
                description: `The step has been paid by card, so the change would contradict money already taken: a manual payment, a price or discount edit, or a rollback is refused and nothing changes. Also returned when the step&#x27;s pending card attempt turned out to have succeeded at Stripe while being cancelled for the change. &#x60;code&#x60; is &#x60;paid_by_card&#x60;. Refunds are not supported in this version.`,
                schema: CommonError.and(z.object({ code: z.literal('paid_by_card') })),
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getServiceStepValidationStatus: {
        method: 'get',
        path: '/service-steps/:serviceStepId/validation',
        alias: 'getServiceStepValidationStatus',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceStepId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceStepValidationStatus }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getServiceTemplateStep: {
        method: 'get',
        path: '/service-template-steps/:id',
        alias: 'getServiceTemplateStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceTemplateStep }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateServiceTemplateStep: {
        method: 'patch',
        path: '/service-template-steps/:id',
        alias: 'updateServiceTemplateStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the service template step.`,
                type: 'Body',
                schema: updateServiceTemplateStep_Body,
            },
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceTemplateStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteServiceTemplateStep: {
        method: 'delete',
        path: '/service-template-steps/:id',
        alias: 'deleteServiceTemplateStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listServiceTemplates: {
        method: 'get',
        path: '/service-templates',
        alias: 'listServiceTemplates',
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('steps')).optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(ServiceTemplate) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createServiceTemplate: {
        method: 'post',
        path: '/service-templates',
        alias: 'createServiceTemplate',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The service template to create.`,
                type: 'Body',
                schema: createServiceTemplate_Body,
            },
        ] as const,
        response: z.object({ data: ServiceTemplate }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getServiceTemplate: {
        method: 'get',
        path: '/service-templates/:serviceTemplateId',
        alias: 'getServiceTemplate',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('steps')).optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[company_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(z.enum(['name', '-name', 'created_at', '-created_at', 'updated_at', '-updated_at']))
                    .optional(),
            },
        ] as const,
        response: z.object({ data: ServiceTemplate }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateServiceTemplate: {
        method: 'patch',
        path: '/service-templates/:serviceTemplateId',
        alias: 'updateServiceTemplate',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the service template.`,
                type: 'Body',
                schema: updateServiceTemplate_Body,
            },
            {
                name: 'serviceTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceTemplate }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    profilePreCheck: {
        method: 'get',
        path: '/service-templates/:serviceTemplateId/profile-precheck',
        alias: 'profilePreCheck',
        description: `Resolves the template&#x27;s quotation-step documents against the student and reports which profile-bound fields are still empty, so the wizard can surface gaps before creating the service.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'student_id',
                type: 'Query',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ success: z.boolean(), data: ProfilePreCheckResult }),
        errors: [
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
        ],
    },
    listServiceTemplateSteps: {
        method: 'get',
        path: '/service-templates/:serviceTemplateId/service-template-steps',
        alias: 'listServiceTemplateSteps',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(ServiceTemplateStep) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createServiceTemplateStep: {
        method: 'post',
        path: '/service-templates/:serviceTemplateId/service-template-steps',
        alias: 'createServiceTemplateStep',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The template step to create.`,
                type: 'Body',
                schema: createServiceTemplateStep_Body,
            },
            {
                name: 'serviceTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceTemplateStep }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    reorderServiceTemplateSteps: {
        method: 'post',
        path: '/service-templates/:serviceTemplateId/service-template-steps/reorder',
        alias: 'reorderServiceTemplateSteps',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The order of the service template steps.`,
                type: 'Body',
                schema: reorderServiceTemplateSteps_Body,
            },
            {
                name: 'serviceTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceTemplate }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listServices: {
        method: 'get',
        path: '/services',
        alias: 'listServices',
        requestFormat: 'json',
        response: z.object({ data: z.array(Service) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createService: {
        method: 'post',
        path: '/services',
        alias: 'createService',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The service to create.`,
                type: 'Body',
                schema: ServiceCreate,
            },
        ] as const,
        response: z.object({ data: Service }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createGeneralInvoice: {
        method: 'post',
        path: '/services/:service/invoices',
        alias: 'createGeneralInvoice',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createGeneralInvoice_Body,
            },
            {
                name: 'service',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: GeneralInvoiceCreatedResponse,
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
        ],
    },
    getService: {
        method: 'get',
        path: '/services/:serviceId',
        alias: 'getService',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Service }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateService: {
        method: 'patch',
        path: '/services/:serviceId',
        alias: 'updateService',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the service.`,
                type: 'Body',
                schema: ServiceUpdate,
            },
            {
                name: 'serviceId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Service }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    deleteService: {
        method: 'delete',
        path: '/services/:serviceId',
        alias: 'deleteService',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getServiceProgress: {
        method: 'get',
        path: '/services/:serviceId/progress',
        alias: 'getServiceProgress',
        requestFormat: 'json',
        parameters: [
            {
                name: 'serviceId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: ServiceProgress }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getServiceStats: {
        method: 'get',
        path: '/services/stats',
        alias: 'getServiceStats',
        requestFormat: 'json',
        response: z.object({ data: ServiceStats }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createServiceFromTemplate: {
        method: 'post',
        path: '/services/templates/:serviceTemplateId/create',
        alias: 'createServiceFromTemplate',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The service to create from template with step configurations.`,
                type: 'Body',
                schema: ServiceCreateFromTemplate,
            },
            {
                name: 'serviceTemplateId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Service }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getSettings: {
        method: 'get',
        path: '/settings',
        alias: 'getSettings',
        description: `Get settings.`,
        requestFormat: 'json',
        response: z.object({ data: Settings }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateSettings: {
        method: 'post',
        path: '/settings',
        alias: 'updateSettings',
        description: `Update Settings.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateSettings_Body,
            },
        ] as const,
        response: z.object({ data: Settings }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateSignature: {
        method: 'post',
        path: '/signatures/:signatureId',
        alias: 'updateSignature',
        description: `Update signature.`,
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateSignature_Body,
            },
            {
                name: 'signatureId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Signature }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    inviteUserToSignUp: {
        method: 'post',
        path: '/signup-referrals/:referralId/invite',
        alias: 'inviteUserToSignUp',
        description: `Invite a user to sign up.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: z.object({ email: z.string() }),
            },
            {
                name: 'referralId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ message: z.string(), success: z.boolean() }).partial(),
    },
    updateSignupReferralPlatform: {
        method: 'patch',
        path: '/signup-referrals/:referralId/platform',
        alias: 'updateSignupReferralPlatform',
        description: `Update the platform source tag (e.g. WhatsApp, Facebook) on a signup referral for tracking purposes.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateSignupReferralPlatform_Body,
            },
            {
                name: 'referralId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.object({ data: SignupReferral }).partial() }),
    },
    getSmartEvent: {
        method: 'get',
        path: '/smart-events/:smartEvent',
        alias: 'getSmartEvent',
        requestFormat: 'json',
        parameters: [
            {
                name: 'smartEvent',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: SmartEvent }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateNoteSmartEvent: {
        method: 'patch',
        path: '/smart-events/:smartEvent/notes',
        alias: 'updateNoteSmartEvent',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createNoteSmartEvent_Body,
            },
            {
                name: 'smartEvent',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: SmartEvent }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listSmartEvents: {
        method: 'get',
        path: '/smart-events/services/:service',
        alias: 'listSmartEvents',
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media_links.media',
                            'task',
                            'user',
                            'task.media',
                            'task.handlers',
                            'task.user',
                            'task.student',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[eventable_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[eventable_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[task_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[user_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[title]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[description]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[created_at]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[updated_at]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'created_at',
                            '-created_at',
                            'updated_at',
                            '-updated_at',
                            'title',
                            '-title',
                            'type',
                            '-type',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'service',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(SmartEvent) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createNoteSmartEvent: {
        method: 'post',
        path: '/smart-events/services/:service/notes',
        alias: 'createNoteSmartEvent',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createNoteSmartEvent_Body,
            },
            {
                name: 'service',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: SmartEvent }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createUploadSmartEvent: {
        method: 'post',
        path: '/smart-events/services/:service/uploads',
        alias: 'createUploadSmartEvent',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createUploadSmartEvent_Body,
            },
            {
                name: 'service',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: SmartEvent }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getStudents: {
        method: 'get',
        path: '/students',
        alias: 'getStudents',
        description: `Get students.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'addresses',
                            'role',
                            'media',
                            'media_storage',
                            'media_storage.media',
                            'personal_details',
                            'agency_branch',
                            'agency_branch.agency',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'student.avatar_url',
                            'student.applications_count',
                            'student.handlers',
                            'student.full_name',
                            'student.referral_platform',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[passport_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[finished_onboarding]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[not_agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[managed_by]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Student) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getStudent: {
        method: 'get',
        path: '/students/:studentId',
        alias: 'getStudent',
        description: `Get student.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'studentId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'addresses',
                            'role',
                            'media',
                            'media_storage',
                            'media_storage.media',
                            'personal_details',
                            'agency_branch',
                            'agency_branch.agency',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'student.avatar_url',
                            'student.applications_count',
                            'student.handlers',
                            'student.full_name',
                            'student.referral_platform',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[passport_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[finished_onboarding]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[not_agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[managed_by]',
                type: 'Query',
                schema: filter_search_,
            },
        ] as const,
        response: z.object({ data: Student }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listStudentFamilyMembers: {
        method: 'get',
        path: '/students/:studentId/family-members',
        alias: 'listStudentFamilyMembers',
        description: `Family members are returned oldest-first, each with its personal details loaded.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'studentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(FamilyMember) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createStudentFamilyMember: {
        method: 'post',
        path: '/students/:studentId/family-members',
        alias: 'createStudentFamilyMember',
        description: `Creates the family member and its personal-details record in one call. The personal details are parented to the student&#x27;s own personal details.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: FamilyMemberCreateRequest,
            },
            {
                name: 'studentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: FamilyMember }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getStudentResume: {
        method: 'get',
        path: '/students/:studentId/resume',
        alias: 'getStudentResume',
        description: `Get resume.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'studentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: StudentResume }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    setStudentResume: {
        method: 'put',
        path: '/students/:studentId/resume',
        alias: 'setStudentResume',
        description: `Set student resume details.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: setStudentResume_Body,
            },
            {
                name: 'studentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: StudentResume }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    linkAgency: {
        method: 'post',
        path: '/students/link-agency',
        alias: 'linkAgency',
        description: `Link student with new agency.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: linkAgency_Body,
            },
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'addresses',
                            'role',
                            'media',
                            'media_storage',
                            'media_storage.media',
                            'personal_details',
                            'agency_branch',
                            'agency_branch.agency',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'student.avatar_url',
                            'student.applications_count',
                            'student.handlers',
                            'student.full_name',
                            'student.referral_platform',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[passport_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[finished_onboarding]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[agency_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[not_agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[managed_by]',
                type: 'Query',
                schema: filter_search_,
            },
        ] as const,
        response: z.object({ data: Student }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createSubAgent: {
        method: 'post',
        path: '/sub-agents',
        alias: 'createSubAgent',
        description: `Create a sub-agent under the calling agency user. Agency users with manage_users only; a sub-agent cannot create another sub-agent (403).`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createSubAgent_Body,
            },
        ] as const,
        response: z.object({ data: User }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateSubAgent: {
        method: 'patch',
        path: '/sub-agents/:subAgentId',
        alias: 'updateSubAgent',
        description: `Update a sub-agent of the calling user&#x27;s agency. A sub-agent cannot update another sub-agent (403). 404 when the user is not a sub-agent of this agency.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateSubAgent_Body,
            },
            {
                name: 'subAgentId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: User }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listTasks: {
        method: 'get',
        path: '/tasks',
        alias: 'listTasks',
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['media', 'handlers', 'user', 'student'])).optional(),
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[taskable_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[taskable_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[user_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[handler_ids]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[start_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[deadline_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[created_at_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[updated_at_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'created_at',
                            '-created_at',
                            'updated_at',
                            '-updated_at',
                            'start_date',
                            '-start_date',
                            'deadline',
                            '-deadline',
                            'title',
                            '-title',
                            'status',
                            '-status',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Task) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createTask: {
        method: 'post',
        path: '/tasks',
        alias: 'createTask',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createTask_Body,
            },
        ] as const,
        response: z.object({ data: Task }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getTask: {
        method: 'get',
        path: '/tasks/:id',
        alias: 'getTask',
        requestFormat: 'json',
        parameters: [
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['media', 'handlers', 'user', 'student'])).optional(),
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[taskable_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[taskable_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[user_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[handler_ids]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[start_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[deadline_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[created_at_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[updated_at_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'created_at',
                            '-created_at',
                            'updated_at',
                            '-updated_at',
                            'start_date',
                            '-start_date',
                            'deadline',
                            '-deadline',
                            'title',
                            '-title',
                            'status',
                            '-status',
                        ])
                    )
                    .optional(),
            },
        ] as const,
        response: z.object({ data: Task }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateTask: {
        method: 'patch',
        path: '/tasks/:id',
        alias: 'updateTask',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateTask_Body,
            },
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Task }),
        errors: [
            {
                status: 404,
                description: `Task not found`,
                schema: z.void(),
            },
            {
                status: 422,
                description: `Validation error`,
                schema: z.void(),
            },
        ],
    },
    rescheduleTask: {
        method: 'patch',
        path: '/tasks/:id/reschedule',
        alias: 'rescheduleTask',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: rescheduleTask_Body,
            },
            {
                name: 'id',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Task }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listComments: {
        method: 'get',
        path: '/tasks/:task/comments',
        alias: 'listComments',
        requestFormat: 'json',
        parameters: [
            {
                name: 'task',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['user', 'media'])).optional(),
            },
            {
                name: 'filter[commentable_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[commentable_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z.array(z.enum(['created_at', '-created_at', 'updated_at', '-updated_at'])).optional(),
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(Comment) })),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    createComment: {
        method: 'post',
        path: '/tasks/:task/comments',
        alias: 'createComment',
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: createComment_Body,
            },
            {
                name: 'task',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: Comment }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getTaskCounts: {
        method: 'get',
        path: '/tasks/counts',
        alias: 'getTaskCounts',
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.enum(['media', 'handlers', 'user', 'student'])).optional(),
            },
            {
                name: 'filter[company_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[status]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[taskable_type]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[taskable_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[student_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[user_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[handler_ids]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[start_date_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[start_date_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[deadline_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[deadline_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[created_at_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[created_at_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'filter[updated_at_gt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_eq]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_gte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_lt]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_lte]',
                type: 'Query',
                schema: coercions.date().optional().optional(),
            },
            {
                name: 'filter[updated_at_between]',
                type: 'Query',
                schema: z.array(coercions.date().optional()).optional(),
            },
            {
                name: 'sort',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'created_at',
                            '-created_at',
                            'updated_at',
                            '-updated_at',
                            'start_date',
                            '-start_date',
                            'deadline',
                            '-deadline',
                            'title',
                            '-title',
                            'status',
                            '-status',
                        ])
                    )
                    .optional(),
            },
        ] as const,
        response: z.object({
            data: z
                .object({
                    total: z.number().int(),
                    todo: z.number().int(),
                    doing: z.number().int(),
                    done: z.number().int(),
                })
                .partial(),
        }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getUserContext: {
        method: 'get',
        path: '/user-context',
        alias: 'getUserContext',
        description: `Get user context.`,
        requestFormat: 'json',
        response: z.object({ data: UserContext }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listUsers: {
        method: 'get',
        path: '/users',
        alias: 'listUsers',
        description: `List users.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'include',
                type: 'Query',
                schema: z
                    .array(
                        z.enum([
                            'media',
                            'addresses',
                            'applications_count',
                            'role',
                            'media_storage',
                            'media_storage.media',
                            'personal_details',
                        ])
                    )
                    .optional(),
            },
            {
                name: 'append',
                type: 'Query',
                schema: z.array(z.literal('user.applications_count')).optional(),
            },
            {
                name: 'filter[search]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[finished_onboarding]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'filter[passport_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agency_branch_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[not_agent_id]',
                type: 'Query',
                schema: filter_company_branch_id_,
            },
            {
                name: 'filter[managed_by]',
                type: 'Query',
                schema: filter_search_,
            },
            {
                name: 'page[size]',
                type: 'Query',
                schema: z.number().int().gte(1).lte(500).optional().default(25),
            },
            {
                name: 'page[number]',
                type: 'Query',
                schema: z.number().int().gte(1).optional().default(1),
            },
        ] as const,
        response: PaginatedResponse.and(z.object({ data: z.array(User) })),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    updateUser: {
        method: 'patch',
        path: '/users/:userId',
        alias: 'updateUser',
        description: `Update information of the given user.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                description: `The fields to update in the user.`,
                type: 'Body',
                schema: updateUser_Body,
            },
            {
                name: 'userId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: User }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    attachRoleToUser: {
        method: 'put',
        path: '/users/:userId/roles/:roleId',
        alias: 'attachRoleToUser',
        description: `Attach role to user.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'userId',
                type: 'Path',
                schema: z.number().int(),
            },
            {
                name: 'roleId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: User }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    suspendUser: {
        method: 'post',
        path: '/users/:userId/suspend',
        alias: 'suspendUser',
        description: `Suspend a user.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: z.object({ reason: z.string() }),
            },
            {
                name: 'userId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: User }),
    },
    expireUserTokens: {
        method: 'delete',
        path: '/users/:userId/tokens',
        alias: 'expireUserTokens',
        description: `This will log out the user from all devices. TODO -- It is automatically done if a user changes password or their account is suspended.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'userId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.void(),
    },
    getAppVersion: {
        method: 'get',
        path: '/version',
        alias: 'getAppVersion',
        description: `Public probe used by the shared CI/CD pipeline&#x27;s verify stage. Returns the APP_VERSION the deploy stamped into the environment so the pipeline can confirm the new release is live and roll back otherwise. No authentication and no data envelope.`,
        requestFormat: 'json',
        response: z.object({ status: z.literal('ok'), version: z.string(), commit_sha: z.string().nullish() }),
        errors: [
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    listVisaApplicationFamilyMembers: {
        method: 'get',
        path: '/visa-applications/:visaApplicationId/family-members',
        alias: 'listVisaApplicationFamilyMembers',
        requestFormat: 'json',
        parameters: [
            {
                name: 'visaApplicationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(FamilyMember) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    syncVisaApplicationFamilyMembers: {
        method: 'put',
        path: '/visa-applications/:visaApplicationId/family-members',
        alias: 'syncVisaApplicationFamilyMembers',
        description: `Replaces the linked set with the given IDs. Every ID must belong to the visa application&#x27;s own student. Returns the resulting set.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: FamilyMemberSyncRequest,
            },
            {
                name: 'visaApplicationId',
                type: 'Path',
                schema: z.number().int(),
            },
        ] as const,
        response: z.object({ data: z.array(FamilyMember) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    stripeWebhook: {
        method: 'post',
        path: '/webhooks/stripe',
        alias: 'stripeWebhook',
        description: `Called by Stripe, not by clients. The raw body is verified against the &#x60;stripe-signature&#x60; header using the current webhook secret, then each configured legacy secret; a payload that matches none is rejected with 400. A verified event of a handled type is queued and processed asynchronously against the payment attempt it names: a succeeded payment is fulfilled, which for a service payment step also writes the step&#x27;s payment state and completes the step. Receiving the same event twice changes nothing the second time. Always answers 204 for a verified event — handled or not, and before processing — so Stripe does not retry.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: z.object({}).partial().passthrough(),
            },
            {
                name: 'stripe-signature',
                type: 'Header',
                schema: z.string(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
        ],
    },
    getWebView: {
        method: 'get',
        path: '/webviews/:webview_id',
        alias: 'getWebView',
        requestFormat: 'json',
        parameters: [
            {
                name: 'webview_id',
                type: 'Path',
                schema: z.string(),
            },
            {
                name: 'include',
                type: 'Query',
                schema: z.array(z.literal('requirements')).optional(),
            },
        ] as const,
        response: z.object({ data: WebView }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    updateWebView: {
        method: 'post',
        path: '/webviews/:webview_id',
        alias: 'updateWebView',
        requestFormat: 'form-data',
        parameters: [
            {
                name: 'body',
                type: 'Body',
                schema: updateWebView_Body,
            },
            {
                name: 'webview_id',
                type: 'Path',
                schema: z.string(),
            },
        ] as const,
        response: z.object({ data: WebView }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 422,
                description: `Unprocessable entity due to unacceptable request body (failed validation).`,
                schema: ValidationError,
            },
        ],
    },
    getWebViewImage: {
        method: 'get',
        path: '/webviews/:webview_id/images/:key',
        alias: 'getWebViewImage',
        requestFormat: 'json',
        parameters: [
            {
                name: 'webview_id',
                type: 'Path',
                schema: z.string(),
            },
            {
                name: 'key',
                type: 'Path',
                schema: z.string(),
            },
        ] as const,
        response: z.void(),
        errors: [
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    getWebViewRequirements: {
        method: 'get',
        path: '/webviews/:webview_id/requirements',
        alias: 'getWebViewRequirements',
        requestFormat: 'json',
        parameters: [
            {
                name: 'webview_id',
                type: 'Path',
                schema: z.string(),
            },
        ] as const,
        response: z.object({ data: WebViewRequirements }),
        errors: [
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
        ],
    },
    listDefinedWebViews: {
        method: 'get',
        path: '/webviews/defined',
        alias: 'listDefinedWebViews',
        description: `Gets the web views that are available in the application. This does not return the current data of any webview, only the available web views with basic information.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(DefinedWebView) }),
        errors: [
            {
                status: 400,
                description: `Request was in a bad format. This is different from validation errors, and it is the server telling you that it does not understand your request (or request parameters do not fit the expected format).`,
                schema: CommonError.and(z.object({ location: z.string() }).partial()),
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getApplicationsLastUpdated: {
        method: 'get',
        path: '/widgets/applications-last-updated',
        alias: 'getApplicationsLastUpdated',
        description: `Get applications last updated.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(Application) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getApplicationsRequiringAction: {
        method: 'get',
        path: '/widgets/applications-requiring-action',
        alias: 'getApplicationsRequiringAction',
        description: `Get applications requiring action.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(Application) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getCompaniesBeingOnboarded: {
        method: 'get',
        path: '/widgets/companies-being-onboarded',
        alias: 'getCompaniesBeingOnboarded',
        description: `Get companies being onboarded.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'company_type',
                type: 'Query',
                schema: z.enum(['student', 'staff', 'school', 'agency']).optional(),
            },
        ] as const,
        response: z.object({ data: z.array(Company) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getLatestMessages: {
        method: 'get',
        path: '/widgets/latest-messages',
        alias: 'getLatestMessages',
        description: `Get latest messages.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(ConversationMessage) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getNewStudents: {
        method: 'get',
        path: '/widgets/new-students',
        alias: 'getNewStudents',
        description: `Get new students.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(Student) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getStatCollection: {
        method: 'get',
        path: '/widgets/stat-collection',
        alias: 'getStatCollection',
        description: `Get stat collection.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(z.object({ name: z.string(), value: z.string() })) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getTopAgents: {
        method: 'get',
        path: '/widgets/top-agents',
        alias: 'getTopAgents',
        description: `Get top agents.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'limit',
                type: 'Query',
                schema: z.number().int().optional(),
            },
        ] as const,
        response: z.object({
            data: z.array(z.object({ applications_count: z.number().int(), company: MinimalCompany })),
        }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getTopPrograms: {
        method: 'get',
        path: '/widgets/top-programs',
        alias: 'getTopPrograms',
        description: `Get top programs.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'limit',
                type: 'Query',
                schema: z.number().int().optional(),
            },
        ] as const,
        response: z.object({ data: z.array(z.object({ applications_count: z.number().int(), program: Program })) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getTopSchools: {
        method: 'get',
        path: '/widgets/top-schools',
        alias: 'getTopSchools',
        description: `Get top schools.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'limit',
                type: 'Query',
                schema: z.number().int().optional(),
            },
        ] as const,
        response: z.object({
            data: z.array(z.object({ applications_count: z.number().int(), company: MinimalCompany })),
        }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
    getUpcomingPaymentItems: {
        method: 'get',
        path: '/widgets/upcoming-payment-items',
        alias: 'getUpcomingPaymentItems',
        description: `Get upcoming payment items.`,
        requestFormat: 'json',
        response: z.object({ data: z.array(PaymentItem) }),
        errors: [
            {
                status: 401,
                description: `Authenticated required.`,
                schema: CommonError,
            },
            {
                status: 403,
                description: `Forbidden from performing this action or accessing resource.`,
                schema: CommonError,
            },
            {
                status: 404,
                description: `Resource or route not found.`,
                schema: CommonError,
            },
            {
                status: 429,
                description: `Too many requests.`,
                schema: CommonError,
            },
            {
                status: 500,
                description: `Internal server error.`,
                schema: CommonError,
            },
        ],
    },
};

export const api = new Zodios(apiEndpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
    return new Zodios(baseUrl, apiEndpoints, options);
}
